import { useState, useCallback, useRef, useEffect, useMemo, useContext, createContext, lazy, Suspense } from "react";
import { trackEvent, saveSubmission, getSource } from './supabase';
import {
  User, FileText, Briefcase, GraduationCap, Zap, Globe, Award, Target,
  Download, Palette, ArrowLeft, Settings, Lock, CheckCircle2, ClipboardList,
  Pencil, LayoutList, PanelLeft, Circle, Building2, Sparkles, Trash2,
  Lightbulb, Check, FileDown, Heart, BadgeCheck, ArrowUpRight, Loader2, Wand2,
  Upload, X, ArrowDown, Mail, Info, RotateCcw,
} from "lucide-react";
import { extractPdfText, readFileText, parseResumeText } from "./resumeImport";

// Admin dashboard is lazy-loaded — it ships as its own chunk so regular
// visitors never download it with the main bundle.
const AdminDashboard = lazy(() => import('./AdminDashboard'));

// ─── TRANSLATIONS ─────────────────────────────────────────────────────────────
const TRANSLATIONS = {
  en: {
    badge: "Free to Build & Download — No Login, No Sign-Up Required",
    headline: "Build Your Resume in Minutes",
    heroSub: "No signup. No account. Just a polished, professional resume — ready to download as PDF, free.",
    cta: "Build My Resume — Free",
    ctaSub: "No account needed  ·  Free PDF download  ·  Takes 5 minutes",
    trustBar: ["Free to download", "No signup required", "Instant PDF export", "Your data stays private"],
    aiUpsell: "Free to build & export — add optional AI polish for $5 when you want recruiters to notice.",
    vp1: "Free to Build & Download", vp1d: "Build and download your resume free — no account, no credit card required. Optional AI polish is there for a one-time $5 whenever you want it.",
    vp2: "No Account Needed", vp2d: "Start building immediately. No signup, no email verification, no passwords to remember.",
    vp3: "Instant PDF Download", vp3d: "Your resume is ready to download as a clean, professional PDF in seconds.",
    vp4: "6 Professional Templates", vp4d: "Classic, Modern, Minimal, Corporate, Creative, and Sidebar layouts — all polished and ATS-friendly.",
    vp5: "Works Everywhere", vp5d: "Built for the web. Works on your phone, tablet, or desktop — no app install required.",
    vp6: "Private & Secure", vp6d: "Your data never leaves your browser. Nothing is sent to a server. It's your resume, your data.",
    howTitle: "Three steps. That's it.",
    step1: "Fill in your details", step1d: "Add your experience, skills, education — guided mode walks you through it.",
    step2: "Pick a template & color", step2d: "Choose from 6 professional layouts and 8 color themes.",
    step3: "Download your PDF", step3d: "One click. Your resume is ready to send to employers.",
    finalCta: "Your next job starts with a great resume",
    finalCtaSub: "Join thousands who've built their resume with Resume88 — takes under 5 minutes.",
    sub: "Create, customize, and download your resume as PDF in minutes — free, no account needed.",
    feat1: "No account required", feat2: "Instant PDF download", feat3: "6 beautiful templates",
    guided: "Guided Step-by-Step", guidedSub: "— beginner friendly",
    manual: "Jump to Editor",
    saved: "Saved progress detected — your data is pre-loaded",
    contact: "Contact & Feedback",
    contactSub: "Have a suggestion or found a bug? I'd love to hear from you.",
    namePh: "Your name", emailPh: "Your email", msgPh: "Your message...",
    send: "Send Message", sending: "Sending...", sent: "Message sent! Thank you 🎉",
    sendErr: "Failed to send. Please try again.",
    builtBy: "Built & open-sourced by Guidjedj Saleh",
  },
  fr: {
    badge: "Gratuit à créer et télécharger — sans connexion, sans inscription",
    headline: "Créez votre CV en quelques minutes",
    heroSub: "Sans inscription. Sans carte bancaire. Un CV professionnel prêt à télécharger en PDF, totalement gratuit.",
    cta: "Créer mon CV — Gratuit",
    ctaSub: "Sans compte  ·  PDF gratuit  ·  5 minutes",
    trustBar: ["Téléchargement gratuit", "Sans inscription", "Export PDF instantané", "Données privées"],
    aiUpsell: "Gratuit à créer et exporter — ajoutez une amélioration IA optionnelle pour 5 $ quand vous voulez.",
    vp1: "Gratuit à créer et télécharger", vp1d: "Créez et téléchargez votre CV gratuitement — sans compte, sans carte bancaire. Une amélioration par IA est optionnelle, pour 5 $ une seule fois quand vous le souhaitez.",
    vp2: "Sans compte requis", vp2d: "Commencez immédiatement. Pas d'inscription, pas de vérification.",
    vp3: "Téléchargement PDF instantané", vp3d: "Votre CV est prêt en quelques secondes en PDF professionnel.",
    vp4: "6 modèles professionnels", vp4d: "Classic, Modern, Minimal, Corporate, Creative et Sidebar.",
    vp5: "Fonctionne partout", vp5d: "Sur téléphone, tablette ou ordinateur — aucune installation requise.",
    vp6: "Privé et sécurisé", vp6d: "Vos données restent dans votre navigateur. Rien n'est envoyé à un serveur.",
    howTitle: "Trois étapes. C'est tout.",
    step1: "Remplissez vos informations", step1d: "Ajoutez expérience, compétences, formation.",
    step2: "Choisissez un modèle", step2d: "6 mises en page et 8 thèmes de couleurs.",
    step3: "Téléchargez votre PDF", step3d: "Un clic. Votre CV est prêt.",
    finalCta: "Votre prochain emploi commence par un bon CV",
    finalCtaSub: "Rejoignez des milliers de personnes — en moins de 5 minutes.",
    sub: "Créez, personnalisez et téléchargez votre CV en PDF en quelques minutes. Totalement gratuit, pour toujours.",
    feat1: "Sans compte requis", feat2: "Téléchargement PDF instantané", feat3: "6 modèles élégants",
    guided: "Guide Étape par Étape", guidedSub: "— idéal pour débutants",
    manual: "Aller à l'éditeur",
    saved: "Progression sauvegardée — vos données sont pré-chargées",
    contact: "Contact & Commentaires",
    contactSub: "Une suggestion ou un bug ? Je serais ravi de vous lire.",
    namePh: "Votre nom", emailPh: "Votre email", msgPh: "Votre message...",
    send: "Envoyer", sending: "Envoi...", sent: "Message envoyé ! Merci 🎉",
    sendErr: "Échec de l'envoi. Réessayez.",
    builtBy: "Développé et open-source par Guidjedj Saleh",
  },
  es: {
    badge: "Gratis para crear y descargar — sin inicio de sesión, sin registro",
    headline: "Crea tu currículum en minutos",
    heroSub: "Sin registro. Sin tarjeta. Un currículum profesional listo para descargar en PDF, completamente gratis.",
    cta: "Crear mi currículum — Gratis",
    ctaSub: "Sin cuenta  ·  PDF gratis  ·  5 minutos",
    trustBar: ["Descarga gratis", "Sin registro", "PDF instantáneo", "Datos privados"],
    aiUpsell: "Gratis para crear y exportar — añade una mejora con IA opcional por 5 $ cuando quieras.",
    vp1: "Gratis para crear y descargar", vp1d: "Crea y descarga tu currículum gratis — sin cuenta, sin tarjeta. La mejora con IA es opcional, por 5 $ una sola vez cuando quieras.",
    vp2: "Sin cuenta necesaria", vp2d: "Empieza ahora. Sin registro, sin verificación.",
    vp3: "Descarga PDF instantánea", vp3d: "Tu currículum listo en segundos como PDF profesional.",
    vp4: "6 plantillas profesionales", vp4d: "Classic, Modern, Minimal, Corporate, Creative y Sidebar.",
    vp5: "Funciona en todas partes", vp5d: "En móvil, tablet u ordenador — sin instalar nada.",
    vp6: "Privado y seguro", vp6d: "Tus datos no salen de tu navegador. Nada se envía a un servidor.",
    howTitle: "Tres pasos. Eso es todo.",
    step1: "Completa tus datos", step1d: "Añade experiencia, habilidades, educación.",
    step2: "Elige una plantilla", step2d: "6 diseños y 8 temas de color.",
    step3: "Descarga tu PDF", step3d: "Un clic. Tu currículum está listo.",
    finalCta: "Tu próximo empleo empieza con un buen currículum",
    finalCtaSub: "Únete a miles de personas — en menos de 5 minutos.",
    sub: "Crea, personaliza y descarga tu currículum en PDF en minutos. Completamente gratis, para siempre.",
    feat1: "Sin cuenta requerida", feat2: "Descarga PDF instantánea", feat3: "6 plantillas hermosas",
    guided: "Guía Paso a Paso", guidedSub: "— amigable para principiantes",
    manual: "Ir al editor",
    saved: "Progreso guardado — tus datos están precargados",
    contact: "Contacto y Sugerencias",
    contactSub: "¿Tienes una sugerencia o encontraste un error? Me encantaría saberlo.",
    namePh: "Tu nombre", emailPh: "Tu correo", msgPh: "Tu mensaje...",
    send: "Enviar Mensaje", sending: "Enviando...", sent: "¡Mensaje enviado! Gracias 🎉",
    sendErr: "Error al enviar. Inténtalo de nuevo.",
    builtBy: "Desarrollado y de código abierto por Guidjedj Saleh",
  },
  ar: {
    badge: "مجاني للإنشاء والتنزيل — بدون تسجيل دخول، بدون اشتراك",
    headline: "أنشئ سيرتك الذاتية في دقائق",
    heroSub: "بدون تسجيل. بدون بطاقة بنكية. سيرة ذاتية احترافية جاهزة للتحميل كـ PDF، مجاناً تماماً.",
    cta: "أنشئ سيرتي الذاتية — مجاناً",
    ctaSub: "بدون حساب  ·  تحميل PDF مجاني  ·  5 دقائق",
    trustBar: ["تنزيل مجاني", "بدون تسجيل", "تصدير PDF فوري", "بياناتك خاصة"],
    aiUpsell: "مجاني للإنشاء والتصدير — أضف لمسة ذكاء اصطناعي اختيارية مقابل 5 $ متى شئت.",
    vp1: "مجاني للإنشاء والتنزيل", vp1d: "أنشئ سيرتك الذاتية ونزّلها مجاناً — بدون حساب، بدون بطاقة بنكية. تحسين اختياري بالذكاء الاصطناعي متاح مقابل 5 $ لمرة واحدة عندما تريد.",
    vp2: "بدون حساب", vp2d: "ابدأ فوراً. بدون تسجيل أو تحقق.",
    vp3: "تحميل PDF فوري", vp3d: "سيرتك الذاتية جاهزة للتحميل في ثوانٍ.",
    vp4: "6 قوالب احترافية", vp4d: "كلاسيكي، حديث، بسيط، مؤسسي، إبداعي، وشريط جانبي.",
    vp5: "يعمل في كل مكان", vp5d: "على الهاتف أو الجهاز اللوحي أو الكمبيوتر.",
    vp6: "خاص وآمن", vp6d: "بياناتك لا تغادر متصفحك. لا شيء يُرسل إلى خادم.",
    howTitle: "ثلاث خطوات فقط.",
    step1: "أملأ بياناتك", step1d: "أضف الخبرة والمهارات والتعليم.",
    step2: "اختر قالباً", step2d: "6 تصاميم و8 ألوان.",
    step3: "حمّل ملف PDF", step3d: "نقرة واحدة. سيرتك جاهزة.",
    finalCta: "وظيفتك القادمة تبدأ بسيرة ذاتية رائعة",
    finalCtaSub: "انضم إلى الآلاف — في أقل من 5 دقائق.",
    sub: "أنشئ سيرتك الذاتية وخصّصها وحمّلها بصيغة PDF في دقائق. مجاني تماماً وللأبد.",
    feat1: "لا حاجة لحساب", feat2: "تحميل PDF فوري", feat3: "6 قوالب جميلة",
    guided: "دليل خطوة بخطوة", guidedSub: "— مناسب للمبتدئين",
    manual: "الانتقال إلى المحرر",
    saved: "تم اكتشاف تقدم محفوظ — بياناتك محمّلة مسبقاً",
    contact: "تواصل وملاحظات",
    contactSub: "هل لديك اقتراح أو وجدت خطأ؟ يسعدني سماعك.",
    namePh: "اسمك", emailPh: "بريدك الإلكتروني", msgPh: "رسالتك...",
    send: "إرسال الرسالة", sending: "جاري الإرسال...", sent: "تم إرسال الرسالة! شكراً 🎉",
    sendErr: "فشل الإرسال. حاول مجدداً.",
    builtBy: "بُني ونشر كمصدر مفتوح بواسطة Guidjedj Saleh",
  },
  de: {
    badge: "Kostenlos erstellen & herunterladen — kein Login, keine Registrierung",
    headline: "Erstelle deinen Lebenslauf in Minuten",
    heroSub: "Keine Registrierung. Keine Kreditkarte. Ein professioneller Lebenslauf als PDF — völlig kostenlos.",
    cta: "Lebenslauf erstellen — Kostenlos",
    ctaSub: "Kein Konto  ·  Kostenloses PDF  ·  5 Minuten",
    trustBar: ["Kostenloser Download", "Ohne Registrierung", "Sofortiger PDF-Export", "Daten bleiben privat"],
    aiUpsell: "Kostenlos erstellen & exportieren — optionaler KI-Feinschliff für 5 $, wann du willst.",
    vp1: "Kostenlos erstellen & herunterladen", vp1d: "Erstelle und lade deinen Lebenslauf kostenlos herunter — ohne Konto, ohne Kreditkarte. Optionaler KI-Feinschliff für einmalig 5 $, wann immer du willst.",
    vp2: "Kein Konto erforderlich", vp2d: "Sofort loslegen. Keine Anmeldung, keine Verifizierung.",
    vp3: "Sofortiger PDF-Download", vp3d: "Dein Lebenslauf ist in Sekunden als professionelles PDF bereit.",
    vp4: "6 professionelle Vorlagen", vp4d: "Classic, Modern, Minimal, Corporate, Creative und Sidebar.",
    vp5: "Funktioniert überall", vp5d: "Auf Handy, Tablet oder Desktop — keine Installation nötig.",
    vp6: "Privat und sicher", vp6d: "Deine Daten verlassen nie deinen Browser.",
    howTitle: "Drei Schritte. Das war's.",
    step1: "Daten ausfüllen", step1d: "Erfahrung, Fähigkeiten, Ausbildung hinzufügen.",
    step2: "Vorlage wählen", step2d: "6 Layouts und 8 Farbthemen.",
    step3: "PDF herunterladen", step3d: "Ein Klick. Dein Lebenslauf ist fertig.",
    finalCta: "Dein nächster Job beginnt mit einem guten Lebenslauf",
    finalCtaSub: "Schließe dich Tausenden an — in unter 5 Minuten.",
    sub: "Erstelle, passe an und lade deinen Lebenslauf als PDF in Minuten herunter. Völlig kostenlos, für immer.",
    feat1: "Kein Konto erforderlich", feat2: "Sofortiger PDF-Download", feat3: "6 schöne Vorlagen",
    guided: "Schritt-für-Schritt-Anleitung", guidedSub: "— anfängerfreundlich",
    manual: "Zum Editor",
    saved: "Gespeicherter Fortschritt erkannt — Ihre Daten sind vorgeladen",
    contact: "Kontakt & Feedback",
    contactSub: "Haben Sie einen Vorschlag oder einen Fehler gefunden?",
    namePh: "Ihr Name", emailPh: "Ihre E-Mail", msgPh: "Ihre Nachricht...",
    send: "Nachricht senden", sending: "Wird gesendet...", sent: "Nachricht gesendet! Danke 🎉",
    sendErr: "Senden fehlgeschlagen. Bitte versuchen Sie es erneut.",
    builtBy: "Erstellt und Open-Source von Guidjedj Saleh",
  },
  zh: {
    badge: "免费制作和下载 — 无需登录、无需注册",
    headline: "几分钟内创建你的简历",
    heroSub: "无需注册。无需信用卡。专业简历即时下载为PDF，完全免费。",
    cta: "免费创建简历",
    ctaSub: "无需账户  ·  免费PDF  ·  5分钟",
    trustBar: ["免费下载", "无需注册", "即时PDF导出", "数据保持私密"],
    aiUpsell: "免费制作和导出 — 需要让招聘官眼前一亮时，可加 5 美元获得可选 AI 润色。",
    vp1: "免费制作和下载", vp1d: "免费制作并下载你的简历 — 无需账户，无需信用卡。需要时可一次性支付 5 美元获得可选的 AI 润色。",
    vp2: "无需账户", vp2d: "立即开始。无需注册，无需验证。",
    vp3: "即时PDF下载", vp3d: "您的简历在几秒钟内即可作为专业PDF下载。",
    vp4: "6个专业模板", vp4d: "经典、现代、极简、商务、创意和侧边栏布局。",
    vp5: "随处使用", vp5d: "手机、平板或电脑 — 无需安装。",
    vp6: "私密安全", vp6d: "数据不离开浏览器。不发送到服务器。",
    howTitle: "三步完成。就这么简单。",
    step1: "填写信息", step1d: "添加经验、技能、教育。",
    step2: "选择模板", step2d: "6种布局和8种配色。",
    step3: "下载PDF", step3d: "一键完成。简历已就绪。",
    finalCta: "你的下一份工作从一份好简历开始",
    finalCtaSub: "加入数千人 — 不到5分钟。",
    sub: "在几分钟内创建、自定义并下载PDF简历。完全免费，永久有效。",
    feat1: "无需账户", feat2: "即时PDF下载", feat3: "6个精美模板",
    guided: "逐步引导", guidedSub: "— 适合初学者",
    manual: "进入编辑器",
    saved: "检测到已保存的进度 — 您的数据已预加载",
    contact: "联系与反馈",
    contactSub: "有建议或发现错误？我很乐意听取您的意见。",
    namePh: "您的姓名", emailPh: "您的邮箱", msgPh: "您的留言...",
    send: "发送消息", sending: "发送中...", sent: "消息已发送！谢谢 🎉",
    sendErr: "发送失败，请重试。",
    builtBy: "由 Guidjedj Saleh 构建并开源",
  },
  pt: {
    badge: "Grátis para criar e baixar — sem login, sem cadastro",
    headline: "Crie seu currículo em minutos",
    heroSub: "Sem cadastro. Sem cartão. Um currículo profissional pronto para download em PDF, completamente grátis.",
    cta: "Criar meu currículo — Grátis",
    ctaSub: "Sem conta  ·  PDF grátis  ·  5 minutos",
    trustBar: ["Download grátis", "Sem cadastro", "PDF instantâneo", "Dados privados"],
    aiUpsell: "Grátis para criar e exportar — adicione um polimento com IA opcional por 5 $ quando quiser.",
    vp1: "Grátis para criar e baixar", vp1d: "Crie e baixe seu currículo de graça — sem conta, sem cartão. Um polimento com IA é opcional, por 5 $ uma única vez quando quiser.",
    vp2: "Sem conta necessária", vp2d: "Comece agora. Sem cadastro, sem verificação.",
    vp3: "Download PDF instantâneo", vp3d: "Seu currículo pronto em segundos como PDF profissional.",
    vp4: "6 modelos profissionais", vp4d: "Classic, Modern, Minimal, Corporate, Creative e Sidebar.",
    vp5: "Funciona em qualquer lugar", vp5d: "No celular, tablet ou computador — sem instalação.",
    vp6: "Privado e seguro", vp6d: "Seus dados não saem do navegador. Nada é enviado a um servidor.",
    howTitle: "Três passos. Só isso.",
    step1: "Preencha seus dados", step1d: "Adicione experiência, habilidades, educação.",
    step2: "Escolha um modelo", step2d: "6 layouts e 8 temas de cores.",
    step3: "Baixe seu PDF", step3d: "Um clique. Seu currículo está pronto.",
    finalCta: "Seu próximo emprego começa com um bom currículo",
    finalCtaSub: "Junte-se a milhares — em menos de 5 minutos.",
    sub: "Crie, personalize e baixe seu currículo em PDF em minutos. Completamente grátis, para sempre.",
    feat1: "Sem conta necessária", feat2: "Download PDF instantâneo", feat3: "6 modelos bonitos",
    guided: "Guia Passo a Passo", guidedSub: "— amigável para iniciantes",
    manual: "Ir ao editor",
    saved: "Progresso salvo detectado — seus dados estão pré-carregados",
    contact: "Contato e Feedback",
    contactSub: "Tem uma sugestão ou encontrou um bug? Adoraria ouvir de você.",
    namePh: "Seu nome", emailPh: "Seu email", msgPh: "Sua mensagem...",
    send: "Enviar Mensagem", sending: "Enviando...", sent: "Mensagem enviada! Obrigado 🎉",
    sendErr: "Falha ao enviar. Tente novamente.",
    builtBy: "Criado e open-source por Guidjedj Saleh",
  },
};

function getLang() {
  const l = (navigator.language || "en").slice(0,2).toLowerCase();
  return TRANSLATIONS[l] ? l : "en";
}

// ─── CONSTANTS ────────────────────────────────────────────────────────────────
export const EMPTY_RESUME = {
  personal: { name:"", title:"", email:"", phone:"", location:"", linkedin:"", github:"", website:"", summary:"", photo:"" },
  experience:[], education:[], skills:[], languages:[], hobbies:[],
  projects:[], certifications:[],
};

// Curated, recruiter-friendly palettes — muted jewel tones + earthy shades, the
// schemes professional resume guides recommend (navy, charcoal, forest, burgundy…)
// plus a refined gold. Each accent is dark enough for white text (used as the
// sidebar/gradient), each muted is light enough for dark text (skill pills).
export const PALETTE_SETS = {
  navy:     { accent:"#1f3a63", dark:"#122845", light:"#eef3fa", muted:"#d6e2f3" },
  charcoal: { accent:"#353b47", dark:"#1f242e", light:"#f2f4f7", muted:"#dde1e8" },
  teal:     { accent:"#0e6f73", dark:"#0a4d50", light:"#ecfafa", muted:"#c5ebe8" },
  emerald:  { accent:"#0b7a52", dark:"#064d36", light:"#ecfaf2", muted:"#c2ebd5" },
  forest:   { accent:"#245038", dark:"#163422", light:"#eef5f0", muted:"#cfe5d6" },
  indigo:   { accent:"#4039b5", dark:"#2a2785", light:"#eeeefc", muted:"#d7d7f6" },
  plum:     { accent:"#5e2a66", dark:"#3e1a45", light:"#f6eff7", muted:"#e6d3ea" },
  burgundy: { accent:"#7c2438", dark:"#511522", light:"#fbeef1", muted:"#f1d2d9" },
  copper:   { accent:"#9a4a23", dark:"#683014", light:"#fcf1ea", muted:"#f4d8c6" },
  gold:     { accent:"#8a6608", dark:"#5c4405", light:"#fdf8e6", muted:"#f1e3a9" },
};

const TEMPLATES = [
  { id:"classic",    label:"Classic",     desc:"Bold header, clean single-column",       icon:LayoutList },
  { id:"sidebar",    label:"Sidebar",     desc:"Colored sidebar + photo support",         icon:PanelLeft  },
  { id:"modern",     label:"Modern",      desc:"Gradient header, sharp & contemporary",   icon:Zap },
  { id:"minimal",    label:"Minimal",     desc:"Centered serif header, ultra-clean",      icon:Circle  },
  { id:"corporate",  label:"Corporate",   desc:"Top accent bar, structured layout",       icon:Building2 },
  { id:"creative",   label:"Creative",    desc:"Bold name, accent block + photo support", icon:Sparkles },
];

const LANG_LEVELS = ["Basic","Conversational","Professional","Fluent","Native"];
// Common languages offered as a pick-list (users can still type any language).
const LANGUAGES = ["English","Spanish","French","German","Mandarin Chinese","Cantonese","Hindi","Arabic","Portuguese","Russian","Japanese","Korean","Italian","Dutch","Polish","Turkish","Vietnamese","Thai","Indonesian","Malay","Filipino (Tagalog)","Bengali","Urdu","Persian (Farsi)","Hebrew","Greek","Swedish","Norwegian","Danish","Finnish","Czech","Hungarian","Romanian","Ukrainian","Swahili","Tamil","Telugu","Punjabi","Gujarati","Marathi","Amharic","Somali","Yoruba","Hausa","Zulu","Afrikaans","Croatian","Serbian","Bulgarian","Slovak"];
const SKILL_LEVELS = ["Beginner","Intermediate","Advanced","Expert"];

const TIPS = {
  personal:   "Use a professional email. LinkedIn/GitHub URLs help recruiters verify your work.",
  summary:    "Keep it 2–4 sentences. Mention your years of experience, main skills, and what you bring to the role.",
  experience: "Use bullet points starting with action verbs: 'Led', 'Built', 'Increased', 'Managed'. Quantify results where possible.",
  education:  "List your most recent degree first. Include GPA only if it's 3.5 or above.",
  skills:     "Mix hard skills (Python, Figma) with soft skills (Team Leadership). Keep it to 8–15 skills.",
  languages:  "Only list languages you can actually use in a work setting.",
  extras:     "Projects and certifications can set you apart, especially if you're early in your career.",
  hobbies:    "Only include hobbies that add value or show personality relevant to the role.",
};

const uid = () => Math.random().toString(36).slice(2,8);
const load = () => { try { return JSON.parse(localStorage.getItem("rb4")||"null"); } catch { return null; } };
const persist = (d) => { try { localStorage.setItem("rb4",JSON.stringify(d)); } catch {} };

// ─── RESUME SCORE ─────────────────────────────────────────────────────────────

// ─── PDF EXPORT ───────────────────────────────────────────────────────────────
// Prints through a hidden, off-screen iframe so the user never leaves the page
// (no new tab) — the browser's save dialog opens right over the current popup,
// and the document title becomes the default "Save as PDF" filename.
function exportPDF(tmplId, pal, resume, branded=false) {
  const filename = (resume.personal.name || "resume").replace(/\s+/g,"-").toLowerCase();
  const html = getPrintHTML(tmplId, pal, resume, branded);

  // ⚠️ WebKit bug: iframe.contentWindow.print() prints the PARENT page, not the
  // iframe — so on iOS (every iOS browser is WebKit) and desktop Safari it would
  // export the on-screen app instead of the resume. Route those to a dedicated
  // print window that contains ONLY the resume. Chromium/Firefox keep the hidden
  // iframe (no extra tab).
  const ua = navigator.userAgent || "";
  const isIOS = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  const isSafari = /^((?!chrome|crios|android|fxios|edg).)*safari/i.test(ua);
  if (isIOS || isSafari) {
    const win = window.open("", "_blank");
    if (!win) { alert("Please allow pop-ups for resume88.com so we can open your PDF to save."); return; }
    win.document.open();
    win.document.write(html);
    win.document.close();
    try { win.document.title = filename; } catch {}
    let printed = false;
    const go = () => { if (printed) return; printed = true; try { win.focus(); win.print(); } catch {} };
    win.onafterprint = () => { try { win.close(); } catch {} };
    try {
      if (win.document.fonts && win.document.fonts.ready) win.document.fonts.ready.then(() => setTimeout(go, 150)).catch(() => setTimeout(go, 150));
    } catch {}
    setTimeout(go, 900); // fallback if fonts.ready never resolves
    return;
  }

  // Shared fallback: open the resume in its OWN window and print from there.
  // Returns false only if the browser blocked the popup (caller then warns).
  const popupMsg = "Please allow pop-ups for resume88.com so we can open your resume to print or save as PDF. You can also use “Email me a copy”.";
  const openPrintWindow = () => {
    let win = null;
    try { win = window.open("", "_blank"); } catch {}
    if (!win) return false;
    try {
      win.document.open(); win.document.write(html); win.document.close();
      try { win.document.title = filename; } catch {}
      let printed = false;
      const go = () => { if (printed) return; printed = true; try { win.focus(); win.print(); } catch {} };
      try {
        if (win.document.fonts && win.document.fonts.ready) win.document.fonts.ready.then(() => setTimeout(go, 150)).catch(() => setTimeout(go, 150));
        else setTimeout(go, 300);
      } catch { setTimeout(go, 300); }
      setTimeout(go, 1200); // fallback if fonts.ready never resolves (dedupe guard above)
    } catch { return false; }
    return true;
  };

  // Primary path: a hidden, off-screen iframe (no extra tab). Guard every step —
  // if the iframe can't be created or written to, fall straight to the window.
  let iframe = null;
  try {
    iframe = document.createElement("iframe");
    iframe.setAttribute("aria-hidden", "true");
    iframe.style.cssText = "position:fixed;left:-9999px;top:0;width:794px;height:0;border:0;opacity:0";
    document.body.appendChild(iframe);
  } catch { iframe = null; }
  if (!iframe || !iframe.contentWindow) {
    if (!openPrintWindow()) alert(popupMsg);
    return;
  }

  // Remove the iframe only after printing finishes (onafterprint), with a long
  // fallback — never on a short timer, which could yank it out from under an
  // open print dialog (blank/failed PDF, esp. where print() is non-blocking).
  let removed = false;
  const cleanup = () => { if (removed) return; removed = true; try { document.body.removeChild(iframe); } catch {} };

  let printed = false;
  const fire = () => {
    if (printed) return;
    printed = true;
    try {
      const w = iframe.contentWindow;
      if (!w) throw new Error("no_iframe_window");
      try { iframe.contentDocument.title = filename; } catch {}
      w.onafterprint = cleanup;
      w.focus();
      w.print();
    } catch (e) {
      // Iframe printing unavailable → dedicated window, then a popup warning.
      cleanup();
      if (!openPrintWindow()) alert(popupMsg);
    }
    setTimeout(cleanup, 60000); // safety net if onafterprint never fires
  };

  try {
    const doc = iframe.contentWindow.document;
    doc.open();
    doc.write(html);
    doc.close();
  } catch (e) {
    cleanup();
    if (!openPrintWindow()) alert(popupMsg);
    return;
  }

  // Print once the webfont (Inter) + content (incl. a base64 photo) have loaded,
  // so the PDF renders in Inter rather than a fallback. Hard timers guard against
  // a slow/blocked font so the download never hangs (printed flag dedupes).
  iframe.onload = () => {
    try {
      const d = iframe.contentWindow.document;
      if (d.fonts && d.fonts.ready) d.fonts.ready.then(() => fire()).catch(() => fire());
      else setTimeout(fire, 300);
    } catch { fire(); }
    setTimeout(fire, 2000);
  };
  setTimeout(fire, 2600);
}

// HTML-escape every user value so resume fields can't inject markup/scripts into
// the print iframe — and, critically, into the admin's "Review" iframe where one
// person's stored resume is rendered in the admin's authenticated session.
const escHtml = (s) => String(s == null ? "" : s).replace(/[&<>"']/g, c => (
  { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]
));
function escapeResume(r) {
  const escObj = (o) => Object.fromEntries(Object.entries(o || {}).map(([k, v]) => [k, typeof v === "string" ? escHtml(v) : v]));
  const escArr = (a) => (a || []).map(escObj);
  return {
    personal: escObj(r && r.personal),
    experience: escArr(r && r.experience),
    education: escArr(r && r.education),
    skills: escArr(r && r.skills),
    languages: escArr(r && r.languages),
    projects: escArr(r && r.projects),
    certifications: escArr(r && r.certifications),
    hobbies: ((r && r.hobbies) || []).map(escHtml),
  };
}

export function getPrintHTML(tmplId, pal, r0, branded=false) {
  const r = escapeResume(r0);
  const {accent:a, dark:dk, light:lt, muted:mu} = pal;
  const contact = [
    r.personal.email, r.personal.phone, r.personal.location,
    r.personal.linkedin, r.personal.github, r.personal.website
  ].filter(Boolean);
  const photoHtml = r.personal.photo
    ? `<img src="${r.personal.photo}" style="width:80px;height:80px;border-radius:50%;object-fit:cover;border:3px solid ${mu};box-shadow:0 1px 3px rgba(0,0,0,0.08)" />`
    : "";
  const expH = r.experience.map(e=>`<div style="margin-bottom:14px"><div style="display:flex;justify-content:space-between;flex-wrap:wrap"><strong style="font-size:12px;color:#1a1a1a">${e.title}${e.company?`<span style="font-weight:400;color:#6b7280"> — ${e.company}</span>`:""}</strong><span style="font-size:10px;color:#9ca3af;letter-spacing:0.3px">${e.start||""}${e.end?" – "+e.end:e.start?" – Present":""}</span></div>${e.location?`<div style="font-size:10px;color:#9ca3af;margin-top:1px">${e.location}</div>`:""}<ul style="margin:4px 0 0 12px;padding:0">${(e.bullets||"").split("\n").filter(Boolean).map(b=>`<li style="font-size:11px;color:#4b5563;line-height:1.6;margin-bottom:2px">${b.replace(/^[•-]\s*/,"")}</li>`).join("")}</ul></div>`).join("");
  const eduH = r.education.map(e=>`<div style="margin-bottom:10px;display:flex;justify-content:space-between;flex-wrap:wrap"><div><strong style="font-size:12px;color:#1a1a1a">${e.degree}${e.field?" in "+e.field:""}</strong><div style="font-size:11px;color:#6b7280;margin-top:1px">${e.school}</div></div><span style="font-size:10px;color:#9ca3af;letter-spacing:0.3px">${e.year||""}</span></div>`).join("");
  const skillH = `<div style="display:flex;flex-wrap:wrap;gap:4px">${r.skills.map(s=>`<span style="background:${mu};color:${dk};padding:2px 10px;border-radius:20px;font-size:10px;line-height:20px">${s.name}${s.level?`<span style="opacity:0.6"> · ${s.level}</span>`:""}</span>`).join("")}</div>`;
  const langH = r.languages.length ? `<div style="display:flex;flex-wrap:wrap;gap:4px">${r.languages.map(l=>`<span style="background:${mu};color:${dk};padding:2px 10px;border-radius:20px;font-size:10px;line-height:20px">${l.name} · ${l.level}</span>`).join("")}</div>` : "";
  const hobbyH = r.hobbies.length ? r.hobbies.join("  ·  ") : "";
  const projH = r.projects.map(p=>`<div style="margin-bottom:10px"><strong style="font-size:12px;color:#1a1a1a">${p.name}</strong>${p.url?` <span style="font-size:10px;color:${a};margin-left:6px">${p.url}</span>`:""}<p style="margin:2px 0;font-size:11px;color:#6b7280;line-height:1.5">${p.description||""}</p></div>`).join("");
  const certH = r.certifications.map(c=>`<div style="margin-bottom:4px;font-size:11px;color:#374151"><strong>${c.name}</strong>${c.issuer?`<span style="font-weight:400"> — ${c.issuer}</span>`:""} ${c.year?`<span style="color:#9ca3af">(${c.year})</span>`:""}</div>`).join("");

  const sec = (title, body) => body ? `<div style="margin-bottom:17px"><div style="font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:1.3px;color:${a};border-bottom:1px solid ${mu};padding-bottom:5px;margin-bottom:10px;line-height:1">${title}</div>${body}</div>` : "";

  // Freemium: a small "Made with resume88.com" footer appears ONLY on the free
  // download (branded=true). Paid downloads and the on-screen preview are clean.
  const footer = branded ? `<div style="margin-top:24px;padding-top:8px;border-top:1px solid ${mu};text-align:center;font-size:9px;color:${a};letter-spacing:0.5px">Made with <a href="https://www.resume88.com" style="color:${a};text-decoration:none;font-weight:600">resume88.com</a></div>` : "";

  const base = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${(r.personal.name||"resume").replace(/\s+/g,"-").toLowerCase()}</title><link rel="preconnect" href="https://fonts.gstatic.com" crossorigin><link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap" rel="stylesheet"><style>*{box-sizing:border-box;margin:0;padding:0}body{font-family:'Inter','Segoe UI',system-ui,-apple-system,Helvetica,Arial,sans-serif;font-size:11.5px;line-height:1.55;color:#1f2937;background:#fff;-webkit-font-smoothing:antialiased;text-rendering:optimizeLegibility}.r88-frame{padding:8mm}.r88-sheet{border-radius:12px}@media print{@page{size:A4;margin:0}body{-webkit-print-color-adjust:exact;print-color-adjust:exact}}</style></head><body><div class="r88-frame"><div class="r88-sheet">`;
  const name = r.personal.name||"Your Name", title=r.personal.title||"";
  const mainSections = `
    ${r.personal.summary?sec("Summary",`<p style="font-size:11px;color:#4b5563;line-height:1.7">${r.personal.summary}</p>`):""}
    ${r.experience.length?sec("Experience",expH):""}
    ${r.education.length?sec("Education",eduH):""}
    ${r.skills.length?sec("Skills",skillH):""}
    ${r.languages.length?sec("Languages",langH):""}
    ${r.projects.length?sec("Projects",projH):""}
    ${r.certifications.length?sec("Certifications",certH):""}
    ${r.hobbies.length?sec("Interests",`<p style="font-size:11px;color:#6b7280;letter-spacing:0.3px">${hobbyH}</p>`):""}
  `;
  const sidebarLeft = `
    ${r.personal.photo?`<div style="margin-bottom:12px">${photoHtml}</div>`:""}
    <div style="font-size:16px;font-weight:700;margin-bottom:2px;line-height:1.25">${name}</div>
    <div style="font-size:11px;opacity:.7;margin-bottom:12px">${title}</div>
    <div style="font-size:10px;line-height:2;margin-bottom:12px;border-bottom:1px solid rgba(255,255,255,.15);padding-bottom:10px">${contact.map(c=>"<div>"+c+"</div>").join("")}</div>
    ${r.skills.length?`<div style="margin-bottom:12px"><div style="font-size:9px;text-transform:uppercase;letter-spacing:1.5px;opacity:.5;margin-bottom:6px">Skills</div><div style="display:flex;flex-wrap:wrap;gap:4px">${r.skills.map(s=>`<span style="background:rgba(255,255,255,.15);border-radius:20px;padding:2px 10px;font-size:10px;line-height:20px">${s.name}</span>`).join("")}</div></div>`:""}
    ${r.languages.length?`<div style="margin-bottom:12px"><div style="font-size:9px;text-transform:uppercase;letter-spacing:1.5px;opacity:.5;margin-bottom:6px">Languages</div>${r.languages.map(l=>`<div style="font-size:11px;opacity:.85;margin-bottom:3px">${l.name} · ${l.level}</div>`).join("")}</div>`:""}
    ${r.certifications.length?`<div><div style="font-size:9px;text-transform:uppercase;letter-spacing:1.5px;opacity:.5;margin-bottom:6px">Certifications</div>${r.certifications.map(c=>`<div style="font-size:11px;opacity:.85;margin-bottom:3px">${c.name}</div>`).join("")}</div>`:""}
  `;

  if (tmplId==="sidebar") return base+`<div style="display:flex"><div style="width:33%;background:${a};color:#fff;padding:24px 18px;border-radius:12px 0 0 12px">${sidebarLeft}</div><div style="flex:1;padding:22px 24px">${mainSections}${footer}</div></div></div></div></body></html>`;
  if (tmplId==="creative") return base+`<div style="position:relative;padding:26px 32px;background:${lt};border-radius:12px 12px 0 0"><div style="position:absolute;left:0;top:0;width:4px;height:100%;background:${a};border-radius:12px 0 0 0"></div><div style="display:flex;align-items:center;gap:16px">${photoHtml}<div><div style="font-size:24px;font-weight:900;letter-spacing:-0.5px;color:${dk};line-height:1.2">${name}</div>${title?`<span style="display:inline-block;margin-top:6px;padding:3px 12px;font-size:10px;font-weight:700;color:#fff;border-radius:20px;background:${a};letter-spacing:0.3px">${title}</span>`:""}${contact.length?`<div style="display:flex;flex-wrap:wrap;gap:8px;margin-top:8px;font-size:10px;color:#6b7280">${contact.map(c=>"<span>· "+c+"</span>").join("")}</div>`:""}</div></div></div><div style="display:flex"><div style="flex:1;padding:20px 24px">${r.personal.summary?sec("Profile",`<p style="font-size:11px;color:#4b5563;line-height:1.7">${r.personal.summary}</p>`):""}${r.experience.length?sec("Experience",expH):""}${r.education.length?sec("Education",eduH):""}${r.projects.length?sec("Projects",projH):""}${r.hobbies.length?sec("Interests",`<p style="font-size:11px;color:#6b7280">${hobbyH}</p>`):""}${footer}</div><div style="width:32%;padding:20px 16px;border-left:1.5px solid ${mu};background:${lt}">${r.skills.length?`<div style="margin-bottom:16px"><div style="font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:1.3px;margin-bottom:8px;color:${a}">Skills</div>${skillH}</div>`:""}${r.languages.length?`<div style="margin-bottom:16px"><div style="font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:1.3px;margin-bottom:8px;color:${a}">Languages</div>${langH}</div>`:""}${r.certifications.length?`<div><div style="font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:1.3px;margin-bottom:8px;color:${a}">Certifications</div>${certH}</div>`:""}</div></div></div></div></body></html>`;
  if (tmplId==="modern") return base+`<div style="background:linear-gradient(135deg,${dk},${a});color:#fff;padding:28px 32px;border-radius:12px 12px 0 0">${photoHtml?`<div style="display:flex;align-items:center;gap:16px"><div>${photoHtml}</div><div><div style="font-size:23px;font-weight:800;letter-spacing:-0.5px">${name}</div><div style="font-size:12px;opacity:.75;margin-top:4px">${title}</div><div style="display:flex;flex-wrap:wrap;gap:14px;margin-top:10px;font-size:10px;opacity:.6">${contact.map(c=>"<span>"+c+"</span>").join("")}</div></div></div>`:`<div style="font-size:23px;font-weight:800;letter-spacing:-0.5px">${name}</div><div style="font-size:12px;opacity:.75;margin-top:4px">${title}</div><div style="display:flex;flex-wrap:wrap;gap:14px;margin-top:10px;font-size:10px;opacity:.6">${contact.map(c=>"<span>"+c+"</span>").join("")}</div>`}</div><div style="padding:24px 32px">${mainSections}${footer}</div></div></div></body></html>`;
  if (tmplId==="corporate") return base+`<div style="height:5px;background:${a};border-radius:12px 12px 0 0"></div><div style="padding:18px 32px 14px;border-bottom:2px solid ${mu};display:flex;justify-content:space-between;align-items:center">${photoHtml?`<div style="display:flex;align-items:center;gap:14px">${photoHtml}<div><div style="font-size:20px;font-weight:700;color:${dk};line-height:1.2">${name}</div><div style="color:${a};font-size:12px;font-weight:600;margin-top:3px">${title}</div></div></div>`:`<div><div style="font-size:20px;font-weight:700;color:${dk};line-height:1.2">${name}</div><div style="color:${a};font-size:12px;font-weight:600;margin-top:3px">${title}</div></div>`}<div style="text-align:right;font-size:10px;color:#6b7280;line-height:1.9">${contact.map(c=>"<div>"+c+"</div>").join("")}</div></div><div style="padding:22px 32px">${mainSections}${footer}</div></div></div></body></html>`;
  if (tmplId==="minimal") return base+`<div style="padding:36px 44px;max-width:720px;font-family:Georgia,'Times New Roman',serif"><div style="text-align:center;margin-bottom:22px;padding-bottom:18px;border-bottom:2px solid ${a}">${photoHtml?`<div style="margin-bottom:10px;display:flex;justify-content:center">${photoHtml}</div>`:""}<div style="font-size:26px;font-weight:300;letter-spacing:3px;text-transform:uppercase;color:#1f2937;line-height:1.2">${name}</div>${title?`<div style="font-size:11px;letter-spacing:2.5px;text-transform:uppercase;margin-top:6px;color:${a}">${title}</div>`:""}<div style="font-size:10px;color:#9ca3af;margin-top:10px;display:flex;justify-content:center;gap:14px;flex-wrap:wrap">${contact.map(c=>"<span>"+c+"</span>").join("")}</div></div>${mainSections}${footer}</div></div></div></body></html>`;
  return base+`<div style="padding:28px 32px;max-width:760px"><div style="margin-bottom:18px;padding-bottom:14px;border-bottom:3px solid ${a};display:flex;justify-content:space-between;align-items:center">${photoHtml?`<div style="display:flex;align-items:center;gap:14px">${photoHtml}<div><div style="font-size:22px;font-weight:700;color:${dk};line-height:1.2;letter-spacing:-0.3px">${name}</div>${title?`<div style="font-size:12px;font-weight:600;color:${a};margin-top:3px;letter-spacing:0.2px">${title}</div>`:""}<div style="margin-top:6px;font-size:10px;color:#6b7280;display:flex;gap:12px;flex-wrap:wrap">${contact.map(c=>"<span>"+c+"</span>").join("")}</div></div></div>`:`<div><div style="font-size:22px;font-weight:700;color:${dk};line-height:1.2;letter-spacing:-0.3px">${name}</div>${title?`<div style="font-size:12px;font-weight:600;color:${a};margin-top:3px">${title}</div>`:""}</div><div style="text-align:right;font-size:10px;color:#6b7280;line-height:1.9">${contact.map(c=>"<div>"+c+"</div>").join("")}</div>`}</div>${mainSections}${footer}</div></div></div></body></html>`;
}

// ─── ROOT ─────────────────────────────────────────────────────────────────────
// ─── DONATIONS ────────────────────────────────────────────────────────────────
// Stripe Payment Links are public URLs (safe to ship in the client) and support
// Apple Pay / Google Pay / cards on Stripe's hosted page — no backend, no keys.
// Set REACT_APP_STRIPE_DONATE_URL to a "customers choose what to pay" link — it
// powers the "Other / any amount" option AND is the fallback for any preset.
// For the presets to charge a FIXED amount (no typing), create one fixed Stripe
// Payment Link per amount and set REACT_APP_STRIPE_DONATE_3/_5/_10. A preset with
// no dedicated link falls back to the base "choose what to pay" link.
const DONATE = {
  // Optional "customer chooses price" link. When set, a fifth "Other / any amount"
  // option appears. (Stripe payment links are public by design and REACT_APP_*
  // vars are inlined into the public bundle anyway, so these live client-side.)
  base: process.env.REACT_APP_STRIPE_DONATE_URL || "",
  links: {
    3:  process.env.REACT_APP_STRIPE_DONATE_3  || "https://buy.stripe.com/14A3cxdz4c936g9dWKgrS00",
    5:  process.env.REACT_APP_STRIPE_DONATE_5  || "https://buy.stripe.com/00w00l3Yub4ZgUN5qegrS03",
    10: process.env.REACT_APP_STRIPE_DONATE_10 || "https://buy.stripe.com/3cIbJ352y6OJ1ZT7ymgrS02",
    20: process.env.REACT_APP_STRIPE_DONATE_20 || "https://buy.stripe.com/6oU7sN0Mia0VgUN8CqgrS04",
  },
  amounts: [
    { v: 3,  label: "$3",  note: "Coffee" },
    { v: 5,  label: "$5",  note: "Keep it running" },
    { v: 10, label: "$10", note: "Fund a tool" },
    { v: 20, label: "$20", note: "Go big" },
    { v: "custom", label: "Other", note: "Any amount" }, // only shown when base is set
  ],
};
// "custom" → the choose-what-to-pay base link; presets → their own link, else base.
const donateUrlFor = (amt) => amt === "custom" ? DONATE.base : (DONATE.links[amt] || DONATE.base);
const donateEnabled = () => !!(DONATE.base || DONATE.links[3] || DONATE.links[5] || DONATE.links[10] || DONATE.links[20]);

// Curated deep links surfaced in the homepage footer. The homepage is our
// highest-authority, most-crawled page, so linking individual example/guide
// pages directly from it raises their crawl priority — the practical fix for
// pages stuck in Google's "Discovered – currently not indexed" state.
const FOOTER_EXAMPLES = [
  ["software-engineer", "Software Engineer"], ["project-manager", "Project Manager"],
  ["registered-nurse", "Registered Nurse"], ["accountant", "Accountant"],
  ["data-analyst", "Data Analyst"], ["teacher", "Teacher"],
  ["customer-service-representative", "Customer Service Rep"], ["administrative-assistant", "Admin Assistant"],
  ["sales-representative", "Sales Representative"], ["product-manager", "Product Manager"],
  ["web-developer", "Web Developer"], ["graphic-designer", "Graphic Designer"],
];
const FOOTER_GUIDES = [
  ["how-to-write-a-resume-2026.html", "How to Write a Resume (2026)"],
  ["ats-friendly-resume.html", "ATS-Friendly Resume Guide"],
  ["resume-format-guide.html", "Resume Format Guide"],
  ["professional-summary-examples.html", "Professional Summary Examples"],
  ["resume-with-no-experience.html", "Resume With No Experience"],
  ["resume-action-verbs.html", "Resume Action Verbs"],
  ["how-long-should-a-resume-be.html", "How Long Should a Resume Be?"],
  ["resume-mistakes-to-avoid.html", "Resume Mistakes to Avoid"],
];

// "What's next" — affiliate/partner links shown after a download (peak intent).
// Each URL is set via env so we never ship broken or undisclosed links: a card
// only renders when its env var is present, and the panel hides entirely if none
// are set. A {title} token in a URL is replaced with the person's job title
// (URL-encoded) so e.g. a job-search link lands on relevant openings.
const NEXT_STEPS = [
  { key: "jobs",   icon: Briefcase,      title: "Find jobs for your role",  desc: "See openings that match your title", url: process.env.REACT_APP_AFFIL_JOBS   || "/jobs?q={title}" },
  { key: "review", icon: BadgeCheck,     title: "Get an expert review",     desc: "A pro checks it before you apply",   url: process.env.REACT_APP_AFFIL_REVIEW || "" },
  { key: "skills", icon: GraduationCap,  title: "Level up your skills",     desc: "Courses that strengthen your resume", url: process.env.REACT_APP_AFFIL_SKILLS || "" },
];
const nextStepsEnabled = () => NEXT_STEPS.some(s => s.url);
const nextStepUrl = (tpl, title) => (tpl || "").replace(/\{title\}/g, encodeURIComponent((title || "").trim()));

// ── Resume88 Premium — AI assistant (account-free, one-time unlock) ──────────────
// Shown only when REACT_APP_AI_ENABLED="1" (set it once the server keys are live).
// AI generation is fully paid: any use requires a valid unlock token, which is
// verified server-side after a one-time Stripe payment (/api/ai-unlock). Free,
// no-cost "teaser" suggestions (curated, not AI) live separately to drive upgrades.
const AI_ENABLED = process.env.REACT_APP_AI_ENABLED === "1";
const AI_UNLOCK_URL = process.env.REACT_APP_STRIPE_AI_URL || ""; // Stripe Payment Link
const AI_PRICE_LABEL = process.env.REACT_APP_AI_PRICE_LABEL || "$5";
// Paywall conversion levers. Price anchor = the typical competitor subscription
// we contrast against. Social proof shows only if you set a real number.
const AI_COMPARE = process.env.REACT_APP_AI_COMPARE || "$24/mo";
const AI_SOCIAL_PROOF = process.env.REACT_APP_SOCIAL_PROOF || ""; // e.g. "12,000"
// Landing-page testimonials. Provide REAL quotes only — either paste them into the
// array below, or set REACT_APP_TESTIMONIALS to a JSON array of
//   { quote, name, role }  objects. Empty by default → the whole section is hidden,
// so nothing unverified ever ships. (Fabricated reviews are never added here.)
const TESTIMONIALS = (() => {
  try {
    const raw = process.env.REACT_APP_TESTIMONIALS;
    if (raw) { const arr = JSON.parse(raw); if (Array.isArray(arr)) return arr.filter(t => t && t.quote).slice(0, 12); }
  } catch {}
  return [
    // { quote: "Landed 3 interviews in a week.", name: "Real Name", role: "Marketing Manager" },
  ];
})();
const testimonialsEnabled = () => TESTIMONIALS.length > 0;
// TEMPORARY pay-per-download gate. When REACT_APP_DOWNLOAD_PAYWALL="1", the PDF
// download requires a one-time $3 Stripe payment (verified server-side). Off by
// default, so nothing changes until you flip the flag in Vercel. Price label is
// display-only; the real charge is set by DOWNLOAD_PRICE_CENTS on the server.
const DOWNLOAD_PAYWALL = process.env.REACT_APP_DOWNLOAD_PAYWALL === "1";
const DOWNLOAD_PRICE = process.env.REACT_APP_DOWNLOAD_PRICE || "$3";
const DONATION_IN_MODAL = false; // donations hidden in the download modal for now
// Stripe publishable key (pk_live_… / pk_test_…). When set, the download fee is
// paid IN-PAGE via Stripe Embedded Checkout (no redirect). Without it, we fall
// back to the hosted-page redirect. Safe to expose (publishable keys are public).
const STRIPE_PK = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || "";

// Load Stripe.js once, on demand.
let _stripeJs = null;
function loadStripeJs() {
  if (typeof window !== "undefined" && window.Stripe) return Promise.resolve(window.Stripe);
  if (!_stripeJs) {
    _stripeJs = new Promise((resolve, reject) => {
      const s = document.createElement("script");
      s.src = "https://js.stripe.com/v3";
      s.async = true;
      s.onload = () => resolve(window.Stripe);
      s.onerror = () => reject(new Error("stripe_js"));
      document.head.appendChild(s);
    });
  }
  return _stripeJs;
}
// Session unlock: once the fee is paid, downloads stay unlocked until the tab is
// closed (sessionStorage) — so they can re-download as much as they want.
const dlUnlocked = () => { try { return sessionStorage.getItem("r88_dl_ok") === "1"; } catch { return false; } };
const setDlUnlocked = () => { try { sessionStorage.setItem("r88_dl_ok", "1"); } catch {} };

// "Email me a copy" — shown only once the Resend backend is configured.
const EMAIL_ENABLED = process.env.REACT_APP_EMAIL_ENABLED === "1";
async function sendResumeEmail(to, resume, accent, branded = false) {
  // Drop the base64 photo — the email/PDF don't use it; keeps the payload small.
  const lite = resume ? { ...resume, personal: { ...((resume && resume.personal) || {}) } } : {};
  if (lite.personal) delete lite.personal.photo;
  try {
    const r = await fetch("/api/send-resume", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ to, resume: lite, accent: accent || "", branded: !!branded }) });
    return await r.json(); // { ok, error? }
  } catch { return { ok: false, error: "network" }; }
}
// Decode a token's claims (base64url JSON payload; NO signature check — the server
// verifies) just so the client can tell an expired token from a live one and avoid
// a doomed call / show the right message.
const decodeAiClaims = (tok) => {
  try {
    const dot = (tok || "").lastIndexOf(".");
    if (dot < 1) return null;
    let b = tok.slice(0, dot).replace(/-/g, "+").replace(/_/g, "/");
    while (b.length % 4) b += "=";
    return JSON.parse(atob(b));
  } catch { return null; }
};
// Active = correct type and not past exp. v1 tokens have no exp → grandfathered
// (matches the server), so existing buyers are never treated as expired.
const tokenActive = (tok) => {
  if (!tok) return false;
  const c = decodeAiClaims(tok);
  if (!c || c.t !== "ai") return false;
  return !c.exp || c.exp > Math.floor(Date.now() / 1000);
};
// In-memory mirror so a successful unlock still works when localStorage is blocked
// or ephemeral (Safari Private, in-app / webview browsers) — at least for this tab.
let memAiToken = "";
const setAiToken = (t) => { memAiToken = t || ""; try { if (t) localStorage.setItem("r88_ai_token", t); } catch {} };
const clearAiToken = () => { memAiToken = ""; try { localStorage.removeItem("r88_ai_token"); } catch {} };
const rawAiToken = () => { try { const s = localStorage.getItem("r88_ai_token"); if (s) return s; } catch {} return memAiToken; };
// Callers get a token ONLY while it's still valid; an expired one reads as "no
// token" so the UI locks and offers restore.
const aiToken = () => { const t = rawAiToken(); return tokenActive(t) ? t : ""; };

const AIContext = createContext(null);
const useAI = () => useContext(AIContext);

// Every AI action is a serializable descriptor { k, id? } so it can be paused at
// the paywall, survive the full-page Stripe redirect, and be replayed exactly.
function useAIProvider(hostRef) {
  const [busy, setBusy] = useState(null);     // descriptor key currently generating
  const [paywall, setPaywall] = useState(false);
  const [unlocked, setUnlocked] = useState(() => !!aiToken());
  const [justUnlocked, setJustUnlocked] = useState(false);
  const [reviewText, setReviewText] = useState("");
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");
  const [aiCheckoutOpen, setAiCheckoutOpen] = useState(false); // in-page Stripe overlay
  const [expired, setExpired] = useState(false); // had a valid token that has lapsed
  const [undoSnap, setUndoSnap] = useState(null); // { resume, label } — revert the last AI change

  // On load, if a stored token has expired, clear it and remember so the paywall can
  // say "your access expired — restore it" instead of a cold "buy" wall.
  useEffect(() => {
    const raw = rawAiToken();
    if (raw && !tokenActive(raw)) { clearAiToken(); setExpired(true); }
  }, []);

  const descKey = (d) => `${d.k}${d.id ? ":" + d.id : ""}`;
  const cleanSkill = (n) => n.replace(/^[\s\-•*\d.)]+/, "").replace(/\.+$/, "").trim();
  // Drop blank lines / stray bullet symbols the model may emit, so no empty bullet
  // ever lands in the editor.
  const cleanBullets = (t) => (t || "").split("\n").map(l => l.replace(/^[\s\-•*]+/, "").trim()).filter(Boolean).join("\n");

  // Build the API payload for a descriptor from the live resume (via hostRef).
  const payloadFor = (d, r) => {
    const p = r.personal || {};
    const expFull = (r.experience || []).map(e => ({ title: e.title, company: e.company, bullets: e.bullets }));
    const expTitles = (r.experience || []).map(e => ({ title: e.title, company: e.company }));
    const skills = (r.skills || []).map(s => s.name).join(", ");
    if (d.k === "title")   return { title: p.title, experience: expTitles };
    if (d.k === "skills")  return { title: p.title, skills, experience: expTitles };
    if (d.k === "review")  return { title: p.title, summary: p.summary, experience: expFull, skills };
    if (d.k === "bullets") { const e = (r.experience || []).find(x => x.id === d.id) || {}; return { title: e.title, company: e.company, bullets: e.bullets }; }
    return { title: p.title, summary: p.summary, experience: expTitles }; // summary
  };

  // Low-level generate (token-gated; server re-verifies before any Claude call).
  const generate = useCallback(async (mode, payload) => {
    const tok = aiToken();
    if (!tok) return null;
    try {
      const res = await fetch("/api/ai", {
        method: "POST", headers: { "content-type": "application/json" },
        body: JSON.stringify({ mode, payload, token: tok }),
      });
      const d = await res.json();
      // Never reveal the cause to the user — only a formal downtime notice.
      if (!d || d.configured === false) { setErr("The AI service is temporarily unavailable. Please try again later."); return null; }
      if (d.unlocked === false) { clearAiToken(); setUnlocked(false); setExpired(true); setPaywall(true); return null; }
      if (d.error || !d.text) { setErr("The AI service is temporarily unavailable. Please try again later."); return null; }
      trackEvent("ai_generate", { template: mode });
      return d.text;
    } catch { setErr("The AI service is temporarily unavailable. Please try again later."); return null; }
  }, []);

  const applyResult = (d, text, host) => {
    if (d.k === "summary") host.update(["personal", "summary"], text);
    else if (d.k === "title") host.update(["personal", "title"], text.replace(/^["']|["']$/g, "").trim());
    else if (d.k === "bullets") host.setResume(r => ({ ...r, experience: r.experience.map(x => x.id === d.id ? { ...x, bullets: cleanBullets(text) } : x) }));
    else if (d.k === "skills") host.setResume(r => {
      const have = new Set(r.skills.map(s => s.name.toLowerCase()));
      const fresh = text.split(/[,\n]/).map(cleanSkill).filter(n => n && n.length <= 40 && !have.has(n.toLowerCase())).map(name => ({ name, level: "" }));
      return { ...r, skills: [...r.skills, ...fresh] };
    });
    else if (d.k === "review") setReviewText(text);
  };

  const UNDO_LABELS = { summary: "summary", title: "title", bullets: "experience bullets", skills: "skills", improveAll: "résumé" };

  const execAction = useCallback(async (d) => {
    setErr("");
    const host = hostRef.current || {};
    const resume = host.resume || {};
    const before = resume; // snapshot to power Undo (setResume replaces immutably)
    if (d.k === "improveAll") {
      // Rewrite summary + every experience block IN PLACE. All other sections
      // (skills, education, projects, certs, languages, contact) are untouched,
      // so the downloaded PDF keeps 100% of the original content.
      setDone(false); setBusy("improveAll");
      let changed = false;
      try {
        const s = await generate("summary", payloadFor({ k: "summary" }, resume));
        if (s) { host.update(["personal", "summary"], s); changed = true; }
        for (const e of (resume.experience || [])) {
          if (!aiToken()) break;
          if (!e.title && !e.bullets) continue;
          const b = await generate("bullets", { title: e.title, company: e.company, bullets: e.bullets });
          if (b) { host.setResume(r => ({ ...r, experience: r.experience.map(x => x.id === e.id ? { ...x, bullets: cleanBullets(b) } : x) })); changed = true; }
        }
        setDone(true);
        if (changed) setUndoSnap({ resume: before, label: UNDO_LABELS.improveAll });
      } finally { setBusy(null); }
      return;
    }
    setBusy(descKey(d));
    try {
      const text = await generate(d.k, payloadFor(d, resume));
      if (text) {
        if (d.k !== "review") setUndoSnap({ resume: before, label: UNDO_LABELS[d.k] || "section" });
        applyResult(d, text, host);
      }
    } finally { setBusy(null); }
  }, [generate, hostRef]);

  // Restore the résumé to exactly what the user had before the last AI change.
  const undoAi = useCallback(() => {
    const host = hostRef.current;
    if (host && host.setResume && undoSnap) {
      host.setResume(() => undoSnap.resume); // setResume auto-persists via effect
      setUndoSnap(null);
      trackEvent("ai_undo");
    }
  }, [undoSnap]);
  const dismissUndo = useCallback(() => setUndoSnap(null), []);

  // Public entry for EVERY AI action. If locked, remember the intent + where the
  // user is, open the paywall; after payment we resume that exact action here.
  const run = useCallback((d) => {
    setErr("");
    if (!aiToken()) {
      const host = hostRef.current || {};
      try { localStorage.setItem("r88_ai_pending", JSON.stringify({ d, view: host.view, tab: host.tab, showPreview: host.showPreview })); } catch {}
      trackEvent("ai_paywall", { template: d.k });
      setPaywall(true);
      return;
    }
    execAction(d);
  }, [execAction, hostRef]);

  // Verify a completed Stripe checkout session → mint + store the unlock token,
  // flip to unlocked, and replay whatever the user clicked before paying. Shared by
  // BOTH the in-page checkout (onComplete) and the hosted-redirect return (?ai_session).
  const redeemAiSession = useCallback(async (sid, { replay = true } = {}) => {
    if (!sid) return false;
    // Attach the resume's identity (if any) + visitor id so the unlock is recorded
    // in the admin Premium list with a name/email, not just the Stripe email.
    const hp = (hostRef.current && hostRef.current.resume && hostRef.current.resume.personal) || {};
    let vid = ""; try { vid = localStorage.getItem("r88_vid") || ""; } catch {}
    const qp = new URLSearchParams({ session: sid });
    if (hp.name) qp.set("name", hp.name);
    if (hp.email) qp.set("remail", hp.email);
    if (hp.title) qp.set("title", hp.title);
    if (vid) qp.set("vid", vid);
    try {
      const r = await fetch(`/api/ai-unlock?${qp.toString()}`);
      const d = await r.json();
      if (!(d && d.unlocked && d.token)) return false;
      setAiToken(d.token);                 // localStorage + in-memory mirror
      setUnlocked(true); setJustUnlocked(true); setExpired(false); setPaywall(false); setAiCheckoutOpen(false);
      trackEvent("ai_unlocked");
      setTimeout(() => setJustUnlocked(false), 6000);
      if (replay) {
        let pend = null;
        try { pend = JSON.parse(localStorage.getItem("r88_ai_pending") || "null"); } catch {}
        try { localStorage.removeItem("r88_ai_pending"); } catch {}
        if (pend && pend.d) {
          const host = hostRef.current || {};
          if (host.setView && pend.view) host.setView(pend.view);
          if (host.setTab && pend.tab) host.setTab(pend.tab);
          if (host.setShowPreview && typeof pend.showPreview === "boolean") host.setShowPreview(pend.showPreview);
          setTimeout(() => execAction(pend.d), 600); // let the restored screen mount
        }
      }
      return true;
    } catch { return false; }
  }, [execAction, hostRef]);

  // Hosted-redirect return path (fallback / older flow): ?ai_session= or session_id.
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const sid = p.get("ai_session") || p.get("session_id");
    if (!sid) return;
    redeemAiSession(sid).finally(() => {
      p.delete("ai_session"); p.delete("session_id");
      const qs = p.toString();
      window.history.replaceState({}, "", window.location.pathname + (qs ? "?" + qs : ""));
    });
  }, [redeemAiSession]);

  // Magic-link recovery: a paying customer opened their emailed ?ai_restore=<grant>
  // link (possibly on a new device). Validate it server-side → store the unlock.
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const grant = p.get("ai_restore");
    if (!grant) return;
    fetch(`/api/ai-restore?grant=${encodeURIComponent(grant)}`)
      .then(r => r.json())
      .then(d => {
        if (d && d.ok && d.token) {
          setAiToken(d.token);
          setUnlocked(true); setJustUnlocked(true); setExpired(false); setPaywall(false);
          trackEvent("ai_restored");
          setTimeout(() => setJustUnlocked(false), 6000);
        }
      })
      .catch(() => {})
      .finally(() => {
        p.delete("ai_restore");
        const qs = p.toString();
        window.history.replaceState({}, "", window.location.pathname + (qs ? "?" + qs : ""));
      });
  }, []);

  // Start the unlock. Preferred: in-page Embedded Checkout (no redirect, no new
  // window) — same proven flow as the $3 download. Fallback (no publishable key):
  // the hosted Stripe Payment Link redirect.
  const startUnlock = () => {
    trackEvent("ai_unlock_click");
    if (STRIPE_PK) { setAiCheckoutOpen(true); return; }
    if (AI_UNLOCK_URL) window.location.href = AI_UNLOCK_URL;
  };

  return { enabled: AI_ENABLED, busy, err, unlocked, justUnlocked, expired, reviewText, done, run, descKey,
           paywall, openPaywall: () => setPaywall(true), closePaywall: () => setPaywall(false), startUnlock,
           aiCheckoutOpen, closeAiCheckout: () => setAiCheckoutOpen(false), redeemAiSession,
           undoSnap, undoAi, dismissUndo };
}

// Brief confirmation shown after a successful AI unlock (post-Stripe redirect).
function AIUnlockedToast() {
  const ai = useAI();
  if (!ai || !ai.justUnlocked) return null;
  return (
    <div className="fixed bottom-5 left-1/2 -translate-x-1/2 z-[80] admin-fade-up">
      <div className="flex items-center gap-2.5 rounded-xl bg-slate-900 border border-brand-400/40 shadow-2xl px-4 py-3 text-sm">
        <Sparkles className="w-5 h-5 text-brand-400 flex-shrink-0" strokeWidth={2} />
        <span className="text-slate-100 font-semibold">Resume88 Premium unlocked — unlimited AI ✨</span>
      </div>
    </div>
  );
}

// Persistent bar offering to revert the last AI change — so a rewrite the user
// dislikes never destroys their original text. Sits just above the unlock toast.
function AIUndoBar() {
  const ai = useAI();
  if (!ai || !ai.undoSnap) return null;
  return (
    <div className="fixed bottom-20 left-1/2 -translate-x-1/2 z-[80] admin-fade-up px-3 w-full max-w-md">
      <div className="flex items-center gap-3 rounded-xl bg-slate-900 border border-white/15 shadow-2xl px-4 py-2.5 text-sm">
        <Wand2 className="w-4 h-4 text-brand-400 flex-shrink-0" strokeWidth={2} />
        <span className="text-slate-200 flex-1 min-w-0">AI updated your <strong className="text-white">{ai.undoSnap.label}</strong>.</span>
        <button onClick={ai.undoAi} className="font-bold text-brand-300 hover:text-brand-200 transition-colors inline-flex items-center gap-1 flex-shrink-0"><RotateCcw className="w-3.5 h-3.5" strokeWidth={2.5} /> Undo</button>
        <button onClick={ai.dismissUndo} aria-label="Dismiss" className="text-slate-500 hover:text-slate-200 transition-colors flex-shrink-0"><X className="w-4 h-4" /></button>
      </div>
    </div>
  );
}

// Small "✨ AI" button. `desc` is a serializable action descriptor ({k, id?}); the
// provider builds the payload, runs it (or paywalls + resumes after payment), and
// applies the result — so callers don't pass payload/onText closures.
function AIButton({ desc, label = "Improve with AI" }) {
  const ai = useAI();
  if (!ai || !ai.enabled) return null;
  const busy = ai.busy === ai.descKey(desc);
  return (
    <button type="button" disabled={busy} onClick={() => ai.run(desc)}
      className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold border border-brand-400/30 bg-brand-500/10 text-brand-300 hover:bg-brand-500/20 disabled:opacity-60 transition-colors">
      {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />}
      {busy ? "Writing…" : label}
    </button>
  );
}

function AIPaywallModal() {
  const ai = useAI();
  // Restore-purchase (magic link) form state — declared before the early return so
  // hook order stays stable.
  const [showRestore, setShowRestore] = useState(false);
  const [restoreEmail, setRestoreEmail] = useState("");
  const [restoreState, setRestoreState] = useState("idle"); // idle | sending | sent | error
  const expired = !!(ai && ai.expired);
  // If access lapsed, lead with the (free) restore path rather than a cold buy wall.
  useEffect(() => { if (expired) setShowRestore(true); }, [expired]);
  const submitRestore = async () => {
    const em = restoreEmail.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(em)) { setRestoreState("error"); return; }
    setRestoreState("sending");
    try {
      const r = await fetch("/api/ai-restore", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ email: em }) });
      const d = await r.json();
      setRestoreState(d && d.ok ? "sent" : "error");
    } catch { setRestoreState("error"); }
  };
  if (!ai || !ai.paywall) return null;
  return (
    <div className="fixed inset-0 z-[70] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 admin-overlay" onClick={ai.closePaywall}>
      <div role="dialog" aria-modal="true" aria-label="Unlock Resume88 Premium" className="admin-modal w-full max-w-md rounded-2xl bg-slate-900 border border-brand-400/20 shadow-2xl shadow-brand-500/10 overflow-hidden" onClick={e=>e.stopPropagation()}>
        {/* Gold gradient header */}
        <div className="relative px-6 pt-6 pb-5 text-center bg-gradient-to-b from-brand-500/15 to-transparent">
          <button onClick={ai.closePaywall} aria-label="Close" className="absolute top-3 right-3 text-slate-500 hover:text-slate-200 transition-colors"><X className="w-5 h-5" /></button>
          <div className="mb-2.5 flex justify-center"><span className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br from-brand-400 to-brand-600 text-slate-900 shadow-lg shadow-brand-500/30 paywall-pop"><Sparkles className="w-7 h-7" strokeWidth={2} /></span></div>
          <h2 className="text-2xl font-extrabold text-white tracking-tight">Resume88 Premium</h2>
          <p className="text-sm text-slate-400 mt-1">Let AI rewrite your resume into something recruiters can't skip.</p>
        </div>

        <div className="px-6 pb-6 text-center">
          {expired && (
            <div className="mb-4 -mt-1 rounded-xl border border-amber-400/25 bg-amber-500/10 px-3.5 py-2.5 text-left">
              <p className="text-[13px] font-semibold text-amber-200">Your Premium access expired.</p>
              <p className="text-[12px] text-amber-100/80 mt-0.5">Already purchased? Restore it free below — we’ll email a fresh link to the address you paid with.</p>
            </div>
          )}
          {/* Price anchor */}
          <div className="flex items-end justify-center gap-2 mb-1">
            <span className="text-slate-500 line-through text-lg">{AI_COMPARE}</span>
            <span className="text-4xl font-black text-brand-300 leading-none">{AI_PRICE_LABEL}</span>
            <span className="text-slate-400 text-sm font-semibold mb-0.5">once</span>
          </div>
          <p className="text-[12px] text-slate-500 mb-4">Others charge a monthly subscription. Pay once — yours forever.</p>

          <ul className="text-left text-sm text-slate-200 space-y-2 mb-4">
            {[
              "Rewrite weak bullets into quantified wins",
              "AI summary written for your exact role",
              "Skills tailored to your title & seniority",
              "Recruiter-ready headline + one-click full rewrite",
              "Unlimited generations — no per-use fees",
            ].map((f,i)=>(
              <li key={i} className="flex items-start gap-2"><Check className="w-4 h-4 text-brand-400 flex-shrink-0 mt-0.5" strokeWidth={2.5} /><span>{f}</span></li>
            ))}
          </ul>

          {/* Outcome framing */}
          <p className="text-[12px] text-slate-400 bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 mb-3">⏱️ Recruiters skim a resume in <strong className="text-slate-200">~7 seconds</strong>. Make every one of yours count.</p>

          {(STRIPE_PK || AI_UNLOCK_URL) ? (
            <button onClick={ai.startUnlock} className="cta-glow w-full py-3.5 bg-brand-600 text-slate-900 rounded-xl font-extrabold text-base hover:bg-brand-500 active:bg-brand-700 transition-colors inline-flex items-center justify-center gap-2">
              <Sparkles className="w-5 h-5" strokeWidth={2.25} /> Get Premium — {AI_PRICE_LABEL} one-time
            </button>
          ) : (
            <p className="text-xs text-amber-300/90">Premium isn’t configured yet.</p>
          )}

          <div className="mt-3 flex flex-wrap items-center justify-center gap-x-2 gap-y-1 text-[11px] font-semibold text-emerald-300">
            <span className="inline-flex items-center gap-1"><Lock className="w-3 h-3" /> Secure via Stripe</span>
            <span className="text-slate-600">·</span><span>No subscription</span>
            <span className="text-slate-600">·</span><span>No account</span>
            <span className="text-slate-600">·</span><span>Instant</span>
          </div>
          {AI_SOCIAL_PROOF && (
            <p className="mt-2.5 text-[12px] text-slate-400 inline-flex items-center gap-1.5"><Heart className="w-3.5 h-3.5 text-brand-400" /> Joined by <strong className="text-slate-200">{AI_SOCIAL_PROOF}+</strong> job seekers</p>
          )}
          <button onClick={ai.closePaywall} className="mt-3 block w-full text-xs text-slate-500 hover:text-slate-300 transition-colors">Maybe later</button>

          {/* Already purchased? Restore on any device via an emailed magic link. */}
          <div className="mt-3 pt-3 border-t border-white/10">
            {!showRestore ? (
              <button onClick={()=>setShowRestore(true)} className="text-[12px] text-slate-400 hover:text-brand-300 transition-colors">
                Already purchased? <span className="underline underline-offset-2">Restore your access</span>
              </button>
            ) : restoreState === "sent" ? (
              <p className="text-[12px] text-emerald-300 inline-flex items-center gap-1.5"><Check className="w-3.5 h-3.5" strokeWidth={2.5} /> If a purchase is on file for that email, we’ve sent a link. Check your inbox.</p>
            ) : (
              <div className="text-left">
                <p className="text-[12px] text-slate-400 mb-2">Enter the email you paid with — we’ll email a link to unlock Premium on this device.</p>
                <div className="flex gap-2">
                  <input type="email" value={restoreEmail} onChange={e=>{setRestoreEmail(e.target.value); if(restoreState==="error") setRestoreState("idle");}} placeholder="you@email.com"
                    className="flex-1 bg-slate-800/60 border border-white/10 text-slate-100 placeholder-slate-500 rounded-lg text-sm px-3 py-2 focus:outline-none focus:border-brand-400/70 focus:ring-2 focus:ring-brand-500/20 transition" />
                  <button onClick={submitRestore} disabled={restoreState==="sending"}
                    className="px-4 py-2 bg-brand-600 text-slate-900 rounded-lg text-sm font-bold hover:bg-brand-500 disabled:opacity-60 transition-colors whitespace-nowrap">
                    {restoreState==="sending" ? "Sending…" : "Send link"}
                  </button>
                </div>
                {restoreState === "error" && <p className="text-[11px] text-amber-400 mt-1.5">Enter a valid email address and try again.</p>}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Import an existing resume (PDF / text file / pasted text / LinkedIn PDF export).
function ImportModal({ open, onClose, onImported }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [text, setText] = useState("");
  const fileRef = useRef();

  useEffect(() => { if (open) { setBusy(false); setErr(""); setText(""); } }, [open]);
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);
  if (!open) return null;

  const finish = (raw, source) => {
    const parsed = parseResumeText(raw);
    if (!parsed || (parsed._found || 0) <= 1) {
      setErr("We couldn’t read much from that. Try pasting the resume text directly below.");
      return;
    }
    trackEvent("import", { template: source });
    onImported(parsed);
  };

  const handleFile = async (file) => {
    if (!file) return;
    setErr(""); setBusy(true);
    try {
      const isPdf = /\.pdf$/i.test(file.name) || file.type === "application/pdf";
      const raw = isPdf ? await extractPdfText(file) : await readFileText(file);
      if (!raw || raw.trim().length < 20) { setErr("That file looks empty or unreadable. Try pasting the text instead."); return; }
      finish(raw, isPdf ? "pdf" : "file");
    } catch (e) {
      setErr("Couldn’t read that file. If it’s a scanned/image PDF, paste the text instead.");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 admin-overlay" onClick={onClose}>
      <div role="dialog" aria-modal="true" aria-label="Import a resume" className="admin-modal w-full max-w-lg rounded-2xl bg-slate-900 border border-white/10 shadow-2xl overflow-hidden" onClick={e=>e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 pt-5 pb-3 border-b border-white/10">
          <h2 className="text-lg font-bold text-white flex items-center gap-2"><Upload className="w-5 h-5 text-brand-400" strokeWidth={2} /> Import your resume</h2>
          <button onClick={onClose} aria-label="Close" className="text-slate-500 hover:text-slate-200 transition-colors"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-6">
          <p className="text-sm text-slate-400 mb-4">Bring in an existing resume — we’ll fill in what we can, then you review &amp; polish in the editor.</p>

          {/* Upload */}
          <button onClick={()=>fileRef.current && fileRef.current.click()} disabled={busy}
            className="w-full rounded-xl border-2 border-dashed border-white/15 bg-white/[0.02] hover:border-brand-400/50 hover:bg-brand-500/[0.05] transition-colors p-6 flex flex-col items-center gap-2 disabled:opacity-60">
            {busy ? <Loader2 className="w-7 h-7 text-brand-400 animate-spin" /> : <FileDown className="w-7 h-7 text-brand-400" strokeWidth={1.5} />}
            <span className="text-sm font-semibold text-slate-200">{busy ? "Reading your file…" : "Upload a PDF or text file"}</span>
            <span className="text-[11px] text-slate-500">.pdf or .txt · processed in your browser, nothing uploaded</span>
          </button>
          <input ref={fileRef} type="file" accept=".pdf,.txt,application/pdf,text/plain" className="hidden"
            onChange={e=>{ const f = e.target.files && e.target.files[0]; e.target.value = ""; handleFile(f); }} />

          {/* LinkedIn tip */}
          <div className="mt-3 flex items-start gap-2 text-[11px] text-slate-500 bg-white/[0.02] border border-white/10 rounded-lg px-3 py-2">
            <Globe className="w-3.5 h-3.5 text-brand-400 flex-shrink-0 mt-0.5" />
            <span>From LinkedIn? Open your profile → <strong className="text-slate-300">More → Save to PDF</strong>, then upload that file here.</span>
          </div>

          {/* Divider */}
          <div className="my-4 flex items-center gap-3 text-[11px] text-slate-600"><div className="flex-1 h-px bg-white/10" /><span>or paste the text</span><div className="flex-1 h-px bg-white/10" /></div>

          {/* Paste */}
          <textarea value={text} onChange={e=>setText(e.target.value)} rows={5}
            placeholder="Paste your resume text here…"
            className="input-field resize-none leading-relaxed text-sm" />
          <button onClick={()=>{ if(text.trim().length<20){ setErr("Paste a bit more of your resume text to import."); return; } finish(text, "paste"); }}
            disabled={busy}
            className="mt-2 w-full py-2.5 bg-brand-600 text-slate-900 rounded-xl font-bold text-sm hover:bg-brand-500 active:bg-brand-700 transition-colors disabled:opacity-60">
            Import pasted text
          </button>

          {err && <p className="mt-3 text-xs text-amber-400">{err}</p>}
        </div>
      </div>
    </div>
  );
}

// In-page Stripe Embedded Checkout for the one-time $5 AI unlock — the SAME
// no-redirect / no-new-window flow as the download fee. On success it verifies the
// session via /api/ai-unlock (mint token) through ai.redeemAiSession, which unlocks
// AI instantly and replays whatever the user clicked before paying.
function AICheckout() {
  const ai = useAI();
  const open = !!(ai && ai.aiCheckoutOpen);
  const mountRef = useRef(null);
  const [err, setErr] = useState("");
  const [ready, setReady] = useState(false);
  // Latest redeem fn in a ref so the effect depends only on `open` (the context
  // object is a fresh reference every render and would otherwise re-init checkout).
  const redeemRef = useRef(null);
  redeemRef.current = ai && ai.redeemAiSession;
  useEffect(() => {
    if (!open) return;
    let checkout = null, cancelled = false;
    const sid = { current: "" };
    setErr(""); setReady(false);
    (async () => {
      try {
        const Stripe = await loadStripeJs();
        if (cancelled || !Stripe) return;
        const stripe = Stripe(STRIPE_PK);
        checkout = await stripe.initEmbeddedCheckout({
          fetchClientSecret: async () => {
            const r = await fetch("/api/ai-pay", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ embedded: true, source: getSource() }) });
            const d = await r.json();
            sid.current = d && d.sessionId ? d.sessionId : "";
            if (!d || !d.clientSecret) throw new Error("no_secret");
            return d.clientSecret;
          },
          onComplete: async () => {
            // Never trust the client — redeemAiSession re-verifies with Stripe.
            const ok = redeemRef.current ? await redeemRef.current(sid.current) : false;
            if (!ok) setErr("Payment couldn’t be confirmed. If you were charged, please contact us and we’ll unlock it for you.");
          },
        });
        if (cancelled) { try { checkout.destroy(); } catch {} return; }
        if (mountRef.current) { checkout.mount(mountRef.current); setReady(true); }
      } catch (e) {
        if (!cancelled) setErr("Couldn’t load secure checkout. Please try again.");
      }
    })();
    return () => { cancelled = true; try { checkout && checkout.destroy(); } catch {} };
  }, [open]);
  if (!open) return null;
  const onClose = () => ai && ai.closeAiCheckout();
  return (
    <div className="fixed inset-0 z-[75] bg-black/75 backdrop-blur-sm flex items-end sm:items-center justify-center sm:p-4 admin-overlay" onClick={onClose}>
      <div className="admin-modal w-full sm:max-w-md max-h-[94vh] flex flex-col rounded-t-3xl sm:rounded-3xl bg-slate-900 border border-white/10 shadow-2xl overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3.5 border-b border-white/10 flex-shrink-0">
          <span className="text-sm font-semibold text-white inline-flex items-center gap-2"><Sparkles className="w-4 h-4 text-brand-400" strokeWidth={2.25} /> Unlock Premium AI <span className="text-slate-400 font-normal"> · {AI_PRICE_LABEL}</span></span>
          <button onClick={onClose} aria-label="Close" className="w-8 h-8 rounded-full hover:bg-white/10 text-slate-400 hover:text-white flex items-center justify-center transition-colors"><X className="w-4 h-4" /></button>
        </div>
        <div className="overflow-y-auto bg-white flex-1 min-h-[280px]">
          {err ? (
            <div className="p-8 text-center">
              <p className="text-sm text-red-600 mb-3">{err}</p>
              <button onClick={onClose} className="text-xs font-semibold text-slate-500 hover:text-slate-700">Close</button>
            </div>
          ) : (
            <>
              {!ready && (
                <div className="py-20 flex flex-col items-center justify-center text-slate-400">
                  <Loader2 className="w-6 h-6 animate-spin mb-2" /><span className="text-xs">Loading secure payment…</span>
                </div>
              )}
              <div ref={mountRef} className="p-3" />
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// In-page Stripe Embedded Checkout for the temporary download fee. Renders the
// payment form as an overlay ON our page (no redirect); on success it verifies
// server-side, then unlocks downloads for the rest of the session.
function DownloadCheckout({ open, onClose, onPaid, price }) {
  const mountRef = useRef(null);
  const [err, setErr] = useState("");
  const [ready, setReady] = useState(false);
  useEffect(() => {
    if (!open) return;
    let checkout = null, cancelled = false;
    const sid = { current: "" };
    setErr(""); setReady(false);
    (async () => {
      try {
        const Stripe = await loadStripeJs();
        if (cancelled || !Stripe) return;
        const stripe = Stripe(STRIPE_PK);
        checkout = await stripe.initEmbeddedCheckout({
          fetchClientSecret: async () => {
            const r = await fetch("/api/download-pay", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ embedded: true, source: getSource() }) });
            const d = await r.json();
            sid.current = d && d.sessionId ? d.sessionId : "";
            if (!d || !d.clientSecret) throw new Error("no_secret");
            return d.clientSecret;
          },
          onComplete: async () => {
            // Never trust the client — confirm with Stripe the session was paid.
            try {
              const r = await fetch(`/api/download-unlock?session=${encodeURIComponent(sid.current)}`);
              const d = await r.json();
              if (d && d.paid) onPaid();
              else setErr("Payment couldn’t be confirmed. If you were charged, please contact us.");
            } catch { setErr("Payment couldn’t be confirmed. If you were charged, please contact us."); }
          },
        });
        if (cancelled) { try { checkout.destroy(); } catch {} return; }
        if (mountRef.current) { checkout.mount(mountRef.current); setReady(true); }
      } catch (e) {
        if (!cancelled) setErr("Couldn’t load secure checkout. Please try again.");
      }
    })();
    return () => { cancelled = true; try { checkout && checkout.destroy(); } catch {} };
  }, [open, onPaid]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[70] bg-black/75 backdrop-blur-sm flex items-end sm:items-center justify-center sm:p-4 admin-overlay" onClick={onClose}>
      <div className="admin-modal w-full sm:max-w-md max-h-[94vh] flex flex-col rounded-t-3xl sm:rounded-3xl bg-slate-900 border border-white/10 shadow-2xl overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3.5 border-b border-white/10 flex-shrink-0">
          <span className="text-sm font-semibold text-white inline-flex items-center gap-2"><Lock className="w-4 h-4 text-brand-400" strokeWidth={2.25} /> Secure checkout{price ? <span className="text-slate-400 font-normal"> · {price}</span> : null}</span>
          <button onClick={onClose} aria-label="Close" className="w-8 h-8 rounded-full hover:bg-white/10 text-slate-400 hover:text-white flex items-center justify-center transition-colors"><X className="w-4 h-4" /></button>
        </div>
        <div className="overflow-y-auto bg-white flex-1 min-h-[280px]">
          {err ? (
            <div className="p-8 text-center">
              <p className="text-sm text-red-600 mb-3">{err}</p>
              <button onClick={onClose} className="text-xs font-semibold text-slate-500 hover:text-slate-700">Close</button>
            </div>
          ) : (
            <>
              {!ready && (
                <div className="py-20 flex flex-col items-center justify-center text-slate-400">
                  <Loader2 className="w-6 h-6 animate-spin mb-2" /><span className="text-xs">Loading secure payment…</span>
                </div>
              )}
              <div ref={mountRef} className="p-3" />
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function DonationModal({ open, onClose, onDownload, jobTitle, resume, pal, dlPaywall, dlPaid, dlBusy, dlErr, dlPrice, onDownloadPay }) {
  const ai = useAI();
  const aiUnlocked = !!(ai && ai.unlocked); // AI Premium buyers get clean downloads free
  const [amt, setAmt] = useState(5); // 3 | 5 | 10 | 20 | "custom"
  const [customAmt, setCustomAmt] = useState("25");
  const [donateBusy, setDonateBusy] = useState(false);
  const [donateErr, setDonateErr] = useState("");
  const [downloaded, setDownloaded] = useState(false);
  const [emailTo, setEmailTo] = useState("");
  const [emailState, setEmailState] = useState("idle"); // idle | sending | sent | error
  const [emailErr, setEmailErr] = useState("");
  // Start fresh each time the popup opens (e.g. a second export).
  useEffect(() => {
    if (open) { setDownloaded(false); setEmailState("idle"); setEmailErr(""); setEmailTo((resume && resume.personal && resume.personal.email) || ""); }
  }, [open, resume]);
  // "Hit the paywall" — the pay-to-download gate was actually shown to this person.
  useEffect(() => {
    if (open && dlPaywall && !dlPaid && !aiUnlocked) trackEvent("download_paywall");
  }, [open, dlPaywall, dlPaid, aiUnlocked]);
  // If they upgrade AFTER taking the free copy, reset the confirmation so the fresh
  // clean "Download PDF" button is shown.
  useEffect(() => { if (dlPaid) setDownloaded(false); }, [dlPaid]);
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);
  if (!open) return null;
  const download = () => { onDownload && onDownload(); setDownloaded(true); };
  const donate = async () => {
    if (amt === "custom") {
      const dollars = Math.floor(Number(customAmt));
      if (!dollars || dollars < 1) { setDonateErr("Enter an amount of $1 or more."); return; }
      if (dollars > 5000) { setDonateErr("For gifts over $5,000, please email us — thank you!"); return; }
      setDonateErr("");
      setDonateBusy(true);
      trackEvent("donate_click", { template: "custom_" + dollars });
      // Open the tab synchronously (before await) so popup blockers don't eat it.
      // NOTE: do NOT pass "noopener" here — window.open returns null when it's set,
      // which would discard the handle. Null the opener after instead (reverse-
      // tabnabbing safety) since we navigate it to an external Stripe URL.
      const w = window.open("", "_blank");
      if (w) { try { w.opener = null; } catch {} }
      try {
        const r = await fetch("/api/donate", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ amount: dollars, source: getSource() }) });
        const d = await r.json();
        if (d && d.url) { if (w) w.location = d.url; else window.location.href = d.url; }
        else { if (w) w.close(); setDonateErr("Couldn’t start checkout — try a preset amount above."); }
      } catch {
        if (w) w.close(); setDonateErr("Couldn’t start checkout — please try again.");
      } finally { setDonateBusy(false); }
      return;
    }
    const url = donateUrlFor(amt);
    trackEvent("donate_click", { template: String(amt) });
    if (url) window.open(url, "_blank", "noopener");
  };
  const openNext = (s) => {
    const url = nextStepUrl(s.url, jobTitle);
    trackEvent("affiliate_click", { template: s.key });
    if (url) window.open(url, "_blank", "noopener");
  };
  const sendEmail = async () => {
    const to = emailTo.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(to)) { setEmailErr("Enter a valid email address."); setEmailState("error"); return; }
    setEmailState("sending");
    trackEvent("email_resume");
    const d = await sendResumeEmail(to, resume, pal && pal.accent, dlPaywall && !dlPaid && !aiUnlocked);
    if (d && d.ok) setEmailState("sent");
    else { setEmailErr(d && d.error === "rate_limited" ? "You’ve sent several emails recently — please try again later." : "Couldn’t send right now — please try again."); setEmailState("error"); }
  };
  // The four fixed presets + a custom "Other" amount (handled by /api/donate).
  const amountOpts = DONATE.amounts;
  const sel = amountOpts.find(a => a.v === amt) || amountOpts[0];
  const donateLabel = sel ? (sel.v === "custom" ? (Number(customAmt) >= 1 ? `$${Math.floor(Number(customAmt))}` : "a custom amount") : sel.label) : "";
  const showDonation = donateEnabled() && DONATION_IN_MODAL;
  const showNext = downloaded && nextStepsEnabled();
  return (
    <div className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center sm:p-4 admin-overlay" onClick={onClose}>
      <div role="dialog" aria-modal="true" aria-label="Download your resume" className="admin-modal w-full sm:max-w-md max-h-[92vh] overflow-y-auto rounded-t-3xl sm:rounded-3xl bg-slate-900 border border-white/10 shadow-2xl" onClick={e=>e.stopPropagation()}>
        <div className="mx-auto mt-2.5 h-1 w-10 rounded-full bg-white/15 sm:hidden" />
        <div className="p-6 sm:p-7 pt-4 text-center">
          <div className="mb-3 flex justify-center">
            <span className={`flex items-center justify-center w-14 h-14 rounded-2xl ${downloaded ? "bg-emerald-500/12" : "bg-brand-500/12"}`}>
              {downloaded ? <CheckCircle2 className="w-8 h-8 text-emerald-400" strokeWidth={1.75} /> : <FileDown className="w-8 h-8 text-brand-400" strokeWidth={1.75} />}
            </span>
          </div>
          <h2 className="text-[22px] font-bold text-white mb-1.5 tracking-tight">{downloaded ? "Your PDF is ready!" : "Your resume is ready!"}</h2>

          {/* Download action / confirmation */}
          {!downloaded ? (
            (dlPaywall && !dlPaid && !aiUnlocked) ? (
              // Freemium: free download (small footer) OR pay to remove it (clean).
              <>
                {/* Upgrade — emphasised */}
                <div className="mb-3 mt-3 text-left rounded-2xl border border-brand-400/25 bg-gradient-to-b from-brand-500/[0.12] to-brand-500/[0.03] p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="flex items-center justify-center w-7 h-7 rounded-lg bg-brand-500/15 text-brand-300 flex-shrink-0"><Sparkles className="w-4 h-4" strokeWidth={2.25} /></span>
                    <p className="text-[13px] font-semibold text-brand-100">Get the clean version — no footer</p>
                  </div>
                  <p className="text-[12.5px] text-slate-300 leading-relaxed mb-3">A one-time <strong className="text-white">{dlPrice}</strong> removes the small “Made with resume88.com” footer and unlocks unlimited clean re-downloads this session. No account, no subscription.</p>
                  <button onClick={onDownloadPay} disabled={dlBusy}
                    className="w-full py-3.5 bg-brand-600 text-slate-900 rounded-xl font-bold text-[15px] hover:bg-brand-500 active:bg-brand-700 transition-all disabled:opacity-60 inline-flex items-center justify-center gap-2 shadow-lg shadow-brand-600/25">
                    {dlBusy ? <><Loader2 className="w-4 h-4 animate-spin" /> Opening secure checkout…</> : <><Lock className="w-4 h-4" strokeWidth={2.5} /> Remove footer — {dlPrice}</>}
                  </button>
                  {dlErr && <p className="mt-2 text-[11px] text-amber-400">{dlErr}</p>}
                  <p className="mt-2 text-[10.5px] text-slate-500 flex items-center justify-center gap-1.5"><Lock className="w-3 h-3 flex-shrink-0" /> Secure Stripe · Apple&nbsp;Pay, Google&nbsp;Pay &amp; cards</p>
                </div>
                {/* Free option — always available */}
                <button onClick={download}
                  className="w-full py-3 rounded-xl font-semibold text-sm border border-white/15 bg-white/[0.03] text-slate-200 hover:bg-white/[0.07] hover:border-white/25 transition-colors inline-flex items-center justify-center gap-2">
                  <Download className="w-4 h-4" strokeWidth={2.25} /> Download free (with footer)
                </button>
                <p className="mt-1.5 text-[11px] text-slate-500">Your free PDF includes a small “Made with resume88.com” footer at the bottom.</p>
              </>
            ) : (
              <button onClick={download} className="w-full mt-3 py-4 bg-brand-600 text-slate-900 rounded-2xl font-bold text-[15px] hover:bg-brand-500 active:bg-brand-700 transition-all inline-flex items-center justify-center gap-2 shadow-lg shadow-brand-600/25">
                <Download className="w-4 h-4" strokeWidth={2.5} /> Download PDF
              </button>
            )
          ) : (
            <div className="mt-3">
              <div className="rounded-2xl border border-emerald-400/25 bg-emerald-500/10 text-emerald-300 text-sm font-medium py-3 px-3 flex items-center justify-center gap-2">
                <Check className="w-4 h-4 flex-shrink-0" strokeWidth={2.5} /><span>Download started — save it from your browser’s dialog.</span>
              </div>
              <button onClick={download} className="mt-2.5 text-xs font-medium text-brand-300 hover:text-brand-200 transition-colors inline-flex items-center gap-1"><Download className="w-3.5 h-3.5" /> Download again</button>
              {/* Free downloader → offer the clean upgrade */}
              {dlPaywall && !dlPaid && !aiUnlocked && (
                <div className="mt-3 pt-3 border-t border-white/10">
                  <p className="text-[12px] text-slate-400 mb-2">Got the version with the small footer. Want it clean?</p>
                  <button onClick={onDownloadPay} disabled={dlBusy}
                    className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-[13px] font-bold bg-brand-600 text-slate-900 hover:bg-brand-500 disabled:opacity-60 transition-colors">
                    {dlBusy ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Opening…</> : <><Lock className="w-3.5 h-3.5" strokeWidth={2.5} /> Remove footer — {dlPrice}</>}
                  </button>
                  {dlErr && <p className="mt-1.5 text-[11px] text-amber-400">{dlErr}</p>}
                </div>
              )}
            </div>
          )}

          {/* AI cross-sell — at peak intent, once the resume is in hand */}
          {downloaded && ai && ai.enabled && !ai.unlocked && (STRIPE_PK || AI_UNLOCK_URL) && (
            <div className="mt-4 text-left rounded-2xl border border-brand-400/25 bg-gradient-to-b from-brand-500/[0.12] to-brand-500/[0.03] p-4">
              <div className="flex items-center gap-2 mb-1">
                <span className="flex items-center justify-center w-7 h-7 rounded-lg bg-brand-500/15 text-brand-300 flex-shrink-0"><Sparkles className="w-4 h-4" strokeWidth={2.25} /></span>
                <p className="text-[13px] font-semibold text-brand-100">Before you send it — make recruiters notice</p>
              </div>
              <p className="text-[12.5px] text-slate-300 leading-relaxed mb-3">Let AI rewrite your bullets into quantified wins and tailor your summary to your title. A one-time <strong className="text-white">{AI_PRICE_LABEL}</strong> — no subscription, no account.</p>
              <button onClick={()=>{ trackEvent("ai_paywall", { template: "download_modal" }); ai.openPaywall(); }}
                className="w-full py-3 bg-brand-600 text-slate-900 rounded-xl font-bold text-[14px] hover:bg-brand-500 active:bg-brand-700 transition-colors inline-flex items-center justify-center gap-2 shadow-lg shadow-brand-600/25">
                <Sparkles className="w-4 h-4" strokeWidth={2.25} /> Polish with AI — {AI_PRICE_LABEL}
              </button>
            </div>
          )}

          {/* Email me a copy */}
          {EMAIL_ENABLED && (
            <div className="mt-4 text-left rounded-xl border border-white/10 bg-white/[0.03] p-3.5">
              <p className="text-[12px] font-semibold text-slate-200 mb-2 flex items-center gap-1.5"><Mail className="w-3.5 h-3.5 text-brand-400" /> Email me a copy</p>
              {emailState === "sent" ? (
                <p className="text-xs text-emerald-300 inline-flex items-center gap-1.5"><Check className="w-3.5 h-3.5" strokeWidth={2.5} /> Sent to {emailTo}</p>
              ) : (
                <>
                  <div className="flex gap-2">
                    <input type="email" value={emailTo} onChange={e=>setEmailTo(e.target.value)} placeholder="you@email.com"
                      className="flex-1 bg-slate-800/60 border border-white/10 text-slate-100 placeholder-slate-500 rounded-lg text-sm px-3 py-2 focus:outline-none focus:border-brand-400/70 focus:ring-2 focus:ring-brand-500/20 transition" />
                    <button onClick={sendEmail} disabled={emailState==="sending"}
                      className="px-4 py-2 bg-brand-600 text-slate-900 rounded-lg text-sm font-bold hover:bg-brand-500 disabled:opacity-60 transition-colors whitespace-nowrap">
                      {emailState==="sending" ? "Sending…" : "Send"}
                    </button>
                  </div>
                  {emailState === "error" && <p className="text-[11px] text-amber-400 mt-1.5">{emailErr || "Couldn’t send — check the address and try again."}</p>}
                  <p className="text-[10px] text-slate-500 mt-2">Edit the address to send it anywhere.</p>
                  <p className="mt-2 flex items-start gap-1.5 text-[10px] leading-relaxed text-slate-500 rounded-lg bg-white/[0.03] border border-white/5 px-2.5 py-2">
                    <Info className="w-3 h-3 mt-px shrink-0 text-brand-400/80" strokeWidth={2} />
                    <span>Emailed copies use one clean, recruiter-friendly layout in your theme color. For your exact template &amp; design, use <strong className="font-semibold text-slate-300">Download</strong>.</span>
                  </p>
                </>
              )}
            </div>
          )}

          {/* What's next — partner links at peak intent (after download) */}
          {showNext && (
            <div className="mt-5 text-left">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-2 text-center">What’s next?</p>
              <div className="space-y-2">
                {NEXT_STEPS.filter(s=>s.url).map(s=>(
                  <button key={s.key} onClick={()=>openNext(s)}
                    className="w-full flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.03] px-3.5 py-3 text-left transition-colors hover:border-brand-400/40 hover:bg-brand-500/[0.06] group">
                    <span className="flex items-center justify-center w-9 h-9 rounded-lg bg-brand-500/12 text-brand-300 flex-shrink-0"><s.icon className="w-[18px] h-[18px]" strokeWidth={2} /></span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-semibold text-slate-100 truncate">{s.title}</span>
                      <span className="block text-[11px] text-slate-400 truncate">{s.desc}</span>
                    </span>
                    <ArrowUpRight className="w-4 h-4 text-slate-500 group-hover:text-brand-300 flex-shrink-0 transition-colors" strokeWidth={2} />
                  </button>
                ))}
              </div>
              <p className="mt-2.5 text-[10px] text-slate-600 leading-relaxed text-center">Some links are partner links that support Resume88 at no cost to you.</p>
            </div>
          )}

          {/* Donation */}
          {showDonation && (
            <>
              <div className="my-5 flex items-center gap-3 text-[11px] text-slate-500">
                <div className="flex-1 h-px bg-white/10" />
                <span className="whitespace-nowrap">{downloaded ? "Enjoyed it? Help keep it free" : "Resume88 is free to use"}</span>
                <div className="flex-1 h-px bg-white/10" />
              </div>
              <p className="text-sm text-slate-400 leading-relaxed mb-4">
                No accounts, no paywall to download, and an ad-free builder. A small gift keeps Resume88 free for everyone and funds the next free tools we build for people who can’t afford the paid ones. Every dollar goes straight into free projects.
              </p>
              <div className="grid grid-cols-4 gap-2 mb-2">
                {amountOpts.filter(a=>a.v!=="custom").map(a=>(
                  <button key={a.v} onClick={()=>{setAmt(a.v);setDonateErr("");}}
                    className={`rounded-xl border px-2 py-3 transition-all ${amt===a.v?"border-brand-400 bg-brand-500/15":"border-white/10 bg-white/[0.03] hover:border-white/25"}`}>
                    <div className={`text-lg font-bold ${amt===a.v?"text-brand-300":"text-white"}`}>{a.label}</div>
                    <div className="text-[10px] text-slate-400 mt-0.5 leading-tight">{a.note}</div>
                  </button>
                ))}
              </div>
              {/* Custom "any amount" — uses /api/donate (Stripe Checkout) */}
              <button onClick={()=>{setAmt(amt==="custom"?5:"custom");setDonateErr("");}}
                className={`w-full rounded-xl border px-3 py-2.5 mb-2 text-sm font-medium transition-all ${amt==="custom"?"border-brand-400 bg-brand-500/15 text-brand-200":"border-white/10 bg-white/[0.03] text-slate-300 hover:border-white/25"}`}>
                {amt==="custom" ? "Custom amount — enter below" : "Other amount…"}
              </button>
              {amt==="custom" && (
                <div className="mb-3 flex items-center rounded-xl border border-white/15 bg-slate-800/60 px-3 focus-within:border-brand-400/70 focus-within:ring-2 focus-within:ring-brand-500/20 transition">
                  <span className="text-slate-400 text-base font-semibold">$</span>
                  <input type="number" inputMode="numeric" min="1" max="5000" step="1" value={customAmt} autoFocus
                    onChange={e=>{setCustomAmt(e.target.value);setDonateErr("");}}
                    placeholder="25"
                    className="flex-1 bg-transparent text-white text-base px-2 py-2.5 focus:outline-none placeholder-slate-500" />
                  <span className="text-[11px] text-slate-500 whitespace-nowrap">USD</span>
                </div>
              )}
              <button onClick={donate} disabled={donateBusy}
                className={`w-full py-3 rounded-xl font-bold text-sm transition-colors disabled:opacity-60 ${downloaded
                  ? "bg-brand-600 text-slate-900 hover:bg-brand-500 active:bg-brand-700"
                  : "border border-brand-400/30 bg-brand-500/10 text-brand-200 hover:bg-brand-500/20"}`}>
                <span className="inline-flex items-center justify-center gap-2">
                  {donateBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Heart className="w-4 h-4" strokeWidth={2.25} />}
                  {donateBusy ? "Opening checkout…" : <>Donate {donateLabel} with Apple Pay / Card</>}
                </span>
              </button>
              {donateErr && <p className="mt-2 text-[11px] text-amber-400 text-center">{donateErr}</p>}
              <p className="mt-3 text-[11px] text-slate-500 leading-relaxed inline-flex items-center gap-1.5"><Lock className="w-3 h-3 flex-shrink-0" /> Secure one-time payment via Stripe · Apple&nbsp;Pay, Google&nbsp;Pay &amp; cards</p>
            </>
          )}

          <button onClick={onClose} className="mt-4 text-xs text-slate-500 hover:text-slate-300 transition-colors">{downloaded ? "Close" : "Maybe later"}</button>
        </div>
      </div>
    </div>
  );
}

// Minimum requirements before a resume can be previewed/exported — keeps people
// from downloading a blank document. Returns a list of what's still missing.
function resumeRequirements(r) {
  const p = (r && r.personal) || {};
  const missing = [];
  if (!p.name || !p.name.trim()) missing.push("Your full name");
  if (!p.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(p.email)) missing.push("A valid email address");
  const hasContent =
    (p.summary && p.summary.trim()) ||
    (r.experience || []).some(e => e.title && e.title.trim()) ||
    (r.education || []).some(e => e.degree && e.degree.trim()) ||
    (r.skills || []).length > 0;
  if (!hasContent) missing.push("At least one section — a summary, work experience, education, or a skill");
  return missing;
}

export default function App() {
  const saved = load();
  const [resume, setResume] = useState(saved?.resume || EMPTY_RESUME);
  const [tmplId, setTmplId] = useState(saved?.tmplId || "classic");
  const [palKey, setPalKey] = useState(saved?.palKey || "navy");
  // Restore the screen the user was on across a refresh (their résumé already
  // persists) — but only the work screens, and only if there IS saved work, so a
  // fresh visitor still lands on the homepage. Admin is never auto-restored.
  const [view, setView] = useState(() => {
    try { const v = localStorage.getItem("rb4_view"); if (saved && (v === "editor" || v === "templates")) return v; } catch {}
    return "start";
  });
  const [step, setStep] = useState(0);
  const [tab, setTab] = useState("personal");
  const [showPreview, setShowPreview] = useState(() => {
    try { return localStorage.getItem("rb4_view") === "editor" && !!saved; } catch { return false; }
  });
  const [showDonate, setShowDonate] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [hasSaved, setHasSaved] = useState(!!saved);
  const [reqMissing, setReqMissing] = useState(null);
  const [dlPaid, setDlPaid] = useState(() => dlUnlocked()); // paid this session → downloads unlocked until the tab closes
  const [dlBusy, setDlBusy] = useState(false);   // opening the pay-to-download checkout
  const [dlErr, setDlErr] = useState("");
  const [showDlCheckout, setShowDlCheckout] = useState(false); // in-page Stripe overlay

  const pal = PALETTE_SETS[palKey] || PALETTE_SETS.navy;
  // Live handle the AI provider uses to read the resume, apply results, and
  // restore the screen after the Stripe redirect. Filled fresh on every render.
  const hostRef = useRef({});
  const ai = useAIProvider(hostRef);

  useEffect(() => { trackEvent("view"); }, []);

  // Remember the current work screen so a refresh returns here instead of the
  // homepage (résumé data already persists). Only editor/templates; going Home
  // clears it; admin is never remembered.
  useEffect(() => {
    try {
      if (view === "editor" || view === "templates") localStorage.setItem("rb4_view", view);
      else if (view === "start") localStorage.removeItem("rb4_view");
    } catch {}
  }, [view]);

  // Persist ALL resume changes (not just personal fields) so nothing is lost on
  // reload — the section editors call setResume directly without going through update().
  useEffect(() => { persist({ resume, tmplId, palKey }); }, [resume, tmplId, palKey]);

  const update = useCallback((path, val) => {
    setResume(prev => {
      const next = path.length===1 ? {...prev,[path[0]]:val} : {...prev,[path[0]]:{...prev[path[0]],[path[1]]:val}};
      persist({ resume:next, tmplId, palKey });
      return next;
    });
  }, [tmplId, palKey]);

  const saveAll = (r=resume,t=tmplId,p=palKey) => persist({resume:r,tmplId:t,palKey:p});

  // Keep the AI provider's view of app state current (resume + appliers + nav).
  hostRef.current = { resume, update, setResume, setView, setTab, setShowPreview, view, tab, showPreview };

  const onImported = (parsed) => {
    // Don't silently wipe existing work — confirm if there's meaningful content.
    const cur = resume || {};
    const hasContent = (cur.personal && cur.personal.name) || (cur.personal && cur.personal.summary) || (cur.experience || []).length > 0;
    if (hasContent && !window.confirm("Importing will replace the resume currently saved in this browser. Continue?")) return;
    const merged = { ...EMPTY_RESUME, ...parsed, personal: { ...EMPTY_RESUME.personal, ...(parsed.personal || {}) } };
    delete merged._found;
    setResume(merged);
    saveAll(merged, tmplId, palKey);
    setHasSaved(true);
    setShowImport(false);
    setTab("personal");
    setShowPreview(false);
    setView("editor");
  };

  const clearData = () => {
    try { localStorage.removeItem("rb4"); } catch {}
    setResume(EMPTY_RESUME);
    setTmplId("classic");
    setPalKey("navy");
    setStep(0);
    setTab("personal");
    setShowPreview(false);
    setHasSaved(false);
  };

  // Clicking "Export" opens the popup; the actual download happens from its button,
  // keeping the download + donation ask + confirmation all on one screen.
  const doExport = () => {
    const missing = resumeRequirements(resume);
    if (missing.length) { trackEvent("export_blocked"); setReqMissing(missing); return; }
    trackEvent("export_open");
    setShowDonate(true);
  };
  const runDownload = () => {
    // Freemium: free download carries the footer; a paid session gets it clean.
    // AI Premium ($5) also unlocks clean downloads — a paid user never gets a footer.
    const branded = DOWNLOAD_PAYWALL && !dlPaid && !(ai && ai.unlocked);
    trackEvent("download",{template:tmplId,color:palKey});
    if (branded) trackEvent("download_free");
    try { saveSubmission(resume,{template:tmplId,color:palKey}); } catch {}
    // Never let a render/print error be a silent dead click — always give feedback.
    try { exportPDF(tmplId,pal,resume,branded); }
    catch (e) { trackEvent("download_error"); alert("Something went wrong preparing your PDF. Please try again, or use “Email me a copy”."); }
    // NOTE: do NOT re-lock — once paid, downloads stay unlocked for the whole
    // session so they can re-download as many times as they want.
  };

  // Mark the fee paid for this session and open the ready-to-download screen.
  const markDownloadPaid = useCallback(() => {
    trackEvent("download_paid");
    setDlUnlocked(); setDlPaid(true); setShowDlCheckout(false); setShowDonate(true);
  }, []);

  // Start the fee payment. Preferred: in-page Stripe Embedded Checkout (no leaving
  // the site). Fallback (no publishable key configured): hosted-page redirect.
  const startDownloadPay = async () => {
    setDlErr("");
    trackEvent("download_pay_click");
    if (STRIPE_PK) { setShowDlCheckout(true); return; }
    setDlBusy(true);
    try {
      const r = await fetch("/api/download-pay", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ source: getSource() }) });
      const d = await r.json();
      if (d && d.url) { window.location.href = d.url; return; }
      setDlErr("Couldn’t start checkout — please try again."); setDlBusy(false);
    } catch { setDlErr("Couldn’t start checkout — please try again."); setDlBusy(false); }
  };

  // Return from the hosted-page fallback (?dl_session=…): verify + unlock the session.
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const sid = p.get("dl_session");
    if (!sid) return;
    fetch(`/api/download-unlock?session=${encodeURIComponent(sid)}`)
      .then(r => r.json())
      .then(d => { if (d && d.paid) { trackEvent("download_paid"); setDlUnlocked(); setDlPaid(true); setShowDonate(true); } })
      .catch(() => {})
      .finally(() => {
        p.delete("dl_session"); const qs = p.toString();
        window.history.replaceState({}, "", window.location.pathname + (qs ? "?" + qs : ""));
      });
  }, []);

  const screen =
    view==="admin" ? (
      <Suspense fallback={<div className="min-h-screen bg-slate-900 flex items-center justify-center text-slate-400"><div className="text-4xl animate-spin">⏳</div></div>}>
        <AdminDashboard onExit={()=>setView("start")} />
      </Suspense>
    )
    : view==="start" ? <StartScreen onGuided={()=>setView("guided")} onManual={()=>setView("editor")} onImport={()=>setShowImport(true)} hasSaved={hasSaved} onClear={clearData} onAdmin={()=>setView("admin")} />
    : view==="templates" ? <TemplateScreen tmplId={tmplId} palKey={palKey} resume={resume} pal={pal} onSelectTemplate={t=>{setTmplId(t);saveAll(resume,t,palKey);}} onSelectPalette={p=>{setPalKey(p);saveAll(resume,tmplId,p);}} onBack={()=>{ setView("editor"); setShowPreview(true); }} onExport={doExport} />
    : view==="guided" ? <GuidedFlow resume={resume} setResume={setResume} step={step} setStep={setStep} update={update} onFinish={()=>{ setView("editor"); setShowPreview(true); }} onSkip={()=>{ setView("editor"); setShowPreview(false); }} />
    : <EditorLayout resume={resume} setResume={setResume} update={update} tmplId={tmplId} pal={pal} tab={tab} setTab={setTab} onTemplates={()=>setView("templates")} onExport={doExport} onBack={()=>setView("start")} saveAll={saveAll} showPreviewInit={showPreview} palKey={palKey} />;

  return (<AIContext.Provider value={ai}>
    {screen}
    <AIPaywallModal />
    <AICheckout />
    <AIUnlockedToast />
    <AIUndoBar />
    <DonationModal open={showDonate} onClose={()=>setShowDonate(false)} onDownload={runDownload} jobTitle={resume?.personal?.title} resume={resume} pal={pal}
      dlPaywall={DOWNLOAD_PAYWALL} dlPaid={dlPaid} dlBusy={dlBusy} dlErr={dlErr} dlPrice={DOWNLOAD_PRICE} onDownloadPay={startDownloadPay} />
    <DownloadCheckout open={showDlCheckout} onClose={()=>setShowDlCheckout(false)} onPaid={markDownloadPaid} price={DOWNLOAD_PRICE} />
    <ImportModal open={showImport} onClose={()=>setShowImport(false)} onImported={onImported} />
    {reqMissing && (
      <div className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 admin-overlay" onClick={()=>setReqMissing(null)}>
        <div className="admin-modal w-full max-w-sm rounded-2xl bg-slate-900 border border-white/10 shadow-2xl p-6 sm:p-7 text-center" onClick={e=>e.stopPropagation()}>
          <div className="mb-3 flex justify-center"><ClipboardList className="w-11 h-11 text-brand-400" strokeWidth={1.5} /></div>
          <h2 className="text-xl font-bold text-white mb-2">Just a little more</h2>
          <p className="text-sm text-slate-400 mb-4">Add the following so your resume isn’t blank:</p>
          <ul className="text-left text-sm text-slate-300 space-y-2 mb-6">
            {reqMissing.map((m,i)=>(<li key={i} className="flex items-start gap-2"><span className="text-amber-400 mt-px">•</span><span>{m}</span></li>))}
          </ul>
          <button onClick={()=>{ setView("editor"); setReqMissing(null); }} className="w-full py-3 bg-brand-600 text-slate-900 rounded-xl font-bold text-sm hover:bg-brand-500 active:bg-brand-700 transition-colors">Back to editing</button>
        </div>
      </div>
    )}
  </AIContext.Provider>);
}

// ─── START SCREEN ─────────────────────────────────────────────────────────────
function StartScreen({onGuided,onManual,onImport,hasSaved,onClear,onAdmin}) {
  const t = TRANSLATIONS[getLang()];
  const isRtl = getLang() === "ar";
  const [form, setForm] = useState({name:"",email:"",message:""});
  const [status, setStatus] = useState("idle");
  const [showContact, setShowContact] = useState(false);

  // IntersectionObserver for scroll-reveal animations
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    if (mq.matches) return;
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(e => { if (e.isIntersecting) { e.target.classList.add("is-visible"); observer.unobserve(e.target); } });
    }, { threshold: 0.15, rootMargin: "0px 0px -40px 0px" });
    document.querySelectorAll(".scroll-reveal").forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const sendMessage = async () => {
    if (!form.name || !form.email || !form.message) return;
    setStatus("sending");
    // Persist to the dashboard inbox (best-effort — never blocks the form).
    fetch("/api/contact", {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name: form.name, email: form.email, message: form.message }),
    }).catch(() => {});
    try {
      const res = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          access_key: process.env.REACT_APP_WEB3FORMS_KEY,
          subject: "New message from Resume88",
          from_name: form.name,
          email: form.email,
          message: form.message,
        }),
      });
      const data = await res.json();
      setStatus(data.success ? "sent" : "error");
    } catch { setStatus("error"); }
  };

  const trustIcons = [
    <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z" clipRule="evenodd"/></svg>,
    <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4"><path fillRule="evenodd" d="M10 1a4.5 4.5 0 00-4.5 4.5V9H5a2 2 0 00-2 2v6a2 2 0 002 2h10a2 2 0 002-2v-6a2 2 0 00-2-2h-.5V5.5A4.5 4.5 0 0010 1zm3 8V5.5a3 3 0 10-6 0V9h6z" clipRule="evenodd"/></svg>,
    <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4"><path d="M10.75 2.75a.75.75 0 00-1.5 0v8.614L6.295 8.235a.75.75 0 10-1.09 1.03l4.25 4.5a.75.75 0 001.09 0l4.25-4.5a.75.75 0 00-1.09-1.03l-2.955 3.129V2.75z"/><path d="M3.5 12.75a.75.75 0 00-1.5 0v2.5A2.75 2.75 0 004.75 18h10.5A2.75 2.75 0 0018 15.25v-2.5a.75.75 0 00-1.5 0v2.5c0 .69-.56 1.25-1.25 1.25H4.75c-.69 0-1.25-.56-1.25-1.25v-2.5z"/></svg>,
    <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4"><path fillRule="evenodd" d="M10 1a4.5 4.5 0 00-4.5 4.5V9H5a2 2 0 00-2 2v6a2 2 0 002 2h10a2 2 0 002-2v-6a2 2 0 00-2-2h-.5V5.5A4.5 4.5 0 0010 1zm3 8V5.5a3 3 0 10-6 0V9h6z" clipRule="evenodd"/></svg>,
  ];

  const vpIcons = [
    /* Free forever */ <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} className="w-7 h-7"><path strokeLinecap="round" strokeLinejoin="round" d="M21 11.25v8.25a1.5 1.5 0 01-1.5 1.5H5.25a1.5 1.5 0 01-1.5-1.5v-8.25M12 4.875A2.625 2.625 0 109.375 7.5H12m0-2.625V7.5m0-2.625A2.625 2.625 0 1114.625 7.5H12m0 0V21m-8.625-9.75h18c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125h-18c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125z"/></svg>,
    /* No account */  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} className="w-7 h-7"><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z"/></svg>,
    /* Instant PDF */  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} className="w-7 h-7"><path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3"/></svg>,
    /* Templates */    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} className="w-7 h-7"><path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6A2.25 2.25 0 016 3.75h2.25A2.25 2.25 0 0110.5 6v2.25a2.25 2.25 0 01-2.25 2.25H6a2.25 2.25 0 01-2.25-2.25V6zM3.75 15.75A2.25 2.25 0 016 13.5h2.25a2.25 2.25 0 012.25 2.25V18a2.25 2.25 0 01-2.25 2.25H6A2.25 2.25 0 013.75 18v-2.25zM13.5 6a2.25 2.25 0 012.25-2.25H18A2.25 2.25 0 0120.25 6v2.25A2.25 2.25 0 0118 10.5h-2.25a2.25 2.25 0 01-2.25-2.25V6zM13.5 15.75a2.25 2.25 0 012.25-2.25H18a2.25 2.25 0 012.25 2.25V18A2.25 2.25 0 0118 20.25h-2.25A2.25 2.25 0 0113.5 18v-2.25z"/></svg>,
    /* Works everywhere */ <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} className="w-7 h-7"><path strokeLinecap="round" strokeLinejoin="round" d="M10.5 1.5H8.25A2.25 2.25 0 006 3.75v16.5a2.25 2.25 0 002.25 2.25h7.5A2.25 2.25 0 0018 20.25V3.75a2.25 2.25 0 00-2.25-2.25H13.5m-3 0V3h3V1.5m-3 0h3m-3 18.75h3"/></svg>,
    /* Private */      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} className="w-7 h-7"><path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z"/></svg>,
  ];

  return (
    <div className="min-h-screen bg-[#060a16] text-slate-200" dir={isRtl?"rtl":"ltr"} style={{fontFamily:"'Inter','Segoe UI',system-ui,-apple-system,sans-serif"}}>

      {/* ── TOP NAV ── */}
      <nav className="sticky top-0 z-40 bg-[#060a16]/85 backdrop-blur-md border-b border-white/10">
        <div className="max-w-5xl mx-auto px-5 h-12 flex items-center justify-between gap-3">
          <a href="/" className="flex items-center gap-2 font-extrabold text-white text-sm tracking-tight flex-shrink-0">
            <img src="/logo-mark.png" alt="Resume88" width="22" height="22" />Resume<span className="text-[#ffd000]">88</span>
          </a>
          {/* Horizontally scrollable on small screens so links never wrap out of
              the fixed-height bar; standard row on larger screens. */}
          <div className="flex items-center gap-x-4 text-[13px] font-medium text-slate-400 overflow-x-auto whitespace-nowrap min-w-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden sm:justify-end">
            <a href="/resume-examples/" className="hover:text-[#ffd000] transition-colors flex-shrink-0">Resume Examples</a>
            <a href="/tools/ats-checker/" className="hover:text-[#ffd000] transition-colors flex-shrink-0">ATS Checker</a>
            <a href="/blog/" className="hover:text-[#ffd000] transition-colors flex-shrink-0">Blog</a>
            <a href="/glossary/" className="hover:text-[#ffd000] transition-colors flex-shrink-0">Glossary</a>
            <button onClick={()=>{ setShowContact(true); setTimeout(()=>document.getElementById("contact")?.scrollIntoView({behavior:"smooth",block:"center"}),60); }} className="hover:text-[#ffd000] transition-colors focus:outline-none flex-shrink-0">Contact</button>
          </div>
        </div>
      </nav>

      {/* ── HERO ── */}
      <section className="relative overflow-hidden">
        {/* Background gradient, glow + dotted grid */}
        <div className="absolute inset-0 bg-gradient-to-b from-slate-900/50 via-[#060a16] to-[#060a16]" />
        <div className="absolute top-[-12%] left-1/2 -translate-x-1/2 w-[900px] h-[700px] rounded-full opacity-[0.20]" style={{background:"radial-gradient(circle, #ffd000 0%, transparent 65%)"}} />
        <div className="absolute inset-0 pointer-events-none" style={{backgroundImage:"radial-gradient(rgba(255,255,255,0.045) 1px, transparent 1px)",backgroundSize:"22px 22px",maskImage:"linear-gradient(to bottom, black, transparent 75%)",WebkitMaskImage:"linear-gradient(to bottom, black, transparent 75%)"}} />

        <div className="relative max-w-3xl mx-auto px-5 pt-12 pb-20 sm:pt-16 sm:pb-28 text-center">
          {/* Brand logo */}
          <div className="hero-anim-1 flex items-center justify-center gap-3 mb-8">
            <img src="/logo-mark.png" alt="Resume88 logo" width="52" height="52" className="rounded-lg w-12 h-12 sm:w-[52px] sm:h-[52px]" />
            <span className="text-2xl sm:text-3xl font-extrabold text-white tracking-tight">Resume<span className="text-[#ffd000]">88</span></span>
          </div>
          {/* Badge */}
          <div className="hero-anim-1 inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-brand-500/10 border border-brand-400/20 text-brand-300 text-xs font-medium mb-8">
            <span className="w-1.5 h-1.5 rounded-full bg-brand-400 animate-pulse" />
            {t.badge}
          </div>

          {/* Headline */}
          <h1 className="hero-anim-2 text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white tracking-tight leading-[1.1] mb-5">
            {t.headline}
          </h1>

          {/* Subheadline */}
          <p className="hero-anim-3 text-base sm:text-lg text-slate-400 max-w-xl mx-auto leading-relaxed mb-8">
            {t.heroSub}
          </p>

          {/* Primary CTA */}
          <div className="hero-anim-4 flex flex-col items-center gap-4">
            <button onClick={onGuided}
              className="cta-glow px-8 py-4 bg-brand-600 text-slate-900 rounded-2xl text-base sm:text-lg font-bold hover:bg-brand-500 active:bg-brand-700 transition-colors">
              {t.cta}
            </button>
            <button onClick={onImport}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold border border-white/15 bg-white/[0.03] text-slate-200 hover:border-brand-400/40 hover:text-brand-200 transition-colors">
              <Upload className="w-4 h-4" strokeWidth={2} /> Import existing resume
            </button>
            {hasSaved && (
              <div className="flex flex-col items-center gap-1.5">
                <button onClick={onManual} className="text-sm text-emerald-300 bg-emerald-500/10 border border-emerald-400/20 rounded-xl px-4 py-2 hover:bg-emerald-500/20 transition-colors">
                  {t.saved}
                </button>
                <button onClick={()=>{ if(window.confirm("Clear your saved resume progress? This permanently erases the data stored in this browser and can’t be undone.")) onClear&&onClear(); }}
                  className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-red-400 transition-colors">
                  <Trash2 className="w-3.5 h-3.5" /> Clear saved data
                </button>
              </div>
            )}
            <p className="text-xs text-slate-500 tracking-wide">{t.ctaSub}</p>
            {AI_ENABLED && t.aiUpsell && (
              <p className="mt-1 inline-flex items-center gap-1.5 text-[12.5px] text-brand-300/90"><Sparkles className="w-3.5 h-3.5 text-brand-400 flex-shrink-0" strokeWidth={2} /> <span className="text-slate-400">{t.aiUpsell}</span></p>
            )}
            {AI_SOCIAL_PROOF && (
              <p className="mt-3 text-[13px] text-slate-400 inline-flex items-center gap-1.5"><Heart className="w-3.5 h-3.5 text-brand-400" strokeWidth={2.25} /> Trusted by <strong className="text-slate-200">{AI_SOCIAL_PROOF}+</strong> job seekers</p>
            )}
          </div>

          {/* Secondary link */}
          <div className="hero-anim-5 mt-4 flex items-center justify-center gap-5 text-sm text-slate-500">
            <button onClick={onManual} className="hover:text-brand-400 transition-colors">
              {t.manual} &rarr;
            </button>
            <span className="text-slate-700">&middot;</span>
            <a href="/blog/" className="hover:text-brand-400 transition-colors">Resume Writing Guides</a>
          </div>
        </div>
      </section>

      {/* ── TRUST BAR ── */}
      <section className="scroll-reveal border-y border-white/10 bg-white/[0.02]">
        <div className="max-w-4xl mx-auto px-5 py-5 flex flex-wrap justify-center gap-x-10 gap-y-3">
          {(t.trustBar||[]).map((item, i) => (
            <div key={i} className="flex items-center gap-2 text-slate-400">
              <span className="text-brand-400">{trustIcons[i]}</span>
              <span className="text-sm font-medium">{item}</span>
            </div>
          ))}
        </div>
      </section>

      {/* ── VALUE PROPS ── */}
      <section className="max-w-5xl mx-auto px-5 py-20 sm:py-24">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {[
            [t.vp1, t.vp1d, 0], [t.vp2, t.vp2d, 1], [t.vp3, t.vp3d, 2],
            [t.vp4, t.vp4d, 3], [t.vp5, t.vp5d, 4], [t.vp6, t.vp6d, 5],
          ].map(([title, desc, iconIdx], i) => (
            <div key={i} className={`scroll-reveal scroll-reveal-delay-${(i%3)+1} group rounded-2xl border border-white/10 bg-white/5 p-6 hover:border-brand-400/30 hover:bg-white/[0.07] transition-all`}>
              <div className="w-10 h-10 rounded-xl bg-brand-500/15 text-brand-300 flex items-center justify-center mb-4 group-hover:bg-brand-500/25 transition-colors">
                {vpIcons[iconIdx]}
              </div>
              <h3 className="text-sm font-bold text-white mb-1.5">{title}</h3>
              <p className="text-sm text-slate-400 leading-relaxed">{desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── TESTIMONIALS ── (renders only when real quotes are configured) */}
      {testimonialsEnabled() && (
        <section className="scroll-reveal max-w-5xl mx-auto px-5 pb-4 sm:pb-8">
          <h2 className="text-center text-xs font-semibold uppercase tracking-widest text-slate-500 mb-8">Loved by job seekers</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {TESTIMONIALS.slice(0,6).map((t2, i) => (
              <figure key={i} className={`scroll-reveal scroll-reveal-delay-${(i%3)+1} rounded-2xl border border-white/10 bg-white/5 p-6`}>
                <div className="text-3xl leading-none text-brand-400/60 mb-2" aria-hidden="true">&ldquo;</div>
                <blockquote className="text-sm text-slate-200 leading-relaxed">{t2.quote}</blockquote>
                {(t2.name || t2.role) && (
                  <figcaption className="mt-4 text-xs text-slate-400">
                    {t2.name && <span className="font-semibold text-slate-300">{t2.name}</span>}
                    {t2.name && t2.role && <span className="text-slate-600"> · </span>}
                    {t2.role && <span>{t2.role}</span>}
                  </figcaption>
                )}
              </figure>
            ))}
          </div>
        </section>
      )}

      {/* ── STEPS ── */}
      <section className="scroll-reveal bg-gradient-to-b from-slate-900/40 to-[#060a16] py-16 sm:py-20">
        <div className="max-w-4xl mx-auto px-5">
          <div className="text-center mb-10">
            <h2 className="text-2xl sm:text-3xl font-bold text-white tracking-tight mb-3">{t.howTitle}</h2>
          </div>
          <div className="grid sm:grid-cols-3 gap-8 sm:gap-6">
            {[
              [t.step1, t.step1d, "01"],
              [t.step2, t.step2d, "02"],
              [t.step3, t.step3d, "03"],
            ].map(([title, desc, num], i) => (
              <div key={i} className={`scroll-reveal scroll-reveal-delay-${i+1} text-center sm:text-left`}>
                <div className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-brand-600 text-slate-900 text-sm font-bold mb-4 shadow-lg shadow-brand-600/30">{num}</div>
                <h3 className="text-base font-bold text-white mb-1.5">{title}</h3>
                <p className="text-sm text-slate-400 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── VISUAL / MOCKUP ── */}
      <section className="scroll-reveal max-w-4xl mx-auto px-5 py-10 sm:py-16">
        <div className="float-anim rounded-2xl border border-white/10 bg-slate-900 overflow-hidden shadow-2xl shadow-brand-950/40">
          <div className="flex items-center gap-1.5 px-4 py-2.5 bg-slate-800/60 border-b border-white/10">
            <div className="w-2.5 h-2.5 rounded-full bg-red-400/70" />
            <div className="w-2.5 h-2.5 rounded-full bg-yellow-400/70" />
            <div className="w-2.5 h-2.5 rounded-full bg-green-400/70" />
            <span className="ml-3 text-xs text-slate-500 font-medium">resume88.com</span>
          </div>
          <div className="p-4 sm:p-6 bg-slate-800/20">
            {/* Mini resume mockup — stays light (it's the product output) */}
            <div className="bg-white rounded-lg p-6 sm:p-8 shadow-xl">
            <div className="flex gap-6">
              <div className="hidden sm:block w-[30%] space-y-3">
                <div className="w-16 h-16 rounded-full bg-brand-100 mx-auto" />
                <div className="h-2 bg-gray-200 rounded-full w-3/4 mx-auto" />
                <div className="h-1.5 bg-gray-100 rounded-full w-2/3 mx-auto" />
                <div className="border-t border-gray-100 pt-3 mt-3 space-y-2">
                  <div className="h-1.5 bg-brand-100 rounded-full w-1/3" />
                  <div className="flex flex-wrap gap-1">
                    {["React","Node","Python","Figma"].map(s=><span key={s} className="text-[8px] bg-brand-50 text-brand-500 px-2 py-0.5 rounded-full">{s}</span>)}
                  </div>
                </div>
              </div>
              <div className="flex-1 space-y-4">
                <div>
                  <div className="h-3 bg-gray-800 rounded w-48 mb-1.5" />
                  <div className="h-1.5 bg-brand-400 rounded w-32" />
                </div>
                <div className="border-t border-gray-100 pt-3 space-y-2">
                  <div className="h-1.5 bg-brand-200 rounded-full w-16" />
                  <div className="h-1.5 bg-gray-100 rounded-full w-full" />
                  <div className="h-1.5 bg-gray-100 rounded-full w-11/12" />
                  <div className="h-1.5 bg-gray-100 rounded-full w-4/5" />
                </div>
                <div className="border-t border-gray-100 pt-3 space-y-2">
                  <div className="h-1.5 bg-brand-200 rounded-full w-20" />
                  <div className="flex justify-between items-start">
                    <div className="space-y-1"><div className="h-1.5 bg-gray-200 rounded w-36" /><div className="h-1 bg-gray-100 rounded w-24" /></div>
                    <div className="h-1 bg-gray-100 rounded w-16" />
                  </div>
                  <div className="ml-3 space-y-1">
                    <div className="h-1 bg-gray-100 rounded-full w-5/6" />
                    <div className="h-1 bg-gray-100 rounded-full w-3/4" />
                  </div>
                </div>
                <div className="border-t border-gray-100 pt-3 space-y-2">
                  <div className="h-1.5 bg-brand-200 rounded-full w-16" />
                  <div className="flex justify-between"><div className="space-y-1"><div className="h-1.5 bg-gray-200 rounded w-40" /><div className="h-1 bg-gray-100 rounded w-28" /></div><div className="h-1 bg-gray-100 rounded w-12" /></div>
                </div>
              </div>
            </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── FINAL CTA ── */}
      <section className="scroll-reveal bg-gradient-to-b from-[#060a16] via-brand-950/30 to-[#060a16] py-20 sm:py-24">
        <div className="max-w-xl mx-auto px-5 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-white tracking-tight mb-4">{t.finalCta}</h2>
          <p className="text-sm text-slate-400 mb-8 leading-relaxed">{t.finalCtaSub}</p>
          <button onClick={onGuided}
            className="cta-glow px-8 py-4 bg-brand-600 text-slate-900 rounded-2xl text-base font-bold hover:bg-brand-500 active:bg-brand-700 transition-colors mb-4">
            {t.cta}
          </button>
          <p className="text-xs text-slate-500 tracking-wide">{t.ctaSub}</p>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer id="contact" className="border-t border-white/10 py-8 px-5">
        {/* Deep links → let Google crawl individual pages straight from the homepage */}
        <nav aria-label="Popular pages" className="max-w-5xl mx-auto mb-8 grid sm:grid-cols-2 gap-x-10 gap-y-6 text-left">
          <div>
            <h2 className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-2.5">Popular resume examples</h2>
            <ul className="grid grid-cols-2 gap-x-4 gap-y-1.5 list-none p-0 m-0">
              {FOOTER_EXAMPLES.map(([slug,label])=>(
                <li key={slug}><a href={`/resume-examples/${slug}/`} className="text-xs text-slate-500 hover:text-brand-400 transition-colors">{label}</a></li>
              ))}
            </ul>
            <a href="/resume-examples/" className="inline-block mt-2.5 text-xs font-medium text-brand-400/80 hover:text-brand-400 transition-colors">All resume examples →</a>
          </div>
          <div>
            <h2 className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-2.5">Resume guides</h2>
            <ul className="grid grid-cols-1 gap-y-1.5 list-none p-0 m-0">
              {FOOTER_GUIDES.map(([slug,label])=>(
                <li key={slug}><a href={`/blog/${slug}`} className="text-xs text-slate-500 hover:text-brand-400 transition-colors">{label}</a></li>
              ))}
            </ul>
            <a href="/blog/" className="inline-block mt-2.5 text-xs font-medium text-brand-400/80 hover:text-brand-400 transition-colors">All guides →</a>
          </div>
        </nav>
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-xs text-slate-500 flex items-center justify-center gap-2 flex-wrap">
            {t.builtBy} &middot;{" "}
            <a href="/resume-examples/" className="text-slate-500 hover:text-brand-400 transition-colors">Resume Examples</a>
            &middot;
            <a href="/tools/ats-checker/" className="text-slate-500 hover:text-brand-400 transition-colors">ATS Checker</a>
            &middot;
            <a href="/blog/" className="text-slate-500 hover:text-brand-400 transition-colors">Blog</a>
            &middot;
            <a href="/glossary/" className="text-slate-500 hover:text-brand-400 transition-colors">Glossary</a>
            &middot;
            <button onClick={()=>setShowContact(s=>!s)} className="text-slate-500 hover:text-brand-400 transition-colors focus:outline-none">{t.contact}</button>
            &middot;
            <a href="/about/" className="text-slate-500 hover:text-brand-400 transition-colors">About</a>
            &middot;
            <a href="/privacy/" className="text-slate-500 hover:text-brand-400 transition-colors">Privacy</a>
            &middot;
            <a href="/terms/" className="text-slate-500 hover:text-brand-400 transition-colors">Terms</a>
            <button onClick={onAdmin} aria-label="Admin login" title="Admin" className="text-slate-600 hover:text-slate-400 focus:outline-none opacity-40 hover:opacity-70 ml-1 inline-flex items-center"><Settings className="w-3.5 h-3.5" /></button>
          </p>
          {showContact && (
            <div className="mt-6 max-w-sm mx-auto">
              <p className="text-xs text-slate-400 mb-3">{t.contactSub}</p>
              {status === "sent" ? (
                <div className="text-center py-3 text-emerald-400 font-semibold text-sm">{t.sent}</div>
              ) : (
                <div className="space-y-2">
                  <input aria-label={t.namePh} value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))} placeholder={t.namePh} className="input-field" />
                  <input aria-label={t.emailPh} value={form.email} onChange={e=>setForm(f=>({...f,email:e.target.value}))} placeholder={t.emailPh} type="email" className="input-field" />
                  <textarea aria-label={t.msgPh} value={form.message} onChange={e=>setForm(f=>({...f,message:e.target.value}))} placeholder={t.msgPh} rows={3} className="input-field resize-none" />
                  {status==="error" && <p className="text-xs text-red-400">{t.sendErr}</p>}
                  <button onClick={sendMessage} disabled={status==="sending"} className="w-full py-2.5 bg-brand-600 text-slate-900 rounded-xl font-semibold text-sm hover:bg-brand-500 transition-colors disabled:opacity-60">
                    {status==="sending" ? t.sending : t.send}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </footer>
    </div>
  );
}

// ─── TEMPLATE SCREEN ──────────────────────────────────────────────────────────
function TemplateScreen({tmplId,palKey,resume,pal,onSelectTemplate,onSelectPalette,onBack,onExport}) {
  const [hov, setHov] = useState(null);
  return (
    <div className="min-h-screen bg-[#060a16] text-slate-200 flex flex-col">
      <div className="bg-slate-900/70 backdrop-blur-sm border-b border-white/10 px-5 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={onBack} aria-label="Back" className="text-slate-400 hover:text-white transition-colors p-1 -ml-1"><ArrowLeft className="w-5 h-5" /></button>
          <span className="font-bold text-white inline-flex items-center gap-2"><Palette className="w-5 h-5 text-brand-400" strokeWidth={2} /> Templates &amp; Colors</span>
        </div>
        <div className="flex gap-2">
          <button onClick={onBack} className="px-3 py-1.5 border border-white/10 rounded-lg text-sm text-slate-300 hover:bg-white/5 hover:border-white/20 transition-all inline-flex items-center gap-1.5"><Pencil className="w-3.5 h-3.5" /> Back to Edit</button>
          <button onClick={onExport} className="px-4 py-1.5 bg-brand-600 text-slate-900 rounded-lg text-sm font-semibold hover:bg-brand-500 transition-colors inline-flex items-center gap-1.5"><Download className="w-4 h-4" strokeWidth={2.25} /> Export PDF</button>
        </div>
      </div>
      <div className="flex flex-1 overflow-hidden">
        <div className="w-full lg:w-[380px] overflow-auto p-5 border-r border-white/10 bg-slate-900/40 flex-shrink-0">
          <h3 className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">Color Theme</h3>
          <div className="grid grid-cols-4 gap-2 mb-6">
            {Object.entries(PALETTE_SETS).map(([k,p])=>(
              <button key={k} onClick={()=>onSelectPalette(k)} className={`rounded-xl overflow-hidden border-2 transition-all ${k===palKey?"border-brand-400 shadow-lg shadow-brand-500/20 scale-105":"border-white/10 hover:border-white/30"}`}>
                <div className="h-8" style={{background:`linear-gradient(135deg,${p.dark},${p.accent})`}} />
                <div className="text-xs py-1 text-center capitalize font-medium text-slate-300 bg-slate-800/80">{k}</div>
              </button>
            ))}
          </div>
          <h3 className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">Template Layout</h3>
          <div className="space-y-2">
            {TEMPLATES.map(t=>(
              <button key={t.id} onClick={()=>onSelectTemplate(t.id)} onMouseEnter={()=>setHov(t.id)} onMouseLeave={()=>setHov(null)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-2xl border-2 transition text-left ${t.id===tmplId?"border-brand-500 bg-brand-500/10":"border-white/10 hover:border-white/25 bg-white/[0.03]"}`}>
                <t.icon className="w-5 h-5 text-slate-300 flex-shrink-0" strokeWidth={1.75} />
                <div className="flex-1"><div className="font-semibold text-white text-sm">{t.label}</div><div className="text-xs text-slate-400">{t.desc}</div></div>
                {t.id===tmplId && <span className="text-brand-400 text-xs font-bold inline-flex items-center gap-1"><Check className="w-3.5 h-3.5" strokeWidth={2.5} /> Active</span>}
              </button>
            ))}
          </div>
        </div>
        <div className="hidden lg:flex flex-1 bg-slate-950 overflow-auto p-6 justify-center items-start">
          <div className="bg-white shadow-2xl rounded-lg overflow-hidden w-full max-w-[560px]" style={{transform:"scale(0.87)",transformOrigin:"top center",marginBottom:"-120px"}}>
            <ResumeDoc tmplId={hov||tmplId} pal={pal} resume={resume} />
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── EDITOR LAYOUT ────────────────────────────────────────────────────────────
const TABS = [
  {key:"personal",  label:"Personal",   icon:User},
  {key:"summary",   label:"Summary",    icon:FileText},
  {key:"experience",label:"Experience", icon:Briefcase},
  {key:"education", label:"Education",  icon:GraduationCap},
  {key:"skills",    label:"Skills",     icon:Zap},
  {key:"languages", label:"Languages",  icon:Globe},
  {key:"extras",    label:"Extras",     icon:Award},
  {key:"hobbies",   label:"Interests",  icon:Target},
];

// ── Resume health check (free, heuristic — no API) ──────────────────────────────
// Surfaces specific, credible weaknesses to build trust and motivate the Premium
// one-click AI fix. Returns a 0–100 score + concrete issues.
const WEAK_PHRASE = /\b(responsible for|worked on|helped (with|to)|duties includ|in charge of|tasked with|assisted with)\b/i;
const WEAK_START = new Set(["responsible","worked","helped","assisted","duties","was","were","participated","involved","handled"]);
function analyzeResume(r) {
  const p = (r && r.personal) || {};
  const exp = r.experience || [];
  const bullets = exp.flatMap(e => (e.bullets || "").split("\n").map(b=>b.trim()).filter(Boolean));
  const issues = [];
  const add = (sev, label) => issues.push({ sev, label });

  if (!p.summary || p.summary.trim().length < 120) add("high", "Your professional summary is missing or too short.");
  else if (p.summary.trim().length > 700) add("low", "Your summary is quite long — tighten it to 2–4 sentences.");

  if (exp.length === 0) add("high", "No work experience added yet.");
  else if (bullets.length === 0) add("high", "Your roles have no accomplishment bullet points.");

  if (bullets.length >= 2) {
    const quantified = bullets.filter(b => /\d/.test(b)).length;
    if (quantified / bullets.length < 0.3) add("high", "Few achievements are backed by numbers or metrics.");
    const weakStart = bullets.filter(b => WEAK_START.has((b.split(/\s+/)[0] || "").toLowerCase().replace(/[^a-z]/g,""))).length;
    if (weakStart > 0) add("med", `${weakStart} bullet${weakStart>1?"s don’t":" doesn’t"} start with a strong action verb.`);
    const weakPhrase = bullets.filter(b => WEAK_PHRASE.test(b)).length;
    if (weakPhrase > 0) add("med", `Weak phrasing found (e.g. “responsible for”) in ${weakPhrase} bullet${weakPhrase>1?"s":""}.`);
  }

  const skills = (r.skills || []).length;
  if (skills < 5) add("med", `Only ${skills} skill${skills===1?"":"s"} listed — aim for 8–12 relevant ones.`);
  if (!p.linkedin || !p.linkedin.trim()) add("low", "Add your LinkedIn URL — recruiters expect it.");

  const w = { high: 15, med: 8, low: 4 };
  const score = Math.max(35, Math.min(98, 100 - issues.reduce((s, i) => s + w[i.sev], 0)));
  return { score, issues };
}

// Projected score after a Premium AI rewrite — AI closes ~80% of the gap to 100,
// capped at 96 so it stays credible.
const projectedScore = (s) => Math.min(96, s + Math.round((100 - s) * 0.8));

// Pick the most convincing line to show transformed (a weak one if possible).
function weakestBullet(r) {
  const exp = (r && r.experience) || [];
  let first = null, firstTitle = "";
  for (const e of exp) {
    for (const b of (e.bullets || "").split("\n").map(s => s.trim()).filter(Boolean)) {
      if (!first) { first = b; firstTitle = e.title || ""; }
      const w0 = (b.split(/\s+/)[0] || "").toLowerCase().replace(/[^a-z]/g, "");
      if (!/\d/.test(b) || WEAK_PHRASE.test(b) || WEAK_START.has(w0)) return { text: b, title: e.title || (r.personal && r.personal.title) || "" };
    }
  }
  if (first) return { text: first, title: firstTitle || (r.personal && r.personal.title) || "" };
  const sum = ((r.personal && r.personal.summary) || "").trim();
  if (sum.length > 12) {
    // First sentence, without lookbehind (which crashes older Safari).
    const m = sum.match(/^[\s\S]*?[.!?](\s|$)/);
    return { text: ((m ? m[0] : sum).trim()).slice(0, 200), title: (r.personal && r.personal.title) || "" };
  }
  return null;
}

// Animated count-up used for the projected score.
function AnimatedNumber({ to, duration = 1000, className }) {
  const [n, setN] = useState(0);
  useEffect(() => {
    let raf, start;
    const step = (ts) => { if (!start) start = ts; const p = Math.min(1, (ts - start) / duration); setN(Math.round(p * p * (3 - 2 * p) * to)); if (p < 1) raf = requestAnimationFrame(step); };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [to, duration]);
  return <span className={className}>{n}</span>;
}
const ArrowDownIcon = () => (<span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-brand-500/15 text-brand-300"><ArrowDown className="w-3.5 h-3.5" strokeWidth={2.5} /></span>);

// Per-section conversion config. `mode` maps to the /api/ai-teaser prompt.
const TEASER_CFG = {
  resume:  { mode: "bullets", heading: "See the difference Premium makes",   beforeLabel: "Your line",    noun: "resume",   cta: "Reveal the full rewrite" },
  summary: { mode: "summary", heading: "Make your summary recruiter-ready",  beforeLabel: "Your summary", noun: "summary",  cta: "Rewrite my summary with AI" },
  bullets: { mode: "bullets", heading: "Turn duties into achievements",      beforeLabel: "Your bullet",  noun: "bullets",  cta: "Improve my bullets with AI" },
  title:   { mode: "title",   heading: "Sharpen your professional headline", beforeLabel: "Your headline",noun: "headline", cta: "Optimize my headline with AI" },
  skills:  { mode: "skills",  heading: "Get skills tailored to your role",    beforeLabel: "",             noun: "skills",   cta: "Unlock AI-tailored skills" },
};
const STATIC_TEASERS = {
  bullets: { before: "Responsible for managing the team and daily tasks.", preview: "Led a 9-person team to 130% of", hidden: 8, demo: true },
  summary: { before: "Hardworking professional seeking a new opportunity.", preview: "Results-driven professional with 6+ years driving", hidden: 9, demo: true },
  title:   { before: "Marketing person", preview: "Senior Marketing Manager — Growth &", hidden: 4, demo: true },
};

// The conversion proof, reusable per section. Shows the user's own content
// rewritten by AI (strong opening + locked blurred tail). Calls /api/ai-teaser
// once per visitor per kind (cached); skills uses the free heuristic (no API).
// Never shown to paid users; truncated server-side so it isn't free usable AI.
function AITeaser({ kind = "resume", source = "", title = "", variant = "full" }) {
  const ai = useAI();
  const cfg = TEASER_CFG[kind] || TEASER_CFG.bullets;
  const cacheKey = "r88_teaser_" + kind;
  const [data, setData] = useState(() => { try { return JSON.parse(localStorage.getItem(cacheKey) || "null"); } catch { return null; } });
  const src = (source || "").trim();
  const enabled = !!(ai && ai.enabled && !ai.unlocked);

  // Debounced: fires shortly AFTER the user pauses typing, using their latest
  // content — so the teaser appears as soon as a section is filled, not only on
  // remount. The card itself shows instantly (loading state) the moment there's
  // content; this just fills in the AI preview.
  useEffect(() => {
    if (kind === "skills" || data || !enabled || src.length < 12) return;
    let off = false;
    const id = setTimeout(() => {
      fetch("/api/ai-teaser", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ text: src, title, mode: cfg.mode }) })
        .then(r => r.json())
        .then(d => {
          if (off) return;
          if (d && d.configured && d.preview) { const obj = { before: src, preview: d.preview, hidden: d.hiddenWords || 6 }; setData(obj); try { localStorage.setItem(cacheKey, JSON.stringify(obj)); } catch {} }
          else setData(STATIC_TEASERS[cfg.mode] || STATIC_TEASERS.bullets);
        })
        .catch(() => { if (!off) setData(STATIC_TEASERS[cfg.mode] || STATIC_TEASERS.bullets); });
    }, 550);
    return () => { off = true; clearTimeout(id); };
  }, [kind, src, data, enabled, cfg.mode, title, cacheKey]);

  if (!enabled) return null;
  const pad = variant === "compact" ? "p-4" : "p-5";

  const Shell = (body, note) => (
    <div className={`teaser-card ${variant === "compact" ? "mt-6" : "mt-4"} rounded-2xl p-[1.5px] bg-gradient-to-br from-brand-400/60 via-brand-500/20 to-transparent admin-fade-up`}>
      <div className={`rounded-2xl bg-slate-900/90 ${pad}`}>
        <p className="text-[11px] font-bold uppercase tracking-widest text-brand-300 flex items-center gap-1.5 mb-3"><Sparkles className="w-3.5 h-3.5" /> {cfg.heading}</p>
        {body}
        <button onClick={ai.openPaywall} className="mt-3.5 w-full py-2.5 rounded-xl bg-brand-600 text-slate-900 font-bold text-sm hover:bg-brand-500 active:bg-brand-700 transition-colors inline-flex items-center justify-center gap-2">
          <Sparkles className="w-4 h-4" strokeWidth={2.25} /> {cfg.cta} — {AI_PRICE_LABEL}
        </button>
        {note && <p className="mt-1.5 text-center text-[10px] text-slate-500">{note}</p>}
      </div>
    </div>
  );

  // Skills — free heuristic chips (no API), some visible, the rest locked.
  if (kind === "skills") {
    const sugg = suggestSkillsFor(title);
    if (!sugg.length) return null;
    const visible = sugg.slice(0, 3), more = Math.max(3, sugg.length - 3);
    return Shell(
      <div className="flex flex-wrap gap-1.5 items-center">
        {visible.map(s => <span key={s} className="tag-pill bg-brand-500/15 text-brand-300 border-brand-400/25 text-xs">{s}</span>)}
        {Array.from({ length: 4 }).map((_, i) => <span key={i} className="tag-pill teaser-blur text-brand-300 border-brand-400/20 text-xs">Skill</span>)}
        <span className="inline-flex items-center gap-1 text-[11px] text-slate-400"><Lock className="w-3 h-3" /> +{more} more</span>
      </div>,
      "Premium tailors skills to your exact role & seniority."
    );
  }

  // Show the card the instant there's content — with the AI preview if it's
  // loaded, otherwise a live "writing…" state that fills in moments later.
  if (data || src.length >= 12) {
    const before = data ? data.before : src;
    return Shell(
      <div className="space-y-2.5">
        <div className="rounded-xl border border-white/10 bg-white/[0.03] px-3.5 py-2.5">
          <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 mb-1">{cfg.beforeLabel}</div>
          <p className="text-[13px] text-slate-400 line-through decoration-slate-600/60">{before}</p>
        </div>
        <div className="flex justify-center"><ArrowDownIcon /></div>
        <div className="teaser-after rounded-xl border border-brand-400/40 bg-brand-500/[0.08] px-3.5 py-2.5 relative overflow-hidden">
          <div className="text-[10px] font-semibold uppercase tracking-wide text-brand-300 mb-1 flex items-center gap-1"><Wand2 className="w-3 h-3" /> With Premium AI</div>
          {data ? (
            <p className="text-[13px] text-white font-medium leading-relaxed">{data.preview}{" "}<span className="teaser-blur align-middle">{"█".repeat(Math.min(18, (data.hidden || 6) * 3))}</span><Lock className="inline w-3 h-3 ml-1 text-brand-300 align-middle" /></p>
          ) : (
            <p className="text-[13px] text-slate-300 font-medium inline-flex items-center gap-2"><Loader2 className="w-3.5 h-3.5 animate-spin text-brand-300" /> Writing a stronger version of your {cfg.noun}…</p>
          )}
        </div>
      </div>,
      data ? (data.demo ? "Example shown — Premium rewrites your real content." : "This is your own content, rewritten by AI.") : null
    );
  }

  // No content in this section yet → a simple CTA card.
  return Shell(<p className="text-[13px] text-slate-400">Let Premium AI write strong, recruiter-ready {cfg.noun} for you — in one click.</p>, null);
}

function ResumeHealth({ resume, embedded }) {
  const ai = useAI();
  if (!ai || !ai.enabled) return null;
  // Nothing meaningful to review on an empty resume.
  const p0 = resume.personal || {};
  if (!p0.name && !(p0.summary || "").trim() && (resume.experience || []).length === 0 && (resume.skills || []).length === 0) return null;

  const running = ai.busy === "improveAll";
  const busy = running || ai.busy === "review";
  const { score, issues } = analyzeResume(resume);
  const tone = score >= 80 ? { c: "#34d399", bg: "border-emerald-400/25 bg-emerald-500/[0.06]", t: "text-emerald-300" }
    : score >= 55 ? { c: "#fbbf24", bg: "border-amber-400/25 bg-amber-500/[0.06]", t: "text-amber-300" }
    : { c: "#f87171", bg: "border-red-400/25 bg-red-500/[0.06]", t: "text-red-300" };
  const sevDot = { high: "bg-red-400", med: "bg-amber-400", low: "bg-slate-400" };
  const locked = !ai.unlocked;
  const proj = projectedScore(score);
  const showLift = locked && issues.length > 0 && proj > score;

  return (
    <div className={`w-full rounded-2xl border p-5 ${tone.bg} ${embedded ? "" : "mt-4 max-w-[680px] mx-auto"}`}>
      <div className="flex items-center gap-4">
        <div className="relative w-14 h-14 flex-shrink-0 rounded-full grid place-items-center"
          style={{ background: `conic-gradient(${tone.c} ${score}%, rgba(255,255,255,0.1) 0)` }}>
          <div className="w-11 h-11 rounded-full bg-slate-900 grid place-items-center text-sm font-bold text-white">{score}</div>
        </div>
        <div className="min-w-0">
          <h3 className="font-bold text-white text-sm">Resume Health Check</h3>
          <p className={`text-xs ${tone.t}`}>{issues.length === 0 ? "Looks strong — nice work!" : `${issues.length} thing${issues.length>1?"s":""} could be stronger`}</p>
        </div>
      </div>

      {issues.length > 0 && (
        <ul className="mt-4 space-y-1.5">
          {issues.map((it, i) => (
            <li key={i} className="flex items-start gap-2 text-xs text-slate-300">
              <span className={`w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0 ${sevDot[it.sev]}`} />{it.label}
            </li>
          ))}
        </ul>
      )}

      {/* Projected score lift — the big, obvious promise */}
      {showLift && (
        <div className="mt-4 flex items-center justify-center gap-3 sm:gap-4 rounded-xl bg-brand-500/10 border border-brand-400/25 py-3.5 px-3 flex-wrap admin-fade-up">
          <div className="text-center">
            <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">Now</div>
            <div className="text-2xl font-extrabold text-slate-300 leading-none">{score}</div>
          </div>
          <ArrowUpRight className="w-6 h-6 text-brand-400" strokeWidth={2.5} />
          <div className="text-center">
            <div className="text-[10px] uppercase tracking-wide text-brand-300/80 font-semibold">With Premium</div>
            <AnimatedNumber to={proj} className="block text-4xl font-black text-brand-300 leading-none" />
          </div>
          <span className="px-2.5 py-1 rounded-full bg-brand-500/20 text-brand-200 text-xs font-bold whitespace-nowrap">+{proj - score} points</span>
        </div>
      )}

      {/* The proof: their own line, rewritten */}
      {(() => { const c = weakestBullet(resume); return <AITeaser kind="resume" source={c ? c.text : ""} title={(c && c.title) || (resume.personal && resume.personal.title) || ""} variant="full" />; })()}

      <div className="mt-4 flex flex-wrap gap-2">
        <button type="button" onClick={()=>ai.run({k:"improveAll"})} disabled={busy}
          className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-bold bg-brand-600 text-slate-900 hover:bg-brand-500 disabled:opacity-60 transition-colors">
          {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
          {running ? "Improving your resume…" : ai.unlocked ? "Fix all with AI" : "Fix all with Premium AI"}
        </button>
        <button type="button" onClick={()=>ai.run({k:"review"})} disabled={busy}
          className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-semibold border border-brand-400/30 bg-brand-500/10 text-brand-300 hover:bg-brand-500/20 disabled:opacity-60 transition-colors">
          {ai.busy === "review" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
          {ai.busy === "review" ? "Reviewing…" : "AI Review"}
        </button>
      </div>

      {!ai.unlocked && (
        <p className="mt-2.5 text-[11px] text-slate-500">Resume88 Premium rewrites your summary &amp; bullets in one click — a one-time {AI_PRICE_LABEL}, then download the stronger version. Your other sections stay exactly as they are.</p>
      )}
      {ai.done && <p className="mt-2.5 text-[11px] text-emerald-300 inline-flex items-center gap-1"><Check className="w-3.5 h-3.5" /> Done — your resume was rewritten. Review it above, then download.</p>}
      {ai.err && <p className="mt-2.5 text-[11px] text-amber-400">{ai.err}</p>}

      {ai.reviewText && (
        <div className="mt-4 rounded-xl border border-white/10 bg-slate-900/60 p-4">
          <div className="flex items-center gap-1.5 mb-2 text-xs font-semibold text-brand-300"><Sparkles className="w-3.5 h-3.5" /> AI Review</div>
          <div className="text-xs text-slate-300 leading-relaxed whitespace-pre-wrap">{ai.reviewText}</div>
        </div>
      )}
    </div>
  );
}

function EditorLayout({resume,setResume,update,tmplId,pal,tab,setTab,onTemplates,onExport,onBack,saveAll,showPreviewInit}) {
  const [mobPreview, setMobPreview] = useState(showPreviewInit ?? false);
  // From the full-screen preview (e.g. after "Finish & Preview"), Back returns to
  // the editing form rather than exiting to the home page.
  const handleBack = () => { if (mobPreview) setMobPreview(false); else onBack(); };
  // Single source of truth — the SAME function the Resume Health panel uses — so
  // the nav badge, the in-builder score box, and the health check always agree.
  const { score, issues } = analyzeResume(resume);
  const tips = issues.slice(0, 4).map(i => i.label);
  const scoreColor = score>=80?"text-emerald-300":score>=55?"text-amber-300":"text-red-300";
  const scoreBg = score>=80?"bg-emerald-500/10 border-emerald-400/20":score>=55?"bg-amber-500/10 border-amber-400/20":"bg-red-500/10 border-red-400/20";
  // Whether the side panel will actually render — mirrors ResumeHealth's own
  // guards so we don't reserve an empty column when it returns null.
  const pp = resume.personal || {};
  const showHealthPanel = AI_ENABLED && !!(pp.name || (pp.summary||"").trim() || (resume.experience||[]).length || (resume.skills||[]).length);

  // Flash a "Saved" confirmation whenever the resume changes (it autosaves to localStorage).
  const [savedFlash, setSavedFlash] = useState(false);
  const firstRender = useRef(true);
  useEffect(() => {
    if (firstRender.current) { firstRender.current = false; return; }
    setSavedFlash(true);
    const t = setTimeout(() => setSavedFlash(false), 1400);
    return () => clearTimeout(t);
  }, [resume]);

  return (
    <div className="min-h-screen bg-[#060a16] text-slate-200 flex flex-col">
      <div className="bg-slate-900/80 backdrop-blur-sm border-b border-white/10 px-4 py-2.5 flex items-center justify-between z-10">
        <div className="flex items-center gap-2.5">
          <button onClick={handleBack} aria-label={mobPreview?"Back to editing":"Back"} title={mobPreview?"Back to editing":"Back"} className="text-slate-400 hover:text-white px-1 transition-colors inline-flex items-center"><ArrowLeft className="w-5 h-5" /></button>
          <span className="font-bold text-white text-sm hidden sm:flex items-center gap-1.5 tracking-tight"><img src="/logo-mark.png" alt="Resume88" width="18" height="18" className="rounded" />Resume<span className="text-[#ffd000]">88</span></span>
        </div>
        <div className="flex items-center gap-2">
          <span className={`hidden sm:flex items-center gap-1 text-xs font-medium transition-colors ${savedFlash?"text-emerald-400":"text-slate-500"}`} aria-live="polite">
            {savedFlash ? <span className="inline-flex items-center gap-1"><Check className="w-3 h-3" strokeWidth={3} /> Saved</span> : "Auto-saved"}
          </span>
          <div className={`hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-semibold ${scoreBg} ${scoreColor}`}>
            <span className="opacity-70">Score</span> {score}%
          </div>
          <button onClick={()=>setMobPreview(s=>!s)} className={`lg:hidden px-3 py-1.5 rounded-xl text-xs font-medium transition-all ${mobPreview?"bg-brand-500/15 text-brand-300 border border-brand-400/25":"bg-white/5 text-slate-400 border border-white/10"}`}>
            {mobPreview?"Edit":"Preview"}
          </button>
          <button onClick={onTemplates} className="px-3 py-1.5 border border-white/10 rounded-xl text-xs font-medium text-slate-300 hover:bg-white/5 hover:border-white/20 flex items-center gap-1.5 transition-all">
            <Palette className="w-3.5 h-3.5" /><span className="hidden sm:inline">Templates</span><span className="sm:hidden">Style</span>
          </button>
          <button onClick={onExport} className="px-3.5 py-1.5 bg-brand-600 text-slate-900 rounded-xl text-xs font-semibold hover:bg-brand-500 transition-all shadow-sm shadow-brand-600/30 inline-flex items-center gap-1.5"><Download className="w-4 h-4" strokeWidth={2.25} /> Export PDF</button>
        </div>
      </div>
      <div className="flex flex-1 overflow-hidden">
        {!mobPreview && (
          <div className="hidden sm:flex flex-col w-44 bg-slate-900/40 border-r border-white/10 py-4 px-2.5 gap-0.5 flex-shrink-0">
            {TABS.map(t=>(
              <button key={t.key} onClick={()=>setTab(t.key)}
                className={`flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-xs font-medium transition-all ${t.key===tab?"bg-brand-500/15 text-brand-300":"text-slate-400 hover:bg-white/5 hover:text-slate-200"}`}>
                <t.icon className="w-4 h-4 flex-shrink-0" strokeWidth={2} />{t.label}
              </button>
            ))}
            <div className="mt-auto border-t border-white/10 pt-3">
              <button onClick={onTemplates} className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-xs font-medium text-slate-400 hover:bg-white/5 hover:text-slate-200 transition-all">
                <Palette className="w-4 h-4 flex-shrink-0" strokeWidth={2} /> Templates
              </button>
            </div>
          </div>
        )}
        {!mobPreview ? (
          <div className="flex-1 overflow-auto">
            <div className="sm:hidden flex gap-1 p-2.5 bg-slate-900/60 border-b border-white/10 overflow-x-auto">
              {TABS.map(t=>(
                <button key={t.key} onClick={()=>setTab(t.key)} className={`flex-shrink-0 px-3 py-1.5 rounded-xl text-xs font-medium transition-all ${t.key===tab?"bg-brand-600 text-slate-900 shadow-sm":"bg-white/5 text-slate-400 hover:bg-white/10"}`}>
                  <span className="inline-flex items-center gap-1.5"><t.icon className="w-3.5 h-3.5" strokeWidth={2} />{t.label}</span>
                </button>
              ))}
            </div>
            <div className="p-6 max-w-xl mx-auto sm:mx-0">
              {TIPS[tab] && <div className="mb-5 text-xs text-brand-300/90 bg-brand-500/10 border border-brand-400/20 rounded-xl px-4 py-2.5 leading-relaxed flex items-start gap-2"><Lightbulb className="w-4 h-4 flex-shrink-0 mt-px" strokeWidth={2} /><span>{TIPS[tab]}</span></div>}
              <TabContent tab={tab} resume={resume} setResume={setResume} update={update} />
              {tips.length > 0 && (
                <div className={`mt-8 border rounded-2xl p-5 ${scoreBg}`}>
                  <div className={`font-semibold text-sm mb-2.5 ${scoreColor}`}>Resume Score: {score}%</div>
                  <div className="w-full bg-white/10 rounded-full h-1.5 mb-4">
                    <div className="h-1.5 rounded-full transition-all duration-500" style={{width:score+"%",background:score>=80?"#34d399":score>=50?"#fbbf24":"#f87171"}} />
                  </div>
                  <p className="text-xs text-slate-300 font-medium mb-2">To improve your score:</p>
                  {tips.map((t,i)=><div key={i} className="text-xs text-slate-400 py-0.5 flex items-start gap-1.5"><span className="text-slate-600 mt-px">—</span>{t}</div>)}
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="flex-1 overflow-auto bg-slate-950 p-4">
            <div className="flex flex-col lg:flex-row lg:items-start lg:justify-center gap-5 max-w-[1140px] mx-auto">
              {/* Health + conversion panel — on TOP on mobile, on the SIDE on desktop.
                  Only rendered when it has something to show, so it never leaves an
                  empty column beside the resume. */}
              {showHealthPanel && (
                <div className="lg:order-2 lg:w-[380px] lg:flex-shrink-0 lg:sticky lg:top-4">
                  <ResumeHealth resume={resume} embedded />
                  {/* Mobile-only cue: the ready PDF preview is just below */}
                  <div className="lg:hidden mt-4 flex flex-col items-center text-center text-slate-400">
                    <span className="text-xs font-medium">Your ready resume preview is below</span>
                    <ArrowDown className="w-5 h-5 text-brand-400 scroll-cue mt-1" strokeWidth={2.5} />
                  </div>
                </div>
              )}
              {/* The PDF preview */}
              <div className="lg:order-1 flex-1 min-w-0 flex justify-center">
                <div className="w-full max-w-[680px] bg-white shadow-xl rounded-lg overflow-hidden">
                  <ResumeDoc tmplId={tmplId} pal={pal} resume={resume} />
                </div>
              </div>
            </div>
          </div>
        )}
        {!mobPreview && (
          <div className="hidden lg:flex flex-col w-[400px] flex-shrink-0 bg-slate-950 border-l border-white/10">
            <div className="flex items-center justify-between px-4 py-2.5 bg-slate-900/60 border-b border-white/10">
              <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Preview</span>
              <button onClick={onTemplates} className="text-xs text-slate-400 hover:text-brand-400 transition-colors">Change Template</button>
            </div>
            <div className="flex-1 overflow-auto p-3 flex justify-center">
              <div className="w-full bg-white shadow-lg rounded-lg overflow-hidden" style={{transform:"scale(0.72)",transformOrigin:"top center",width:"138%",marginLeft:"-19%"}}>
                <ResumeDoc tmplId={tmplId} pal={pal} resume={resume} />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function TabContent({tab,resume,setResume,update}) {
  if (tab==="personal") return <PersonalTab resume={resume} update={update} setResume={setResume} />;
  if (tab==="summary") return <SummaryTab resume={resume} update={update} />;
  if (tab==="experience") return <ExperienceEditor experience={resume.experience} setResume={setResume} />;
  if (tab==="education") return <EducationEditor education={resume.education} setResume={setResume} />;
  if (tab==="skills") return <SkillsEditor skills={resume.skills} setResume={setResume} title={resume.personal.title} />;
  if (tab==="languages") return <LanguagesEditor languages={resume.languages} setResume={setResume} />;
  if (tab==="extras") return <ExtrasEditor resume={resume} setResume={setResume} />;
  if (tab==="hobbies") return <HobbiesEditor hobbies={resume.hobbies} setResume={setResume} />;
  return null;
}

function SummaryTab({resume,update}) {
  return (
    <div>
      <div className="flex items-start justify-between gap-3 mb-1">
        <h2 className="editor-title">Professional Summary</h2>
        <AIButton desc={{k:"summary"}} label="Improve with AI" />
      </div>
      <p className="editor-subtitle mb-4">A brief overview of your professional background and key strengths</p>
      <textarea rows={6} placeholder="Results-driven professional with X years of experience in..."
        value={resume.personal.summary} onChange={e=>update(["personal","summary"],e.target.value)}
        className="input-field resize-none leading-relaxed" />
      <AIHint />
      <AITeaser kind="summary" source={resume.personal.summary} title={resume.personal.title} variant="compact" />
    </div>
  );
}

// Tiny inline note shown under AI-enabled fields (Premium status / upsell).
function AIHint() {
  const ai = useAI();
  if (!ai || !ai.enabled) return null;
  if (ai.err) return <p className="mt-2 text-[11px] text-amber-400">{ai.err}</p>;
  return (
    <p className="mt-2 text-[11px] text-slate-500">
      {ai.unlocked
        ? <span className="inline-flex items-center gap-1"><Sparkles className="w-3 h-3 text-brand-400" /> Premium · unlimited AI</span>
        : <span className="inline-flex items-center gap-1">
            <Sparkles className="w-3 h-3 text-brand-400" /> Powered by Resume88 Premium ·{" "}
            <button type="button" onClick={ai.openPaywall} className="text-brand-300 hover:text-brand-200 underline underline-offset-2">unlock for {AI_PRICE_LABEL}</button>
          </span>}
    </p>
  );
}

function Field({label, value, onChange, placeholder="", type="text", half=false, error=""}) {
  return (
    <div className={`mb-3.5 ${half?"flex-1":""}`}>
      <label className="field-label">{label}</label>
      <input type={type} placeholder={placeholder} value={value} onChange={onChange}
        aria-invalid={!!error} aria-label={label}
        className={`input-field ${error?"!border-red-400/60 focus:!ring-red-500/20":""}`} />
      {error && <p className="text-xs text-red-400 mt-1">{error}</p>}
    </div>
  );
}

function PersonalTab({resume,update}) {
  const fileRef = useRef();
  const email = resume.personal.email || "";
  const emailErr = email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) ? "Enter a valid email address" : "";
  const handlePhoto = (e) => {
    const file = e.target.files[0];
    if (!file || !/^image\//.test(file.type)) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      // Downscale to a small square-ish JPEG so a multi-MB upload can't blow the
      // localStorage quota (which would silently drop the whole saved resume).
      const img = new Image();
      img.onload = () => {
        const MAX = 400;
        const scale = Math.min(1, MAX / Math.max(img.width, img.height));
        const w = Math.round(img.width * scale), h = Math.round(img.height * scale);
        const canvas = document.createElement("canvas");
        canvas.width = w; canvas.height = h;
        try {
          canvas.getContext("2d").drawImage(img, 0, 0, w, h);
          update(["personal","photo"], canvas.toDataURL("image/jpeg", 0.82));
        } catch {
          update(["personal","photo"], ev.target.result); // fallback to original
        }
      };
      img.onerror = () => update(["personal","photo"], ev.target.result);
      img.src = ev.target.result;
    };
    reader.readAsDataURL(file);
  };
  return (
    <div>
      <h2 className="editor-title mb-1">Personal Information</h2>
      <p className="editor-subtitle mb-5">Basic details that appear at the top of your resume</p>
      <div className="mb-6 flex items-center gap-4 p-4 rounded-2xl border border-white/10 bg-white/5">
        <div className="w-[72px] h-[72px] rounded-full bg-slate-800 border-2 border-dashed border-white/15 flex items-center justify-center overflow-hidden flex-shrink-0 cursor-pointer hover:border-brand-400/50 hover:bg-brand-500/10 transition-all" onClick={()=>fileRef.current.click()}>
          {resume.personal.photo ? <img src={resume.personal.photo} alt="profile" className="w-full h-full object-cover" /> : <span className="text-2xl text-slate-500">+</span>}
        </div>
        <div>
          <button onClick={()=>fileRef.current.click()} className="px-3.5 py-1.5 bg-white/5 border border-white/10 text-slate-300 rounded-xl text-xs font-medium hover:border-brand-400/40 hover:text-brand-300 transition-all">Upload Photo</button>
          {resume.personal.photo && <button onClick={()=>update(["personal","photo"],"")} className="ml-2 px-3 py-1.5 bg-white/5 border border-red-400/20 text-red-400 rounded-xl text-xs hover:bg-red-500/10 hover:text-red-300 transition-all">Remove</button>}
          <p className="text-xs text-slate-500 mt-1.5">Optional — visible in some templates</p>
        </div>
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handlePhoto} />
      </div>
      <div className="flex gap-3">
        <Field label="Full Name *" value={resume.personal.name} onChange={e=>update(["personal","name"],e.target.value)} placeholder="Jane Smith" half />
        <Field label="Professional Title" value={resume.personal.title} onChange={e=>update(["personal","title"],e.target.value)} placeholder="Software Engineer" half />
      </div>
      {resume.personal.title && (
        <div className="-mt-1.5 mb-2 flex justify-end">
          <AIButton desc={{k:"title"}} label="AI: optimize my headline" />
        </div>
      )}
      <div className="flex gap-3">
        <Field label="Email *" value={resume.personal.email} onChange={e=>update(["personal","email"],e.target.value)} placeholder="jane@email.com" type="email" half error={emailErr} />
        <Field label="Phone" value={resume.personal.phone} onChange={e=>update(["personal","phone"],e.target.value)} placeholder="+1 555-000-0000" half />
      </div>
      <Field label="Location" value={resume.personal.location} onChange={e=>update(["personal","location"],e.target.value)} placeholder="San Francisco, CA" />
      <Field label="LinkedIn URL" value={resume.personal.linkedin} onChange={e=>update(["personal","linkedin"],e.target.value)} placeholder="linkedin.com/in/jane" />
      <div className="flex gap-3">
        <Field label="GitHub" value={resume.personal.github} onChange={e=>update(["personal","github"],e.target.value)} placeholder="github.com/jane" half />
        <Field label="Portfolio / Website" value={resume.personal.website} onChange={e=>update(["personal","website"],e.target.value)} placeholder="janesmith.com" half />
      </div>
      <AITeaser kind="title" source={resume.personal.title} title={resume.personal.title} variant="compact" />
    </div>
  );
}

// ─── GUIDED FLOW ──────────────────────────────────────────────────────────────
const GUIDED_STEPS = [
  {key:"personal",  label:"Personal Info",   icon:User},
  {key:"summary",   label:"Summary",         icon:FileText},
  {key:"experience",label:"Work Experience", icon:Briefcase},
  {key:"education", label:"Education",       icon:GraduationCap},
  {key:"skills",    label:"Skills",          icon:Zap},
  {key:"languages", label:"Languages",       icon:Globe},
  {key:"extras",    label:"Extras",          icon:Award},
];

function GuidedFlow({resume,setResume,step,setStep,update,onFinish,onSkip}) {
  const cur = GUIDED_STEPS[step];
  return (
    <div className="min-h-screen bg-[#060a16] text-slate-200 flex flex-col">
      <div className="bg-slate-900/80 backdrop-blur-sm border-b border-white/10 px-5 py-3 flex items-center justify-between">
        <span className="font-bold text-white tracking-tight flex items-center gap-2"><img src="/logo-mark.png" alt="Resume88" width="20" height="20" className="rounded" />Resume<span className="text-[#ffd000]">88</span></span>
        <div className="flex items-center gap-3">
          <span className="text-xs text-slate-400 font-medium">Step {step+1} of {GUIDED_STEPS.length}</span>
          <button onClick={onSkip} className="text-xs text-slate-400 hover:text-brand-400 transition-colors">Skip to Editor</button>
        </div>
      </div>
      <div className="h-1 bg-white/10"><div className="h-full bg-brand-500 transition-all duration-500" style={{width:((step)/GUIDED_STEPS.length*100)+"%"}} /></div>
      <div className="flex flex-1 overflow-hidden">
        <div className="hidden md:flex flex-col w-52 bg-slate-900/40 border-r border-white/10 p-3.5 gap-0.5">
          {GUIDED_STEPS.map((s,i)=>(
            <button key={s.key} onClick={()=>setStep(i)} className={`flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-xs font-medium transition-all ${i===step?"bg-brand-500/15 text-brand-300":i<step?"text-brand-400 hover:bg-brand-500/10":"text-slate-400 hover:bg-white/5"}`}>
              {i<step ? <Check className="w-4 h-4 flex-shrink-0" strokeWidth={2.5} /> : <s.icon className="w-4 h-4 flex-shrink-0" strokeWidth={2} />}{s.label}
            </button>
          ))}
        </div>
        <div className="flex-1 flex flex-col overflow-auto">
          <div className="flex-1 p-6 max-w-xl mx-auto w-full">
            <h2 className="text-xl font-semibold text-white mb-1.5 tracking-tight flex items-center gap-2"><cur.icon className="w-5 h-5 text-brand-400" strokeWidth={2} />{cur.label}</h2>
            {TIPS[cur.key] && <div className="mb-5 text-xs text-brand-300/90 bg-brand-500/10 border border-brand-400/20 rounded-xl px-4 py-2.5 leading-relaxed flex items-start gap-2"><Lightbulb className="w-4 h-4 flex-shrink-0 mt-px" strokeWidth={2} /><span>{TIPS[cur.key]}</span></div>}
            <TabContent tab={cur.key} resume={resume} setResume={setResume} update={update} />
          </div>
          {/* Pinned to the bottom of the scroll area so Continue/Finish is always
              reachable on mobile, even on long sections. */}
          <div className="sticky bottom-0 z-10 bg-slate-900/95 backdrop-blur-sm border-t border-white/10 px-6 py-3.5 flex justify-between">
            {step>0 ? <button onClick={()=>setStep(s=>s-1)} className="px-5 py-2 border border-white/10 rounded-xl text-sm text-slate-300 hover:bg-white/5 hover:text-white transition-all">Back</button> : <div/>}
            {step<GUIDED_STEPS.length-1
              ? <button onClick={()=>setStep(s=>s+1)} className="px-6 py-2 bg-brand-600 text-slate-900 rounded-xl font-semibold text-sm hover:bg-brand-500 transition-all shadow-sm shadow-brand-600/30">Continue</button>
              : <button onClick={()=>{ trackEvent("build"); onFinish(); }} className="px-6 py-2 bg-brand-600 text-slate-900 rounded-xl font-semibold text-sm hover:bg-brand-500 active:bg-brand-700 transition-all shadow-sm shadow-brand-600/30">Finish &amp; Preview</button>}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── RESUME DOCUMENT ──────────────────────────────────────────────────────────
function ResumeDoc({tmplId,pal,resume:r}) {
  const {accent:a,dark:dk,light:lt,muted:mu} = pal;
  const contact = [r.personal.email,r.personal.phone,r.personal.location,r.personal.linkedin,r.personal.github,r.personal.website].filter(Boolean);
  const Photo = ({size=64,ring=false}) => r.personal.photo ? (
    <img src={r.personal.photo} alt="profile" style={{width:size,height:size,borderRadius:"50%",objectFit:"cover",border:ring?`3px solid ${mu}`:"2px solid white",flexShrink:0,boxShadow:"0 1px 3px rgba(0,0,0,0.08)"}} />
  ) : null;

  const Sec = ({title,children}) => (
    <div style={{marginBottom:17}}>
      <div style={{fontSize:10,fontWeight:700,textTransform:"uppercase",letterSpacing:"1.3px",paddingBottom:5,marginBottom:10,borderBottom:`1px solid ${mu}`,color:a,lineHeight:1}}>{title}</div>
      {children}
    </div>
  );
  const ExpList = () => r.experience.map(e=>(
    <div key={e.id} style={{marginBottom:14}}>
      <div className="flex justify-between items-baseline flex-wrap gap-1">
        <span style={{fontSize:12,fontWeight:600,color:"#1a1a1a",lineHeight:1.4}}>{e.title}{e.company&&<span style={{fontWeight:400,color:"#6b7280"}}> — {e.company}</span>}</span>
        <span style={{fontSize:10,color:"#9ca3af",whiteSpace:"nowrap",letterSpacing:"0.3px"}}>{e.start}{e.end?` – ${e.end}`:e.start?" – Present":""}</span>
      </div>
      {e.location&&<div style={{fontSize:10,color:"#9ca3af",marginTop:1}}>{e.location}</div>}
      {e.bullets&&<ul style={{marginTop:4,marginLeft:12,paddingLeft:0}}>{e.bullets.split("\n").filter(Boolean).map((b,i)=><li key={i} style={{fontSize:11,color:"#4b5563",lineHeight:1.6,marginBottom:2,listStyleType:"disc"}}>{b.replace(/^[•-]\s*/,"")}</li>)}</ul>}
    </div>
  ));
  const EduList = () => r.education.map(e=>(
    <div key={e.id} style={{marginBottom:10}} className="flex justify-between items-baseline flex-wrap gap-1">
      <div><div style={{fontSize:12,fontWeight:600,color:"#1a1a1a",lineHeight:1.4}}>{e.degree}{e.field&&` in ${e.field}`}</div><div style={{fontSize:11,color:"#6b7280",marginTop:1}}>{e.school}</div></div>
      <span style={{fontSize:10,color:"#9ca3af",letterSpacing:"0.3px"}}>{e.year}</span>
    </div>
  ));
  const SkillTags = ({dark=false}) => (
    <div className="flex flex-wrap" style={{gap:4}}>
      {r.skills.map((s,i)=><span key={i} style={dark?{background:"rgba(255,255,255,.15)",color:"#fff",padding:"2px 10px",borderRadius:20,fontSize:10,lineHeight:"20px"}:{background:mu,color:dk,padding:"2px 10px",borderRadius:20,fontSize:10,lineHeight:"20px"}}>{s.name}{s.level&&<span style={{opacity:.6}}> · {s.level}</span>}</span>)}
    </div>
  );
  const LangTags = ({dark=false}) => r.languages.length>0 ? (
    <div className="flex flex-wrap" style={{gap:4}}>
      {r.languages.map((l,i)=><span key={i} style={dark?{background:"rgba(255,255,255,.15)",color:"#fff",padding:"2px 10px",borderRadius:20,fontSize:10,lineHeight:"20px"}:{background:mu,color:dk,padding:"2px 10px",borderRadius:20,fontSize:10,lineHeight:"20px"}}>{l.name} · {l.level}</span>)}
    </div>
  ) : null;
  const ProjList = () => r.projects.map(p=>(
    <div key={p.id} style={{marginBottom:10}}><div style={{fontSize:12,fontWeight:600,color:"#1a1a1a"}}>{p.name}{p.url&&<span style={{fontSize:10,fontWeight:400,marginLeft:6,color:a}}>{p.url}</span>}</div>{p.description&&<p style={{fontSize:11,color:"#6b7280",marginTop:2,lineHeight:1.5}}>{p.description}</p>}</div>
  ));
  const CertList = ({dark=false}) => r.certifications.map(c=>(
    <div key={c.id} style={{fontSize:11,marginBottom:4,...(dark?{color:"rgba(255,255,255,.85)"}:{color:"#374151"})}}><span style={{fontWeight:600}}>{c.name}</span>{c.issuer&&<span style={{fontWeight:400}}> — {c.issuer}</span>}{c.year&&<span style={{color:dark?"rgba(255,255,255,.5)":"#9ca3af"}}> ({c.year})</span>}</div>
  ));
  // No watermark on the resume — neither the on-screen preview nor the PDF ever
  // shows "Built with resume88.com" (kept as a no-op so every <Footer/> in the
  // preview templates simply renders nothing).
  const Footer = () => null;

  const name = r.personal.name||"Your Name", title=r.personal.title||"";

  const MainContent = ({skipSkills=false}) => (<>
    {r.personal.summary&&<Sec title="Summary"><p style={{fontSize:11,color:"#4b5563",lineHeight:1.7}}>{r.personal.summary}</p></Sec>}
    {r.experience.length>0&&<Sec title="Experience"><ExpList/></Sec>}
    {r.education.length>0&&<Sec title="Education"><EduList/></Sec>}
    {!skipSkills&&r.skills.length>0&&<Sec title="Skills"><SkillTags/></Sec>}
    {!skipSkills&&r.languages.length>0&&<Sec title="Languages"><LangTags/></Sec>}
    {r.projects.length>0&&<Sec title="Projects"><ProjList/></Sec>}
    {r.certifications.length>0&&!skipSkills&&<Sec title="Certifications"><CertList/></Sec>}
    {r.hobbies.length>0&&<Sec title="Interests"><p style={{fontSize:11,color:"#6b7280",letterSpacing:"0.3px"}}>{r.hobbies.join("  ·  ")}</p></Sec>}
    <Footer/>
  </>);

  /* ── Classic ── */
  if (tmplId==="classic") return (
    <div style={{padding:"28px 32px",fontSize:12,minHeight:900,fontFamily:"'Inter','Segoe UI',system-ui,-apple-system,sans-serif",lineHeight:1.5}}>
      <div style={{paddingBottom:14,marginBottom:18,borderBottom:`3px solid ${a}`}}>
        <div className="flex items-center gap-4">
          <Photo size={58} />
          <div className="flex-1">
            <div style={{fontSize:22,fontWeight:700,color:dk,lineHeight:1.2,letterSpacing:"-0.3px"}}>{name}</div>
            {title&&<div style={{fontSize:12,fontWeight:600,marginTop:3,color:a,letterSpacing:"0.2px"}}>{title}</div>}
            {contact.length>0&&<div className="flex flex-wrap gap-2 mt-1.5" style={{fontSize:10,color:"#6b7280"}}>{contact.map((c,i)=><span key={i}>{c}</span>)}</div>}
          </div>
        </div>
      </div>
      <MainContent/>
    </div>
  );

  /* ── Sidebar ── */
  if (tmplId==="sidebar") return (
    <div className="flex" style={{fontSize:12,minHeight:900,fontFamily:"'Inter','Segoe UI',system-ui,-apple-system,sans-serif",lineHeight:1.5}}>
      <div style={{width:"33%",background:a,color:"#fff",padding:"24px 18px",display:"flex",flexDirection:"column",gap:14}}>
        {r.personal.photo&&<div className="flex justify-center" style={{marginBottom:4}}><Photo size={76} ring /></div>}
        <div><div style={{fontWeight:700,fontSize:16,lineHeight:1.25}}>{name}</div>{title&&<div style={{fontSize:11,opacity:.7,marginTop:3}}>{title}</div>}</div>
        {contact.length>0&&<div style={{fontSize:10,opacity:.8,lineHeight:2,borderTop:"1px solid rgba(255,255,255,.15)",paddingTop:10}}>{contact.map((c,i)=><div key={i}>{c}</div>)}</div>}
        {r.skills.length>0&&<div style={{borderTop:"1px solid rgba(255,255,255,.15)",paddingTop:10}}><div style={{fontSize:9,textTransform:"uppercase",letterSpacing:"1.5px",opacity:.5,marginBottom:6}}>Skills</div><SkillTags dark/></div>}
        {r.languages.length>0&&<div style={{borderTop:"1px solid rgba(255,255,255,.15)",paddingTop:10}}><div style={{fontSize:9,textTransform:"uppercase",letterSpacing:"1.5px",opacity:.5,marginBottom:6}}>Languages</div><LangTags dark/></div>}
        {r.certifications.length>0&&<div style={{borderTop:"1px solid rgba(255,255,255,.15)",paddingTop:10}}><div style={{fontSize:9,textTransform:"uppercase",letterSpacing:"1.5px",opacity:.5,marginBottom:6}}>Certifications</div><CertList dark/></div>}
        {r.hobbies.length>0&&<div style={{borderTop:"1px solid rgba(255,255,255,.15)",paddingTop:10}}><div style={{fontSize:9,textTransform:"uppercase",letterSpacing:"1.5px",opacity:.5,marginBottom:6}}>Interests</div><p style={{fontSize:10,opacity:.8,lineHeight:1.6}}>{r.hobbies.join(", ")}</p></div>}
      </div>
      <div style={{flex:1,padding:"22px 24px"}}><MainContent skipSkills/></div>
    </div>
  );

  /* ── Modern ── */
  if (tmplId==="modern") return (
    <div style={{fontSize:12,minHeight:900,fontFamily:"'Inter','Segoe UI',system-ui,-apple-system,sans-serif",lineHeight:1.5}}>
      <div style={{padding:"28px 32px",color:"#fff",background:`linear-gradient(135deg,${dk},${a})`}}>
        <div className="flex items-center gap-4">
          <Photo size={66} ring />
          <div><div style={{fontSize:23,fontWeight:800,letterSpacing:"-0.5px",lineHeight:1.2}}>{name}</div>{title&&<div style={{fontSize:12,opacity:.75,marginTop:4,letterSpacing:"0.3px"}}>{title}</div>}{contact.length>0&&<div className="flex flex-wrap" style={{gap:14,marginTop:10,fontSize:10,opacity:.6}}>{contact.map((c,i)=><span key={i}>{c}</span>)}</div>}</div>
        </div>
      </div>
      <div style={{padding:"24px 32px"}}><MainContent/></div>
    </div>
  );

  /* ── Minimal ── */
  if (tmplId==="minimal") return (
    <div style={{padding:"36px 44px",fontSize:12,minHeight:900,fontFamily:"Georgia,'Times New Roman',serif",lineHeight:1.6}}>
      <div style={{textAlign:"center",marginBottom:22,paddingBottom:18,borderBottom:`2px solid ${a}`}}>
        {r.personal.photo&&<div className="flex justify-center" style={{marginBottom:10}}><Photo size={66}/></div>}
        <div style={{fontSize:26,fontWeight:300,letterSpacing:"3px",textTransform:"uppercase",color:"#1f2937",lineHeight:1.2}}>{name}</div>
        {title&&<div style={{fontSize:11,letterSpacing:"2.5px",textTransform:"uppercase",marginTop:6,color:a}}>{title}</div>}
        {contact.length>0&&<div className="flex justify-center flex-wrap" style={{gap:14,marginTop:10,fontSize:10,color:"#9ca3af"}}>{contact.map((c,i)=><span key={i}>{c}</span>)}</div>}
      </div>
      <MainContent/>
    </div>
  );

  /* ── Corporate ── */
  if (tmplId==="corporate") return (
    <div style={{fontSize:12,minHeight:900,fontFamily:"'Inter','Segoe UI',system-ui,-apple-system,sans-serif",lineHeight:1.5}}>
      <div style={{height:5,width:"100%",background:a}} />
      <div style={{padding:"18px 32px 14px",borderBottom:`2px solid ${mu}`}}>
        <div className="flex justify-between items-center flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <Photo size={54} ring />
            <div><div style={{fontSize:20,fontWeight:700,color:dk,lineHeight:1.2}}>{name}</div>{title&&<div style={{fontSize:12,fontWeight:600,marginTop:3,color:a}}>{title}</div>}</div>
          </div>
          {contact.length>0&&<div style={{textAlign:"right",fontSize:10,color:"#6b7280",lineHeight:1.9}}>{contact.map((c,i)=><div key={i}>{c}</div>)}</div>}
        </div>
      </div>
      <div style={{padding:"22px 32px"}}><MainContent/></div>
    </div>
  );

  /* ── Creative ── */
  if (tmplId==="creative") return (
    <div style={{fontSize:12,minHeight:900,fontFamily:"'Inter','Segoe UI',system-ui,-apple-system,sans-serif",lineHeight:1.5}}>
      <div style={{position:"relative",padding:"26px 32px",background:lt}}>
        <div style={{position:"absolute",left:0,top:0,width:4,height:"100%",background:a}} />
        <div className="flex items-center gap-4">
          <Photo size={62} ring />
          <div>
            <div style={{fontSize:24,fontWeight:900,letterSpacing:"-0.5px",color:dk,lineHeight:1.2}}>{name}</div>
            {title&&<span style={{display:"inline-block",marginTop:6,padding:"3px 12px",fontSize:10,fontWeight:700,color:"#fff",borderRadius:20,background:a,letterSpacing:"0.3px"}}>{title}</span>}
            {contact.length>0&&<div className="flex flex-wrap" style={{gap:8,marginTop:8,fontSize:10,color:"#6b7280"}}>{contact.map((c,i)=><span key={i}>· {c}</span>)}</div>}
          </div>
        </div>
      </div>
      <div className="flex">
        <div style={{flex:1,padding:"20px 24px"}}>
          {r.personal.summary&&<Sec title="Profile"><p style={{fontSize:11,color:"#4b5563",lineHeight:1.7}}>{r.personal.summary}</p></Sec>}
          {r.experience.length>0&&<Sec title="Experience"><ExpList/></Sec>}
          {r.education.length>0&&<Sec title="Education"><EduList/></Sec>}
          {r.projects.length>0&&<Sec title="Projects"><ProjList/></Sec>}
          {r.hobbies.length>0&&<Sec title="Interests"><p style={{fontSize:11,color:"#6b7280"}}>{r.hobbies.join("  ·  ")}</p></Sec>}
          <Footer/>
        </div>
        <div style={{width:"32%",padding:"20px 16px",borderLeft:`1.5px solid ${mu}`,background:lt+"80"}}>
          {r.skills.length>0&&<div style={{marginBottom:16}}><div style={{fontSize:10,fontWeight:700,textTransform:"uppercase",letterSpacing:"1.3px",marginBottom:8,color:a}}>Skills</div><SkillTags/></div>}
          {r.languages.length>0&&<div style={{marginBottom:16}}><div style={{fontSize:10,fontWeight:700,textTransform:"uppercase",letterSpacing:"1.3px",marginBottom:8,color:a}}>Languages</div><LangTags/></div>}
          {r.certifications.length>0&&<div><div style={{fontSize:10,fontWeight:700,textTransform:"uppercase",letterSpacing:"1.3px",marginBottom:8,color:a}}>Certifications</div><CertList/></div>}
        </div>
      </div>
    </div>
  );
  return null;
}

// ─── SUB EDITORS ──────────────────────────────────────────────────────────────
function ExperienceEditor({experience,setResume}) {
  const add = () => setResume(r=>({...r,experience:[...r.experience,{id:uid(),title:"",company:"",location:"",start:"",end:"",bullets:""}]}));
  const upd = (id,f,v) => setResume(r=>({...r,experience:r.experience.map(e=>e.id===id?{...e,[f]:v}:e)}));
  const del = (id) => setResume(r=>({...r,experience:r.experience.filter(e=>e.id!==id)}));
  return (
    <div>
      <h2 className="editor-title mb-1">Work Experience</h2>
      <p className="editor-subtitle mb-5">Add your relevant work history, most recent first</p>
      {experience.map((e,idx)=>(
        <div key={e.id} className="section-card mb-4">
          <div className="flex justify-between items-center mb-4">
            <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Position {idx+1}</span>
            <button onClick={()=>del(e.id)} className="text-slate-500 hover:text-red-400 text-xs transition-colors">Remove</button>
          </div>
          {[["Job Title","title","Software Engineer"],["Company","company","Acme Corp"],["Location","location","San Francisco, CA"]].map(([l,f,ph])=>(
            <div key={f} className="mb-3"><label className="field-label">{l}</label><input value={e[f]} placeholder={ph} onChange={ev=>upd(e.id,f,ev.target.value)} className="input-field-sm" /></div>
          ))}
          <div className="flex gap-3 mb-3">
            {[["Start Date","start","Jan 2022"],["End Date","end","Dec 2024 or leave blank"]].map(([l,f,ph])=>(
              <div key={f} className="flex-1"><label className="field-label">{l}</label><input value={e[f]} placeholder={ph} onChange={ev=>upd(e.id,f,ev.target.value)} className="input-field-sm" /></div>
            ))}
          </div>
          <div className="flex items-center justify-between gap-3 mb-1">
            <label className="field-label !mb-0">Key Responsibilities & Achievements</label>
            <AIButton desc={{k:"bullets",id:e.id}} label={(e.bullets||"").trim()?"Improve bullets":"Write bullets with AI"} />
          </div>
          <textarea rows={4} value={e.bullets} placeholder={"Led a team of 5 engineers\nIncreased performance by 40%\nOne achievement per line"} onChange={ev=>upd(e.id,"bullets",ev.target.value)} className="input-field-sm resize-none leading-relaxed" />
        </div>
      ))}
      <button onClick={add} className="add-btn-dashed">+ Add Work Experience</button>
      {(() => {
        const fb = experience.flatMap(e => (e.bullets || "").split("\n")).map(s => s.trim()).find(Boolean) || "";
        const ft = (experience.find(e => (e.bullets || "").trim()) || {}).title || "";
        return <AITeaser kind="bullets" source={fb} title={ft} variant="compact" />;
      })()}
    </div>
  );
}

function EducationEditor({education,setResume}) {
  const add = () => setResume(r=>({...r,education:[...r.education,{id:uid(),degree:"",field:"",school:"",year:""}]}));
  const upd = (id,f,v) => setResume(r=>({...r,education:r.education.map(e=>e.id===id?{...e,[f]:v}:e)}));
  const del = (id) => setResume(r=>({...r,education:r.education.filter(e=>e.id!==id)}));
  return (
    <div>
      <h2 className="editor-title mb-1">Education</h2>
      <p className="editor-subtitle mb-5">Add your degrees and academic credentials</p>
      {education.map((e,idx)=>(
        <div key={e.id} className="section-card mb-4">
          <div className="flex justify-between items-center mb-4">
            <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Degree {idx+1}</span>
            <button onClick={()=>del(e.id)} className="text-slate-500 hover:text-red-400 text-xs transition-colors">Remove</button>
          </div>
          {[["Degree","degree","Bachelor of Science"],["Field of Study","field","Computer Science"],["School / University","school","Stanford University"],["Year","year","2020"]].map(([l,f,ph])=>(
            <div key={f} className="mb-3"><label className="field-label">{l}</label><input value={e[f]} placeholder={ph} onChange={ev=>upd(e.id,f,ev.target.value)} className="input-field-sm" /></div>
          ))}
        </div>
      ))}
      <button onClick={add} className="add-btn-dashed">+ Add Education</button>
    </div>
  );
}

// Curated job-family → skills map. Powers the FREE "Suggest skills" teaser (no API
// cost), which seeds value and nudges users toward the Premium AI-tailored version.
const SKILL_LIBRARY = [
  { k: /(software|developer|engineer|programmer|full.?stack|backend|front.?end|web dev)/i, s: ["JavaScript","TypeScript","React","Node.js","Python","SQL","Git","REST APIs","AWS","Docker","CI/CD","Agile"] },
  { k: /(data scien|data analyst|analytics|machine learning|\bml\b|ai engineer)/i, s: ["Python","SQL","Pandas","NumPy","Tableau","Power BI","Machine Learning","Statistics","Data Visualization","A/B Testing","Excel"] },
  { k: /(product manager|product owner|\bpm\b)/i, s: ["Product Roadmapping","Agile / Scrum","User Research","A/B Testing","Stakeholder Management","Jira","Analytics","Wireframing","Go-to-Market","Prioritization"] },
  { k: /(ux|ui|product design|graphic design|designer)/i, s: ["Figma","Adobe XD","Prototyping","User Research","Wireframing","Design Systems","Usability Testing","Accessibility","Typography","Sketch"] },
  { k: /(marketing|seo|content|brand|growth|social media)/i, s: ["SEO","Content Marketing","Google Analytics","Social Media","Email Marketing","PPC","Copywriting","HubSpot","A/B Testing","Brand Strategy"] },
  { k: /(sales|account executive|business development|\bbdr\b|\bsdr\b)/i, s: ["Salesforce CRM","Lead Generation","Negotiation","Pipeline Management","Account Management","B2B Sales","Cold Outreach","Forecasting","Closing"] },
  { k: /(account|finance|financial|bookkeep|audit)/i, s: ["Excel","QuickBooks","Financial Reporting","GAAP","Budgeting","Forecasting","Accounts Payable","Reconciliation","SAP","Variance Analysis"] },
  { k: /(nurse|\brn\b|healthcare|medical|clinical|caregiver)/i, s: ["Patient Care","EMR / EHR","Vital Signs","Medication Administration","HIPAA","Triage","CPR / BLS","Care Planning","Patient Education"] },
  { k: /(teacher|educat|tutor|instructor|professor)/i, s: ["Lesson Planning","Classroom Management","Curriculum Development","Differentiated Instruction","Assessment","Google Classroom","Student Engagement"] },
  { k: /(project manager|program manager|scrum master)/i, s: ["Agile","Scrum","Risk Management","Budgeting","Stakeholder Management","MS Project","Jira","Resource Planning","Gantt Charts"] },
  { k: /(customer (service|support|success)|help desk|call center)/i, s: ["CRM","Zendesk","Conflict Resolution","Troubleshooting","Ticketing Systems","Active Listening","Multitasking","Product Knowledge"] },
  { k: /(human resources|\bhr\b|recruit|talent)/i, s: ["Recruiting","Onboarding","Applicant Tracking (ATS)","Employee Relations","HRIS","Talent Acquisition","Performance Management","Compliance"] },
  { k: /(operations|logistics|supply chain|warehouse|admin)/i, s: ["Process Improvement","Scheduling","MS Office","Vendor Management","Inventory Management","Logistics","Data Entry","Documentation"] },
  { k: /(writer|editor|journalist|copywriter)/i, s: ["Copywriting","SEO","Editing","Content Strategy","WordPress","Research","Proofreading","Storytelling"] },
];
const GENERIC_SKILLS = ["Communication","Leadership","Problem Solving","Time Management","Teamwork","Microsoft Office","Project Management","Adaptability","Critical Thinking"];
function suggestSkillsFor(title) {
  const t = (title || "").trim();
  const hit = t ? SKILL_LIBRARY.find(e => e.k.test(t)) : null;
  return (hit ? hit.s : GENERIC_SKILLS).slice(0, 12);
}

function SkillsEditor({skills,setResume,title}) {
  const ai = useAI();
  const [suggested,setSuggested] = useState([]);
  const have = new Set((skills||[]).map(s=>s.name.toLowerCase()));
  // Suggested/auto-added skills carry NO proficiency level (only manually-typed
  // skills get a level, chosen in the dropdown). Empty level renders cleanly.
  const addNames = (names,level="") => setResume(r=>{
    const existing = new Set(r.skills.map(s=>s.name.toLowerCase()));
    const clean = names
      .map(n=>n.replace(/^[\s\-•*\d.)]+/,"").replace(/\.+$/,"").trim()) // strip bullets/numbering AI may add
      .filter(n=>n && n.length<=40);
    const fresh = clean.filter(n=>!existing.has(n.toLowerCase())).map(name=>({name,level}));
    return {...r, skills:[...r.skills, ...fresh]};
  });
  const runSuggest = () => setSuggested(suggestSkillsFor(title).filter(s=>!have.has(s.toLowerCase())));
  const [input,setInput] = useState("");
  const [level,setLevel] = useState("Intermediate");
  const add = () => {
    const t = input.trim();
    if (!t) return;
    const items = t.split(",").map(s=>s.trim()).filter(Boolean).map(name=>({name,level}));
    setResume(r=>{
      const existing = new Set(r.skills.map(s=>s.name.toLowerCase()));
      const newOnes = items.filter(i=>!existing.has(i.name.toLowerCase()));
      return {...r, skills:[...r.skills,...newOnes]};
    });
    setInput("");
  };
  const remove = (name) => setResume(r=>({...r,skills:r.skills.filter(s=>s.name!==name)}));
  return (
    <div>
      <h2 className="editor-title mb-1">Skills</h2>
      <p className="editor-subtitle mb-4">Add skills with an optional proficiency level</p>
      <div className="flex gap-2 mb-2">
        <input aria-label="Skill name" value={input} onChange={e=>setInput(e.target.value)} onKeyDown={e=>e.key==="Enter"&&add()} placeholder="e.g. React, Python, Figma" className="input-field flex-1" />
        <select aria-label="Proficiency level" value={level} onChange={e=>setLevel(e.target.value)} className="select-dark">
          {SKILL_LEVELS.map(l=><option key={l}>{l}</option>)}
        </select>
        <button onClick={add} className="btn-primary whitespace-nowrap">Add</button>
      </div>

      {/* Suggestions: free curated teaser + Premium AI-tailored */}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <button type="button" onClick={runSuggest}
          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold border border-white/10 bg-white/[0.03] text-slate-300 hover:border-brand-400/40 hover:text-brand-200 transition-colors">
          <Lightbulb className="w-3.5 h-3.5" /> Suggest skills for my role
        </button>
        <AIButton desc={{k:"skills"}} label="AI: tailor to my experience" />
      </div>

      {suggested.length > 0 && (
        <div className="mb-4 rounded-xl border border-white/10 bg-white/[0.02] p-3">
          <div className="flex items-center justify-between mb-2">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Suggested for {title?`a ${title}`:"you"}</p>
            <button onClick={()=>{ addNames(suggested); setSuggested([]); }} className="text-[11px] font-semibold text-brand-300 hover:text-brand-200">+ Add all</button>
          </div>
          <div className="chip-tray">
            {suggested.map(s=>(
              <button key={s} onClick={()=>{ addNames([s]); setSuggested(p=>p.filter(x=>x!==s)); }}
                className="tag-pill bg-white/5 text-slate-300 border-white/10 hover:border-brand-400/40 hover:text-brand-200 transition-colors">+ {s}</button>
            ))}
          </div>
          {!ai?.unlocked && ai?.enabled && (
            <p className="mt-2 text-[11px] text-slate-500">Want skills tailored to your exact role &amp; seniority? <button onClick={ai.openPaywall} className="text-brand-300 hover:text-brand-200 underline underline-offset-2">Unlock Premium</button>.</p>
          )}
        </div>
      )}

      {skills.length > 0 && (
        <div className="chip-tray">
          {skills.map(s=>(
            <span key={s.name} className="tag-pill bg-brand-500/15 text-brand-300 border-brand-400/20">
              {s.name}{s.level&&<span className="opacity-50 font-normal"> · {s.level}</span>}
              <button onClick={()=>remove(s.name)} className="text-brand-300 hover:text-red-400 ml-0.5 transition-colors">×</button>
            </span>
          ))}
        </div>
      )}
      <AITeaser kind="skills" title={title} variant="compact" />
    </div>
  );
}

function LanguagesEditor({languages,setResume}) {
  const [name,setName] = useState("");
  const [level,setLevel] = useState("Professional");
  const add = () => {
    if (!name.trim()) return;
    setResume(r=>({...r,languages:[...r.languages,{name:name.trim(),level}]}));
    setName("");
  };
  return (
    <div>
      <h2 className="editor-title mb-1">Languages</h2>
      <p className="editor-subtitle mb-4">Add languages you can use in a work setting</p>
      <div className="flex gap-2 mb-2">
        <input aria-label="Language" list="lang-options" value={name} onChange={e=>setName(e.target.value)} onKeyDown={e=>e.key==="Enter"&&add()} placeholder="Type or pick a language" className="input-field flex-1" />
        <datalist id="lang-options">{LANGUAGES.map(l=><option key={l} value={l} />)}</datalist>
        <select aria-label="Proficiency level" value={level} onChange={e=>setLevel(e.target.value)} className="select-dark">
          {LANG_LEVELS.map(l=><option key={l}>{l}</option>)}
        </select>
        <button onClick={add} className="btn-primary whitespace-nowrap">Add</button>
      </div>
      {languages.length > 0 && (
        <div className="chip-tray">
          {languages.map((l,i)=>(
            <span key={i} className="tag-pill bg-indigo-500/15 text-indigo-300 border-indigo-400/20">
              {l.name} <span className="opacity-50 font-normal">· {l.level}</span>
              <button onClick={()=>setResume(r=>({...r,languages:r.languages.filter((_,j)=>j!==i)}))} className="text-indigo-300 hover:text-red-400 ml-0.5 transition-colors">×</button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function HobbiesEditor({hobbies,setResume}) {
  const [input,setInput] = useState("");
  const add = () => {
    const items = input.split(",").map(s=>s.trim()).filter(Boolean);
    setResume(r=>({...r,hobbies:[...new Set([...r.hobbies,...items])]}));
    setInput("");
  };
  return (
    <div>
      <h2 className="editor-title mb-1">Hobbies & Interests</h2>
      <p className="editor-subtitle mb-4">Optional — only include if relevant or if you have space</p>
      <div className="flex gap-2 mb-2">
        <input aria-label="Interest or hobby" value={input} onChange={e=>setInput(e.target.value)} onKeyDown={e=>e.key==="Enter"&&add()} placeholder="e.g. Open Source, Chess, Photography" className="input-field flex-1" />
        <button onClick={add} className="btn-primary whitespace-nowrap">Add</button>
      </div>
      {hobbies.length > 0 && (
        <div className="chip-tray">
          {hobbies.map((h,i)=>(
            <span key={i} className="tag-pill bg-orange-500/15 text-orange-300 border-orange-400/20">
              {h}<button onClick={()=>setResume(r=>({...r,hobbies:r.hobbies.filter((_,j)=>j!==i)}))} className="text-orange-300 hover:text-red-400 ml-0.5 transition-colors">×</button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function ExtrasEditor({resume,setResume}) {
  const addP = () => setResume(r=>({...r,projects:[...r.projects,{id:uid(),name:"",url:"",description:""}]}));
  const updP = (id,f,v) => setResume(r=>({...r,projects:r.projects.map(p=>p.id===id?{...p,[f]:v}:p)}));
  const delP = (id) => setResume(r=>({...r,projects:r.projects.filter(p=>p.id!==id)}));
  const addC = () => setResume(r=>({...r,certifications:[...r.certifications,{id:uid(),name:"",issuer:"",year:""}]}));
  const updC = (id,f,v) => setResume(r=>({...r,certifications:r.certifications.map(c=>c.id===id?{...c,[f]:v}:c)}));
  const delC = (id) => setResume(r=>({...r,certifications:r.certifications.filter(c=>c.id!==id)}));
  return (
    <div>
      <h2 className="editor-title mb-1">Projects & Certifications</h2>
      <p className="editor-subtitle mb-5">Showcase your work and professional credentials</p>
      <div className="mb-6">
        <h3 className="text-sm font-semibold text-slate-200 mb-3">Projects</h3>
        {resume.projects.map((p,i)=>(
          <div key={p.id} className="section-card mb-3">
            <div className="flex justify-between items-center mb-3"><span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Project {i+1}</span><button onClick={()=>delP(p.id)} className="text-slate-500 hover:text-red-400 text-xs transition-colors">Remove</button></div>
            {[["Name","name","My App"],["URL","url","github.com/user/app"]].map(([l,f,ph])=>(
              <div key={f} className="mb-3"><label className="field-label">{l}</label><input value={p[f]} placeholder={ph} onChange={e=>updP(p.id,f,e.target.value)} className="input-field-sm" /></div>
            ))}
            <label className="field-label">Description</label>
            <textarea rows={2} value={p.description} onChange={e=>updP(p.id,"description",e.target.value)} className="input-field-sm resize-none" />
          </div>
        ))}
        <button onClick={addP} className="add-btn-dashed">+ Add Project</button>
      </div>
      <div>
        <h3 className="text-sm font-semibold text-slate-200 mb-3">Certifications</h3>
        {resume.certifications.map((c,i)=>(
          <div key={c.id} className="section-card mb-3">
            <div className="flex justify-between items-center mb-3"><span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Cert {i+1}</span><button onClick={()=>delC(c.id)} className="text-slate-500 hover:text-red-400 text-xs transition-colors">Remove</button></div>
            <div className="grid grid-cols-2 gap-3">
              {[["Name","name","AWS Solutions Architect"],["Issuer","issuer","Amazon"],["Year","year","2024"]].map(([l,f,ph])=>(
                <div key={f} className={f==="name"?"col-span-2":""}>
                  <label className="field-label">{l}</label>
                  <input value={c[f]} placeholder={ph} onChange={e=>updC(c.id,f,e.target.value)} className="input-field-sm" />
                </div>
              ))}
            </div>
          </div>
        ))}
        <button onClick={addC} className="add-btn-dashed">+ Add Certification</button>
      </div>
    </div>
  );
}